// @atlas/contracts — a ROUTE-térkép (`atlasContract`) és a csomag publikus felülete
//
// A SÉMÁK per-feature fájlokban élnek (`src/<feature>/schemas.ts`), a kereszt-vágó építőkövek a
// `src/_shared/schemas.ts`-ben; ez a fájl a route-okat tartja. MIÉRT ÍGY, és miért NEM a route-ok is
// szét: a sémák sima ÉRTÉKEK (Zod-objektumok), a mozgatásuk a ts-rest típus-inferenciáját nem érinti —
// a route-ok viszont EGY `c.router({...})` objektum-literálban élnek, és a fájl korábbi fejléce szerint
// ez a literál a ts-rest kontextus-inferencia HATÁRÁN van: a lapos bővítés egyszer már elrontotta az
// ÖSSZES handler tipizálását (hamis TS2345), és épp ezért lett a ticket-csatolmány / komment-sablon /
// üzenet-melléklet sub-router. Ezért a bontás tudatosan csak a séma-felet vitte el.
//
// A csomag PUBLIKUS felülete változatlan: minden korábbi export továbbra is a `@atlas/contracts`
// gyökérből jön (lásd a `export * from './<feature>/schemas.js'` blokkot lentebb).
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
import { ActivityEventSchema, EditorImageCommitBodySchema, EditorImageCommitSchema, EditorImageResolveSchema, EditorImageUploadBodySchema, EditorImageUploadUrlSchema, ErrorSchema, ListQuerySchema, OdinResourceSchema, PersonOptionSchema, ValidationErrorSchema, } from './_shared/schemas.js';
import { AddMemberBodySchema, AdminAppQuerySchema, AdminAppSchema, AdminIdentitiesQuerySchema, AdminIdentitySchema, AdminMemberSchema, AdminPermissionSchema, AdminRoleSchema, AdminScopeQuerySchema, CreateRoleBodySchema, } from './access-rights/schemas.js';
import { AccountAffiliationUpsertBodySchema, AccountCreateBodySchema, AccountCreateResponseSchema, AccountDetailSchema, AccountListQuerySchema, AccountRowSchema, AccountUpdateBodySchema, } from './accounts/schemas.js';
import { ActivityLogQuerySchema, ExportAuditBodySchema } from './activity-log/schemas.js';
import { AssetDecideBodySchema, AssetListQuerySchema, AssetProgressBodySchema, AssetRefsSchema, AssetCatalogSchema, AssetCategoryRowSchema, AssetCategoryWriteBodySchema, AssetRequestCreateBodySchema, AssetRequestUpdateBodySchema, AssetRowSchema, AssetTypeRowSchema, AssetTypeWriteBodySchema, AssetWriteBodySchema, } from './assets/schemas.js';
import { ForgotPasswordBodySchema, ForgotPasswordResponseSchema, LoginBodySchema, LoginErrorSchema, ResetPasswordBodySchema, ImpersonationStartBodySchema, ImpersonationStartResponseSchema, ImpersonationStopResponseSchema, SessionSchema, } from './auth/schemas.js';
import { BillingProfileClientRefsSchema, BillingProfileCreateBodySchema, BillingProfileListQuerySchema, BillingProfileRowSchema, BillingProfileWriteBodySchema, } from './billing-profiles/schemas.js';
import { CampaignCreateBodySchema, CampaignCreateResponseSchema, CampaignDetailSchema, CampaignListQuerySchema, CampaignListResponseSchema, CampaignRefsSchema, CampaignUpdateBodySchema, } from './campaign-forecast/schemas.js';
import { ClientCreateBodySchema, ClientDetailSchema, ClientIdempotencyHeaders, ClientIfMatchHeaders, ClientListQuerySchema, ClientListResponseSchema, ClientUpdateBodySchema, } from './clients/schemas.js';
import { ContactCreateBodySchema, ContactListQuerySchema, ContactRowSchema, ContactUpdateBodySchema, } from './contacts/schemas.js';
import { CurrencyRateDeleteParamsSchema, CurrencyRateListQuerySchema, CurrencyRateRowSchema, CurrencyRateUpsertBodySchema, } from './currency-rates/schemas.js';
import { CelebrationsQuerySchema, CelebrationsResponseSchema, DashboardSchema, } from './dashboard/schemas.js';
import { EmployeeCreateBodySchema, EmployeeRefsSchema, EmployeeRowSchema, EmployeeUpdateBodySchema, } from './employees/schemas.js';
import { ExpenseCategoryRowSchema, ExpenseBulkClassifyBodySchema, ExpenseBulkClassifyResultSchema, ExpenseCategoryWriteBodySchema, ExpenseAmountBodySchemaChecked, ExpenseClassifyBodySchema, ExpenseDismissBodySchema, ExpenseImportBatchSchema, ExpenseImportCommitBodySchema, ExpenseImportPreviewBodySchema, ExpenseImportPreviewSchema, ExpenseImportResultSchema, ExpenseImportTemplateSchema, ExpenseListQuerySchema, ExpenseRowSchema, ExpenseSummarySchema, ExpenseAnalyticsQuerySchema, ExpenseAnalyticsSchema, ExpenseWriteBodySchemaChecked, NavImportReportSchema, NavStatusSchema, NavSyncBodySchema, } from './expenses/schemas.js';
import { GlobalSearchQuerySchema, GlobalSearchResponseSchema } from './global-search/schemas.js';
import { IdentitySchema } from './identities/schemas.js';
import { InvoiceBulkIssueBodySchema, InvoiceBulkIssueResultSchema, InvoiceCreateBodySchema, InvoiceDetailSchema, InvoiceDraftBodySchema, InvoiceGenerateBodySchema, InvoiceGenerateResultSchema, InvoiceGenerateStartedSchema, InvoiceGenerationSkipListQuerySchema, InvoiceGenerationSkipListSchema, InvoiceIssueBodySchema, InvoiceLifecycleResultSchema, InvoiceListQuerySchema, InvoicePdfQuerySchema, InvoicePdfSchema, InvoiceRefsQuerySchema, InvoiceRefsSchema, InvoiceRowSchema, InvoiceStornoBodySchema, InvoiceSyncResultSchema, } from './invoices/schemas.js';
import { MeetingEmployeeGroupSchema, MeetingListQuerySchema, MeetingParticipantConflictQuerySchema, MeetingParticipantConflictSchema, MeetingRowSchema, MeetingRsvpBodySchema, MeetingSeriesScopeQuerySchema, MeetingWriteBodySchema, } from './meetings/schemas.js';
import { MessageAttachmentCommitBodySchema, MessageAttachmentRowSchema, MessageAttachmentUploadUrlBodySchema, MessageAttachmentUploadUrlResponseSchema, MessageComposeBodySchema, MessageContentSchema, MessageDetailSchema, MessageListQuerySchema, MessageRefsSchema, MessageRowSchema, MessageScheduleBodySchema, } from './messages/schemas.js';
import { NotificationListQuerySchema, NotificationListResponseSchema, } from './notifications/schemas.js';
import { DepartmentWriteBodySchema, OrgDepartmentRowSchema, OrgListQuerySchema, OrgPositionRowSchema, OrgTeamRowSchema, PositionWriteBodySchema, TeamWriteBodySchema, } from './org-structure/schemas.js';
import { PaymentCardClientRefsSchema, PaymentCardCreateBodySchema, PaymentCardListQuerySchema, PaymentCardRowSchema, PaymentCardWriteBodySchema, } from './payment-cards/schemas.js';
import { PortalInvoiceListQuerySchema, PortalMeetingRsvpBodySchema, PortalMessageDetailSchema, PortalMessagesResponseSchema, PortalInvoiceListResponseSchema, PortalMeResponseSchema, PortalServicesResponseSchema, PortalInvoiceDetailSchema, PortalBillingQuerySchema, PortalBillingResponseSchema, PortalEditorImageCommitBodySchema, PortalEditorImageCommitSchema, PortalEditorImageUploadBodySchema, PortalTicketAccessBodySchema, PortalTicketAttachmentSchema, PortalTicketCommentEditedSchema, PortalTicketImageSchema, PortalTicketCreateBodySchema, PortalTicketDetailSchema, PortalTicketFormOptionsResponseSchema, PortalTicketListQuerySchema, PortalTicketListResponseSchema, PortalTicketReplyBodySchema, } from './portal/schemas.js';
import { PortalAccessListResponseSchema, PortalAccessOverviewResponseSchema, PortalAccessRowSchema, PortalAccessSetBodySchema, } from './portal-access/schemas.js';
import { DashboardLayoutEnvelopeSchema, DashboardLayoutSchema, NotesPrefEnvelopeSchema, NotesPrefSchema, PreferenceIfMatchHeaders, } from './preferences/schemas.js';
import { PortalProductUpdateListResponseSchema, ProductUpdateDetailSchema, ProductUpdateListQuerySchema, ProductUpdateListResponseSchema, ProductUpdatePublishBodySchema, ProductUpdateWriteBodySchema, } from './product-updates/schemas.js';
import { AsanaPatBodySchema, AsanaPatStateSchema, AvatarCommitSchema, AvatarUploadUrlBodySchema, AvatarUploadUrlSchema, PasswordChangeBodySchema, PasswordPolicyErrorSchema, ProfilePersonalSchema, ProfilePersonalUpdateBodySchema, ProfileSchema, ProfileUpdateBodySchema, RecoveryCodesSchema, TwoFactorEnableBodySchema, TwoFactorSetupSchema, TwoFactorStepUpBodySchema, } from './profile/schemas.js';
import { RevenueForecastQuerySchema, RevenueForecastResponseSchema, RevenueRowsQuerySchema, RevenueRowsResponseSchema, } from './revenue-forecast/schemas.js';
import { ServiceAssignmentActiveCheckQuerySchema, ServiceAssignmentActiveCheckSchema, ServiceAssignmentAttentionSchema, ServiceAssignmentCloseBodySchema, ServiceAssignmentCreateBodySchema, ServiceAssignmentDetailSchema, ServiceAssignmentInvoiceGroupsQuerySchema, ServiceAssignmentInvoiceGroupsSchema, ServiceAssignmentListQuerySchema, ServiceAssignmentRefsSchema, ServiceAssignmentRowSchema, ServiceAssignmentUpdateBodySchema, ServiceCatalogDetailSchema, ServiceCatalogListQuerySchema, ServiceCatalogRowSchema, ServiceCatalogWriteSchema, ServiceUsageFetchBodySchema, ServiceUsageFetchResultSchema, ServiceUsageListQuerySchema, ServiceUsageRowSchema, } from './services/schemas.js';
import { SupportTeamRowSchema, SupportTeamWriteBodySchema } from './support-teams/schemas.js';
import { TicketAsanaTaskSchema, TicketAttachmentCommitBodySchema, AttachmentDownloadUrlSchema, TicketAttachmentRowSchema, TicketAttachmentUploadUrlBodySchema, TicketAttachmentUploadUrlResponseSchema, TicketCommentCreateBodySchema, TicketCommentRowSchema, TicketCommentTemplateCreateBodySchema, TicketCommentTemplateRowSchema, TicketCommentReactionSchema, TicketCommentUpdateBodySchema, TicketReactionToggleBodySchema, TicketCreateBodySchema, TicketDetailSchema, TicketFormOptionsSchema, TicketListItemSchema, TicketListQuerySchema, TicketRefsSchema, TicketUpdateBodySchema, } from './tickets/schemas.js';
// A sémák per-feature fájlokban élnek (lásd fent); a csomag PUBLIKUS felülete változatlan:
// minden korábbi export továbbra is a `@atlas/contracts` gyökérből jön.
export * from './_shared/schemas.js';
export * from './access-rights/schemas.js';
export * from './accounts/schemas.js';
export * from './activity-log/schemas.js';
export * from './assets/schemas.js';
export * from './auth/schemas.js';
export * from './billing-profiles/schemas.js';
export * from './clients/schemas.js';
export * from './contacts/schemas.js';
export * from './currency-rates/schemas.js';
export * from './dashboard/schemas.js';
export * from './preferences/schemas.js';
export * from './employees/schemas.js';
export * from './global-search/schemas.js';
export * from './identities/schemas.js';
export * from './invoices/schemas.js';
export * from './meetings/schemas.js';
export * from './messages/schemas.js';
export * from './notifications/schemas.js';
export * from './org-structure/schemas.js';
export * from './payment-cards/schemas.js';
export * from './portal-access/schemas.js';
export * from './portal/schemas.js';
export * from './profile/schemas.js';
export * from './revenue-forecast/schemas.js';
export * from './services/schemas.js';
export * from './support-teams/schemas.js';
export * from './product-updates/schemas.js';
export * from './campaign-forecast/schemas.js';
export * from './expenses/schemas.js';
export * from './tickets/schemas.js';
// ── Clients (Ügyfelek) modul — sémák + ts-rest sub-router (inline; a contract source-only ESM-csomag, a
//    relatív al-import futásidőben nem oldódna fel — a modulok kulcsai ezért itt, egy fájlban élnek). ──
//
// A `clients/client` profil a CRM ügyfél forrás-igazsága (name/email/phone/statusOriginal/autoBilling); a
// platform-mag `clients` a tenant-registry (id + EntityStatus + createdAt). A `status` az EREDETI CRM-státusz
// (active/pending/inactive) — adat-parity, NEM a platform-EntityStatus.
/** Clients ts-rest sub-router — a root contract `clients` kulcsa ezt ágyazza be. */
const clientsContract = c.router({
    list: {
        method: 'GET',
        path: '/clients',
        query: ClientListQuerySchema,
        // 502: átmeneti upstream-hiba (Odin 429/5xx) — a felület „Újra"-t ajánl, NEM jog-hibát mutat.
        responses: {
            200: ClientListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Ügyféllista a CRM adat-parity mezőivel (platform-mag + clients/client profil-merge a BFF-ben) — név/email keresés, státusz-szűrő, név szerint rendezve, offset-lapozás (végtelen görgetés).',
    },
    get: {
        method: 'GET',
        path: '/clients/:id',
        responses: {
            200: ClientDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            // ÁTMENETI upstream-zavar (fojtás / 5xx) — NEM „nincs ilyen ügyfél". A 404-re képezve a felület egy
            // LÉTEZŐ ügyfélre mondta volna, hogy nem található (a repo olvasás-konvenciója: 401/403 → 403, minden
            // más nem-2xx → 502).
            502: ErrorSchema,
        },
        summary: 'Egy ügyfél detail-nézete: a core-mezők + OCC-verzió + a fiók-profilok száma.',
    },
    create: {
        method: 'POST',
        path: '/clients',
        headers: ClientIdempotencyHeaders,
        body: ClientCreateBodySchema,
        responses: {
            201: ClientDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Ügyfél létrehozása: Odin foundation tenant-root (POST /v1/admin/clients) + clients/client profil — Idempotency-Key.',
    },
    update: {
        method: 'PATCH',
        path: '/clients/:id',
        headers: ClientIfMatchHeaders,
        body: ClientUpdateBodySchema,
        responses: {
            200: ClientDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            // ÁTMENETI upstream-zavar: az ÍRÁS sikerülhetett, csak a visszaolvasás bukott — a 404 azt üzenné,
            // hogy az ügyfél eltűnt, a 422 pedig azt, hogy a bemenet hibás. Egyik sem igaz.
            503: ErrorSchema,
        },
        summary: 'Ügyfél frissítése (If-Match/OCC, kötelező fejléc): CSAK a profil PUT (a CRM-státusz a profilé); státusz-kaszkád pending/inactive → a fiók-profilokra (felfelé sosem); `inactive`-ra a nyitott szolgáltatás-hozzárendelések a mai nappal lezárulnak (best-effort — a státusz-mentést nem buktatja).',
    },
    remove: {
        method: 'DELETE',
        path: '/clients/:id',
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            // ÁTMENETI upstream-zavar (a platform fojtása vagy 5xx) — a kaszkád fail-closed, de a teendő MÁS:
            // nincs mit javítani a bemeneten, újra kell próbálni. 422-ként kiadva a felület „ellenőrizd a
            // mezőket" üzenetet adott egy törlésre, ahol mező sincs.
            503: ErrorSchema,
        },
        summary: 'Ügyfél soft-delete (fail-closed kaszkád): előbb a tenant élő portál-grantjainak visszavonása, majd a gyerekek (fiók-profilok, fiók-kötések, fiók-domének, billing-profilok, service-assignmentek, hozzárendelés-fiók pivotok, bankkártyák) + profil, végül a tenant-root (If-Match). Bármely lépés bukása → a root marad (422; átmeneti upstream-zavarnál 503). Invoice/Contact/Ticket sosem törlődik.',
    },
});
// ── Üzenet-melléklet (a ticket-csatolmány MINTÁJÁRA; a bájtok KÖZVETLENÜL a tárba) ──
//
// A KÜLÖNBSÉG a tickethez képest: az üzenet BELSŐ körlevél, tehát NINCS „hordozó" ügyfele — a melléklet a
// MORGENS-t mint ügyfél-rekordot kapja tenantnak (`ATLAS_INTERNAL_CLIENT_ID`), a szerver oldalán feloldva.
// Ez nem trükk: a körlevél szerzője a cég, tehát a fájl is a cégé; a címzett ügyfelek egyikéhez sem tartozik.
// Ezért a mellékletnek NINCS `accountId`-ja sem (a ticketnél a ticket fiókja kötelező).
// ── Org structure (Szervezeti felépítés) — részlegek + csapatok + beosztások (workforce INTERNAL) ──
//
// HÁROMSZINTŰ fa: `workforce/departments` (részlegek) → `workforce/teams` (csapatok, részleg-FK-val) →
// `workforce/positions` (beosztások, csapat-FK-val). A munkatárs KIZÁRÓLAG beosztáshoz kötődik; a
// csapata és a részlege ebből a láncból származik (nincs külön mező rajta).
// A név/leírás kétnyelvű — a busz-oszlop i18n JSON (`{ hu, en }`); a BFF a `nameHu`/`nameEn` (+
// `descriptionHu`/`descriptionEn`) mezőpárra bontja (szerkeszthető külön). A `slug` az angol névből
// generált STABIL azonosító (create-kor; az update NEM regenerálja — org-structure.md döntés, a legacy
// mentéskori újragenerálás megszűnik).
// A `color` badge-szín (Tailwind-színnév) KIZÁRÓLAG a CSAPATON él — a részlegnek nincs színe. Ez a
// rendszer egyetlen szervezeti szín-forrása (avatar-accent, csapat-badge, eszköz-lista).
// A `teams` sorai a `departmentId`-t részleg-névvé oldják fel; a `positions` sorai a `teamId`-t
// csapat-névvé + -színné, ÉS két szinten át a részleg nevévé (`departmentName`).
// ── Invoices draft-CRUD (írás-vertikum — docs/modules/admin/invoices.md) ──
//
// A pénz/mennyiség Decimal-STRING a dráton (a kanonikus fixpontos számítás a `@atlas/shared`
// `calculateInvoiceItem`/`sumInvoiceTotals` — a kliens összeget NEM küldhet, a szerver számol);
// a dátumok DATE-only (YYYY-MM-DD). Az enum-értékkészletek forrás-igazsága a `@atlas/shared`.
// ── Számla-kiállítási életciklus (Számlázz.hu Számla Agent — docs/modules/admin/invoices.md) ──
//
// A számla az Odin `billing/invoices` resource (adat); a kiállítás = (1) Számlázz.hu API-hívás, majd (2)
// SIKER esetén az Odin invoice-sor busz-frissítése (invoice_number/status/dátumok/storno/related). A
// Számlázz.hu-hívás a busz-update ELŐTT (siker → frissít). Hiba → 502 (a számla eredeti státuszban marad);
// nincs konfigurált agent-kulcs → 503. Minden művelet OCC-védett (If-Match a fej verziójából) — hiányzó
// If-Match → 428. A státusz-guardok (draft/issued/overdue/proforma) a BFF-ben (nem-megfelelő státusz → 409).
// ── Szolgáltatás-katalógus HÁROMSZINTŰ szerkesztés (service → plan → price) — a legacy 1:1 enumkészlete ──
// (a `../crm/app/Enums/*` értékei 1:1 átvéve; a pénz Decimal→string, a devizák ISO-4217).
// ── Üzenet write-vertikum (compose/send/schedule/edit/read) — a messages.md írás-vertikum terve ──
// ── Billing profiles (Számlázási profilok) — lista + CRUD (docs/modules/admin/billing-profiles.md) ──
//
// A `billing.billing-profiles` [T, CLIENT-scoped] busz-erőforrás: egy ügyfél számlázási identitásai
// (számlázási név/cím/adószámok/e-mailek/default-ok). Ügyfelenként pontosan EGY elsődleges (`isPrimary`)
// — Odin-oldalon partial unique index `(client_id) WHERE is_primary AND deleted_at IS NULL` védi. A BFF
// az elsődlegessé-tétel előtt feloldja a régi elsődlegest (ensureSinglePrimary, külön OCC-s busz-PUT-ok),
// mert egyébként a create/update a unique index miatt 409/422-t kapna. Az összeg/ID-mezők NEM a body-ban:
// a `clientId`-t a busz injektálja (targetClientId), a `version` az OCC forrása.
// ── Bankkártyák (payment-cards) — sémák + router-ág ──
//
// A `billing.payment-cards` [T, CLIENT-only tenant-root] busz-erőforrás: egy ügyfélhez tartozó bankkártya
// (birtokos + kártyatípus + utolsó 4 jegy + lejárat + bank). A `clientId`-t a busz injektálja; a `version` az
// OCC. A billing.billing-profiles PONTOS mintája, DE NINCS `isPrimary`/set-primary (kártyának nincs elsődlegese).
// Fehérlistás (írható) oszlopok: holderName, brand, last4, expiryMonth, expiryYear, bankName, note. A `brand`
// enum + a `last4` `^\d{4}$` mintáját a Schema Registry is kikényszeríti (rossz payload → 422).
// ── Szolgáltatás-hozzárendelések (service-plan-assignments) — sémák + router-ág ──
//
// A `billing.service-plan-assignments` [T, client + account?] busz-erőforrás: egy ügyfélhez/fiókhoz rendelt
// szolgáltatás-csomag. A legacy per-assignment KLÓNOZTA az egész ServicePlan-fát; az Odin ezt KISIMÍTOTTA:
// az assignment `servicePlanId`-ja a KATALÓGUS-planre mutat, a klón-mezők a `planSnapshot` JSONB-ben fagyasztva,
// a klón-árak a `service-plan-assignment-prices` sorokban (assignmentId-ra kulcsolva). A `clientId`-t a busz
// injektálja (targetClientId), a `version` az OCC forrása.
//
// SCOPE: a komponens-fa (parentId self-ref) IMPLEMENTÁLT (create-tree + update-reconcile + delete-cascade; a
// szülő ára a gyerekek összege). A LISTA továbbra is CSAK root (parentId null) — a gyerekek a detail `children`-jében
// és a bevétel-drilldown children-bontásában látszanak. Dokumentált increment-halasztás: usage-számlázás tényleges feed.
// ── Bevétel-előrejelzés (revenue forecast) — READ-ONLY belső vezetői nézet ──
// A legacy CRM RevenueForecast motorjának 1:1 portja: MRR/PRR/NRR/ARR/AR kártyák + 4 chart, adott hónapra
// és szűrőkre. Minden pénz Decimal-STRING (a BFF fixpontosan számol, NEM float); a deviza mindig 'HUF'
// (EUR/USD → MNB előző-hó-vég árfolyam, hiány/hiba → 1.0 fallback). Jog-kapu: service_assignments.view.
// ── Profil (self-service) — a bejelentkezett user SAJÁT identity/profil-erőforrása ──
//
// A session-adat (SessionSchema) a JWKS-validált token CLAIMJEIBŐL él; ez KÜLÖN, gazdagabb self-profil
// erőforrás, amit a BFF az Odin `/iam/me/*` self-végpontjairól hoz — a user SAJÁT tokenjével (on-behalf,
// self-scope: az Odin a `claims.sub`-ra dolgozik, NINCS :id). A titok-mezők (`currentPassword`,
// `newPassword`, `asanaPat`, `code`) KIZÁRÓLAG bemenet (write-only, sosem jönnek vissza); a `secret`/
// `otpauthUri`/`recoveryCodes` egyszeri-megjelenítésű kimenet (a UI mutatja, a BFF SOHA nem logolja).
// ── Avatar (self-service profilkép) — a bájtok KÖZVETLENÜL a tárba (presigned PUT), Odinon SOSE át ──
//
// Flow: (1) POST /profile/avatar/upload-url {contentType, contentLength} → {uploadUrl, key}; (2) a kliens
// a blobbal PUT-ol az uploadUrl-re (objektum-tár); (3) POST /profile/avatar/commit → {avatarUrl} (friss presigned GET).
// A törlés idempotens. A típus + méret az Odin presigned-aláírásába kötve (nincs MIME-swap / storage-exhaustion).
/**
 * Ticket-csatolmány sub-router (a `clientsContract` mintájára KÜLÖN `c.router`-be emelve). Ez NEM csak
 * rendszerezés: a monolit `atlasContract` a ts-rest kontextus-típus-inferencia határán van, és a lapos
 * bővítés az összes handler kontextus-tipizálását elrontja (a `tsRestHandler` visszatérési literálok
 * kiszélesednek → hamis TS2345). A sub-router EGY top-level kulcsot ad a 4 helyett → visszaadja a fejteret.
 * A route-ok abszolút path-ot tartanak; a hivatkozás `atlasContract.ticketAttachments.{list|uploadUrl|commit|delete}`.
 */
const ticketAttachmentsContract = c.router({
    list: {
        method: 'GET',
        path: '/tickets/:id/attachments',
        responses: {
            200: z.array(TicketAttachmentRowSchema),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Ticket-csatolmányok (belső nézet) egy tickethez — a `support.ticket-attachments` busz-erőforrásból ticketId-re szűrve, friss presigned GET-URL + feltöltő-név dúsítással. A bájtok KÖZVETLENÜL a tárból (presigned), sosem a BFF-en át. Ismeretlen ticketId → üres lista. Jog-kapu: tickets.view. A path-`:id` plain-UUID (traversal choke-point → 400).',
    },
    uploadUrl: {
        method: 'POST',
        path: '/tickets/:id/attachments/upload-url',
        body: TicketAttachmentUploadUrlBodySchema,
        responses: {
            200: TicketAttachmentUploadUrlResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Presigned feltöltő-URL egy ticket-csatolmányhoz (a bájtok KÖZVETLENÜL az objektum-tárba, presigned PUT — sosem a BFF-en át). Jog-kapu: tickets.manage. BFF-validáció: kiterjesztés-whitelist + per-fájl méret-plafon + max. 10 fájl/ticket (422). A cél-tenant a ticket forrásából (szerver-oldal). A path-`:id` plain-UUID (traversal → 400).',
    },
    commit: {
        method: 'POST',
        path: '/tickets/:id/attachments',
        body: TicketAttachmentCommitBodySchema,
        responses: {
            201: TicketAttachmentRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Ticket-csatolmány véglegesítése (a presigned PUT után): az Odin fájl-commit + a `support/ticket-attachments` sor létrehozása. Jog-kapu: tickets.manage. A cél-tenant + accountId a ticket FORRÁSÁBÓL (szerver-oldal). A path-`:id` és a body-`fileId` plain-UUID (traversal → 400).',
    },
    delete: {
        method: 'DELETE',
        path: '/tickets/:id/attachments/:attachmentId',
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Ticket-csatolmány törlése (soft): a `support/ticket-attachments` sor + best-effort az Odin fájl soft-delete (a bájtok NEM hard-cascade — a legacy adatvesztést kihagyjuk). Jog-kapu: tickets.manage. Az attachment ticket alá tartozását a BFF ellenőrzi (IDOR-védelem). A path-`:id`/`:attachmentId` plain-UUID (traversal → 400).',
    },
    downloadUrl: {
        method: 'GET',
        path: '/tickets/:id/attachments/:attachmentId/download-url',
        responses: {
            200: AttachmentDownloadUrlSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'FRISS presigned letöltő-URL egy ticket-csatolmányhoz — a felület KATTINTÁSKOR kéri, mert a listában kapott URL az Odin `FILE_URL_TTL`-je (alapból 5 perc) után lejár, a lap pedig tovább nyitva marad. Jog-kapu: tickets.view. Az attachment ticket alá tartozását a BFF ellenőrzi (IDOR → 404); tartalom nélküli (migrált) sor → 404. Az URL-t nem naplózzuk.',
    },
});
/**
 * Üzenet-melléklet sub-router — a `ticketAttachmentsContract` MINTÁJÁRA (és ugyanazért: a monolit
 * `atlasContract` a ts-rest kontextus-típus-inferencia határán van, ezért 4 lapos route helyett EGY
 * top-level kulcs). Hivatkozás: `atlasContract.messageAttachments.{list|uploadUrl|commit|delete}`.
 */
const messageAttachmentsContract = c.router({
    list: {
        method: 'GET',
        path: '/messages/:id/attachments',
        responses: {
            200: z.array(MessageAttachmentRowSchema),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Üzenet-mellékletek egy üzenethez — a `communication.message-attachments` busz-erőforrásból messageId-re szűrve, friss presigned GET-URL + feltöltő-név dúsítással. A bájtok KÖZVETLENÜL a tárból (presigned), sosem a BFF-en át. Csak a LÁTHATÓ üzenetre (a küldőé, vagy neki szól) — különben 404. Jog-kapu: messages.view. A path-`:id` plain-UUID (traversal → 400).',
    },
    uploadUrl: {
        method: 'POST',
        path: '/messages/:id/attachments/upload-url',
        body: MessageAttachmentUploadUrlBodySchema,
        responses: {
            200: MessageAttachmentUploadUrlResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Presigned feltöltő-URL egy üzenet-melléklethez (a bájtok KÖZVETLENÜL a tárba, presigned PUT). Jog-kapu: messages.manage + CSAK a saját (a hívó által küldött) üzenet — idegen üzenet → 403. BFF-validáció: kiterjesztés-whitelist + per-fájl méret-plafon + max. 10 fájl/üzenet (422). A cél-tenant a BELSŐ (MORGENS) ügyfél-rekord, szerver-oldalról (`ATLAS_INTERNAL_CLIENT_ID`); ha nincs beállítva, a végpont 503 (fail-closed, sosem választ magának tenantot). A path-`:id` plain-UUID (traversal → 400).',
    },
    commit: {
        method: 'POST',
        path: '/messages/:id/attachments',
        body: MessageAttachmentCommitBodySchema,
        responses: {
            201: MessageAttachmentRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Üzenet-melléklet véglegesítése (a tár-PUT után): az Odin fájl-commit + a `communication/message-attachments` sor létrehozása. Jog-kapu: messages.manage + CSAK a saját üzenet (idegen → 403). A cél-tenant a BELSŐ ügyfél-rekord (szerver-oldal). A path-`:id` és a body-`fileId` plain-UUID (traversal → 400).',
    },
    delete: {
        method: 'DELETE',
        path: '/messages/:id/attachments/:attachmentId',
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Üzenet-melléklet törlése (soft): a `communication/message-attachments` sor + best-effort az Odin fájl soft-delete. Jog-kapu: messages.manage + CSAK a saját üzenet (idegen → 403). A melléklet üzenet alá tartozását a BFF ellenőrzi (IDOR-védelem). A path-`:id`/`:attachmentId` plain-UUID (traversal → 400).',
    },
});
/**
 * Komment-sablon sub-router (SHARED katalógus) — a `ticketAttachmentsContract` mintájára KÜLÖN `c.router`-be
 * emelve (a monolit `atlasContract` ts-rest kontextus-típus-inferencia határa: EGY top-level kulcs 3 helyett).
 * A `list` STATIKUS path (`/tickets/comment-templates`) → a controllerben a `/tickets/:id` (ticketGet) ELÉ
 * kell sorolni (Express first-match). Hivatkozás: `atlasContract.ticketCommentTemplates.{list|create|delete}`.
 */
const ticketCommentTemplatesContract = c.router({
    list: {
        method: 'GET',
        path: '/tickets/comment-templates',
        responses: {
            200: z.array(TicketCommentTemplateRowSchema),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Komment-sablonok (SHARED katalógus, belső) — a `support.ticket-comment-templates` busz-erőforrásból, cím szerint rendezve. Olvasás minden aktív belső identitynek. Jog-kapu: tickets.view.',
    },
    create: {
        method: 'POST',
        path: '/tickets/comment-templates',
        body: TicketCommentTemplateCreateBodySchema,
        responses: {
            201: TicketCommentTemplateRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Komment-sablon mentése (updateOrCreate a `title`-re): azonos című sablon → tartalom frissül (OCC), különben új sor. Jog-kapu: tickets.manage (a SHARED-írás write:all:support-ot igényel). NO_TENANT.',
    },
    delete: {
        method: 'DELETE',
        path: '/tickets/comment-templates/:templateId',
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Komment-sablon törlése (soft). Jog-kapu: tickets.manage (write:all:support). A path-`:templateId` plain-UUID (traversal → 400).',
    },
});
// ── Service-usage (UsageDataChecker) + manuális havi árfolyam ──
//
// A `billing/service-usage` (INTERNAL) nyers külső-rendszer használati sorok olvasása + a `platform.currency_rates`
// manuális havi árfolyam megtekintés/felvitel. A kolléga a generátor ELŐTT megnézi a nyers usage-adatot, és
// megadja a deviza-tételek havi manuális árfolyamát (EUR/USD → HUF, a hó 1-jére). A `sourceReferenceId` (külső
// rendszer ID) → best-effort feloldott fiók-nevek (foundation clients/account). A read `invoices.manage`;
// a manuális árfolyam-upsert Odin-oldalon `platform:admin` (jog nélkül 403 — a UI-n is kapuzva).
/**
 * PARTNER-PORTÁL sub-router — a `ticketAttachments` MINTÁJÁRA, és ugyanazért.
 *
 * A monolit `atlasContract` a ts-rest TÍPUS-INFERENCIA határán van: a tizenöt portál-végpont LAPOS felvétele
 * után a `tsc` már nem tudta szerializálni a kliens (`initQueryClient`) inferált típusát („The inferred type
 * of this node exceeds the maximum length the compiler will serialize" — mérve). A sub-router EGY
 * top-level kulcsot ad a tizenöt helyett, és ezzel visszaadja a fejteret.
 *
 * A route-ok ABSZOLÚT path-ot tartanak; a hivatkozás `atlasContract.portal.{tickets|invoices|…}`.
 */
const portalContract = c.router({
    /**
     * PARTNER-PORTÁL — a bejelentkezett KAPCSOLATTARTÓ saját felülete.
     *
     * Ezek az EGYETLEN végpontok, amiket `type=partner` token elér (`@AllowPartner()` a BFF-en) — minden más
     * atlas-végpont 403 neki. A válaszok a LÁTHATÓSÁGI MAG (négy ág) szűrése után keletkeznek, és a DB-oldali
     * fal (Odin RLS + a fiók-grant) alattuk ott van: ha az app-réteg hibázna, más ügyfél adata AKKOR SEM
     * szivárog. Belső jegyzet, belső jog-kulcs és kolléga-e-mail SOHA nem kerül a válaszba.
     */
    /**
     * A PARTNER-SHELL adatai (ki vagyok · mely fiókjaim · mennyi vár rám) — a felület kerete ebből épül.
     * Minden portál-lapon lefut, ezért EGY kérés (nem három).
     */
    me: {
        method: 'GET',
        path: '/portal/me',
        /**
         * `accountId` = a sidebar FIÓK-SCOPE-ja. Hiányzik/üres → „Minden fiók" (a hívó összes portál-fiókja).
         *
         * MIÉRT A KERET-VÉGPONTON IS: a nav-jelvények (nyitott jegy · olvasatlan válasz · üzenet · lejárt számla)
         * ugyanazt az adatot összegzik, amit a listák mutatnak — ha a jelvény aggregált maradna, miközben a lista
         * szűkített, a kettő ellentmondana („71 jegy" a jelvényen, 3 a listán). A FIÓK-LISTA viszont MINDIG a
         * teljes jogosultság szerint jön (abból épül maga a váltó), és idegen/érvénytelen `accountId` esetén a
         * szerver FAIL-SOFT: a scope-ot figyelmen kívül hagyja (nem 403), különben a keret sem tudna renderelni.
         */
        query: z.object({
            lang: z.enum(['hu', 'en']).optional(),
            accountId: z.string().uuid().optional(),
        }),
        responses: {
            200: PortalMeResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A partner-felület keretének adatai: a hívó neve/e-mailje, a portál-fiókjai (név + ügyfél-név + fiókonkénti szerep + elsődleges-kapcsolattartó tény) és a nav-jelvények számai. A fiók-lista a token portál-claimjéből jön, a fiók- és ügyfél-név a `GET /iam/me/tenants` grant-szűrt feloldásából; a `null` számláló azt jelenti, hogy NEM TUDJUK (nincs gépi kredenciál vagy upstream-hiba) — a felület ilyenkor nem mutat jelvényt.',
    },
    /** A partner ÜZENETEI és MEGHÍVÓI egy lapon (a terv IA-ja szerint). */
    messages: {
        method: 'GET',
        path: '/portal/messages',
        /**
         * `accountId` = a sidebar FIÓK-SCOPE-ja. A szűkítés CSAK a FIÓK-CÍMZETT üzenetekre hat: a SZEMÉLYES
         * (`contact`) és az ügyfél-közlemény (`broadcast`) mindig látszik, mert azok nem fiókhoz kötöttek —
         * elrejtésük adat-vesztésként jelenne meg a felhasználónak. A MEGHÍVÓK szintén személy-scope-úak
         * (a hívó résztvevő), tehát a scope nem szűri őket.
         */
        query: z.object({
            lang: z.enum(['hu', 'en']).optional(),
            accountId: z.string().uuid().optional(),
            /** `only` → CSAK a hívó archiváltjai; elhagyva → az alap-lista NÉLKÜLÜK (olvasó-oldali archívum). */
            archived: z.enum(['only']).optional(),
        }),
        responses: {
            200: PortalMessagesResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hívóhoz szóló KIKÜLDÖTT, KÜLSŐ üzenetek (személyes · fiók-szintű · ügyfél-közlemény) + a közelgő megbeszélés-meghívói a saját RSVP-jével. A `communication` tárgykör NEM tenant-scope-olt (az RLS see_all-t kér), ezért a BFF a GÉPI identitásával olvas, és a címzett-szűrés szerver-oldali. Gépi kredenciál nélkül a válasz `unavailable: true` (nem üres lista).',
    },
    /** Egy üzenet teljes tartalma + mellékletei. */
    messageDetail: {
        method: 'GET',
        path: '/portal/messages/:id',
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: PortalMessageDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy hozzá szóló üzenet teljes (szanitált) törzse és mellékletei friss presigned URL-lel. Nem a hívóhoz szóló üzenet → 404 (a létezését sem szivárogtatjuk).',
    },
    /** Üzenet olvasottra állítása. */
    messageRead: {
        method: 'POST',
        path: '/portal/messages/:id/read',
        body: c.noBody(),
        responses: {
            200: z.object({ updated: z.number().int().nonnegative() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az üzenet olvasottra állítása a HÍVÓ számára (`message_reads` sor). Idempotens. A jog: a hívó címzett legyen — különben 404.',
    },
    /**
     * OLVASÓ-OLDALI ARCHIVÁLÁS / visszaállítás — a hívó SAJÁT olvasás-során (a többi címzettnél az üzenet megmarad,
     * NEM törlés). Az archiválás olvasottá is tesz. Idempotens. Nem a hívóhoz szóló üzenet → 404.
     */
    messageArchive: {
        method: 'POST',
        path: '/portal/messages/:id/archive',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az üzenet archiválása a HÍVÓ saját nézetéből (`message_reads.archivedAt`; olvasottá is tesz). Idempotens. Nem a hívóhoz szóló üzenet → 404.',
    },
    messageUnarchive: {
        method: 'POST',
        path: '/portal/messages/:id/unarchive',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az archivált üzenet visszaállítása a hívó listájába (az olvasottság marad). Idempotens. Nem a hívóhoz szóló üzenet → 404.',
    },
    /**
     * MIND OLVASOTTRA állítás — a hívó minden LÁTHATÓ, még olvasatlan üzenetére. A válasz a tényleges
     * darabszámot adja (`updated`), tehát a felület nem találgat: ha 0, nem volt mit jelölni.
     *
     * MIÉRT NEM a felület ciklusa: a „látható üzenet" halmazát a szerver ismeri (a modul három szűrő-
     * feltétele), és N kérés részleges elhasalása után a lista hamis állapotot mutatna.
     */
    messagesReadAll: {
        method: 'POST',
        path: '/portal/messages/read-all',
        body: c.noBody(),
        responses: {
            200: z.object({ updated: z.number().int().nonnegative() }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hívó MINDEN látható, olvasatlan üzenetének olvasottra állítása. A válasz `updated` mezője a tényleges darabszám.',
    },
    /** Megbeszélés-meghívó megválaszolása (RSVP). */
    meetingRsvp: {
        method: 'PUT',
        path: '/portal/meetings/:id/rsvp',
        body: PortalMeetingRsvpBodySchema,
        responses: {
            200: z.object({ status: z.enum(['pending', 'confirmed', 'declined']) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hívó RSVP-jének beállítása egy megbeszélés-meghívón (`meeting_participants.status`). CSAK a SAJÁT résztvevő-sorát írhatja; más résztvevő sora → 404. A visszaállás `pending`-re engedett (a felület „válasz módosítása" gombja).',
    },
    /** A partner SZÁMLÁI (a saját fiókjaihoz tartozók) — három fül, kereső, fiók-szűrő. */
    invoices: {
        method: 'GET',
        path: '/portal/invoices',
        query: PortalInvoiceListQuerySchema,
        responses: {
            200: PortalInvoiceListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hívó fiókjaihoz tartozó számlák: fül (kiállított | kiegyenlített | érvénytelenített), szabad-szöveges kereső (számlaszám, vevő), fiók-szűrő. A pénz DECIMAL-STRING devizával; a lejárt számlák összesítése devizánként bontva. A jog-fal a portál-szerep + az Odin RLS (a számla tenant-scope-olt, a partner csak a grantolt ügyfelét látja).',
    },
    /** Egy számla tételesen (a számlára kerülő megjegyzéssel). */
    invoiceDetail: {
        method: 'GET',
        path: '/portal/invoices/:id',
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: PortalInvoiceDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A partner SAJÁT számlája tételesen: a tételek (megnevezés, időszak, mennyiség, egységár, nettó/áfa/bruttó) és a számlára kerülő megjegyzés; a közös számla jelölésével. Jog-fal mint a PDF-nél (idegen számla → 404); a stornó/érvénytelenített is lekérhető, a piszkozat is („előkészítés alatt”).',
    },
    /** A partner havi számlázási idővonala: tény (számlák tételesen) + terv (még nem számlázott, esedékes tételek). */
    billing: {
        method: 'GET',
        path: '/portal/billing',
        query: PortalBillingQuerySchema,
        responses: {
            200: PortalBillingResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Havi idővonal (alap: az idei év eleje → a mostani hónap + 3). Hónaponként a TÉNY (a hónapban kelt számlák tételesen, a piszkozat is) és a TERV (a hónapban esedékes, még nem számlázott tételek — a belső bevétel-motor szabályával, a SZÁMLÁZÁS hónapjára). Fiók-szűrésnél a közös (több fiókra szóló) tétel és számla is megjelenik, jelölve. Használat-alapú tétel összege az utolsó ismert számla-tételből becsült.',
    },
    /** Egy számla PDF-je (base64) — a Számlázz.hu-ról, a szerver agent-kulcsával. */
    invoicePdf: {
        method: 'GET',
        path: '/portal/invoices/:id/pdf',
        responses: {
            200: InvoicePdfSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'A partner SAJÁT számlájának PDF-je (base64; a UI Blob-bá dekódolja — a fájl SOHA nem tárolódik). A jog-fal: a számla a hívó valamelyik portál-fiókjához (vagy fiók nélkül a grantolt ügyfeléhez) tartozzon — különben 404 (a létezését sem szivárogtatjuk). Nem letölthető státusz (pl. piszkozat) → 409; nincs agent-kulcs → 503.',
    },
    /** A partner szolgáltatásai (szolgáltatásonként, időszakokkal) + a következő havi várható díj. */
    services: {
        method: 'GET',
        path: '/portal/services',
        query: z.object({
            accountId: z.string().uuid().optional(),
            lang: z.enum(['hu', 'en']).optional(),
        }),
        responses: {
            200: PortalServicesResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hívó fiókjainak AKTÍV szolgáltatás-hozzárendelései + a következő havi várható díj (devizánként bontva, a használat-alapú tételek jelzésével). A szolgáltatás-NÉV a katalógus-csomag MAI neve (a pillanatképé átnevezés után elavul), híján a `planSnapshot`-é. A katalógust partner-token nem olvashatja, ezért a GÉPI identitás olvassa, a hívó már látható hozzárendeléseire vetítve (5 perc cache); gépi kredenciál nélkül a név a pillanatképből jön, a lista nem bukik el.',
    },
    tickets: {
        method: 'GET',
        path: '/portal/tickets',
        query: PortalTicketListQuerySchema,
        responses: {
            200: PortalTicketListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A partner SAJÁT jegy-listája, két fülön (folyamatban / lezárt). A láthatóság négy ága: elsődleges kapcsolattartó → a fiók minden jegye; nincs elsődleges → mindenki mindent lát; ő nyitotta; explicit jegy-hozzáférés. Rendezés: az OLVASATLAN külső kommentesek elöl (legacy paritás), utána lastActivityAt DESC (a LEGUTÓBBI aktivitás, nem a nyitás ideje). Csak partner token (403 belsősnek/gépinek).',
    },
    ticketDetail: {
        method: 'GET',
        path: '/portal/tickets/:id',
        // A `lang` a NEVEK sorrendjéhez kell (magyarul vezetéknév elöl), ugyanúgy, mint a listán: a komment-szerzők
        // és a hozzárendelt kolléga neve KÉSZ stringként megy ki, tehát a szervernek tudnia kell a néző nyelvét.
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: PortalTicketDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy jegy részletei a partner szemszögéből: fejadatok, a nyitó leírás, fájlmegosztó linkek, KÜLSŐ kommentek (belső jegyzet SOHA), mellékletek. A hozzáférést UGYANAZ a láthatósági helper dönti el, mint a listánál — nem látható jegyre 404 (nem 403: a létezését sem szivárogtatjuk).',
    },
    ticketFormOptions: {
        method: 'GET',
        path: '/portal/ticket-form-options',
        query: z.object({ accountId: z.string().uuid().optional() }),
        responses: {
            200: PortalTicketFormOptionsResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A jegy-nyitó űrlap opciói: a hívó portál-hozzáférésű FIÓKJAI (csak ezekre nyithat) + `accountId` esetén a fiók szolgáltatásai KÉT csoportban („Szolgáltatásaim" / „További szolgáltatásaink" — legacy paritás) + a jegytípusok. Ügyfél-választó nincs: a fiók-lista mellé az ügyfél neve kerül.',
    },
    ticketCreate: {
        method: 'POST',
        path: '/portal/tickets',
        body: PortalTicketCreateBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Jegy nyitása a partner-portálról. A KÉRELMEZŐ a token `sub`-ja (a partner nem nyithat más nevében), a csapat és az első felelős szerver-oldalon oldódik fel a szolgáltatásból/fiókból. Jog: a fiókra szóló portál-szerep, `portal-viewer` NEM nyithat (403). A validáció a belső jegy-nyitással AZONOS (tárgy kötelező, leírás ≥ 25 látható karakter) — ugyanaz a write-orchesztráció fut.',
    },
    /**
     * A JEGY-HOZZÁFÉRÉS beállítása (ki láthatja a jegyet a fiók kapcsolattartói közül).
     *
     * A jog TULAJDONLÁS-alapú (user-döntés): a jegy NYITÓJA, vagy a fiók ELSŐDLEGES kapcsolattartója oszthat —
     * a portál-szerep nem ad jogot. Kivétel: a `portal-viewer` tulajdonosként sem oszthat (403 — a
     * hozzáférés-osztás ÍRÁS). A body TELJES állapotot ad: a kimaradó hozzáférés visszavonódik.
     * Az újonnan hozzáférést kapó kapcsolattartó in-app + e-mail értesítést kap (`ticket.access_granted`);
     * a visszavonásról szándékosan NEM megy értesítés.
     */
    ticketAccess: {
        method: 'PUT',
        path: '/portal/tickets/:id/access',
        body: PortalTicketAccessBodySchema,
        responses: {
            200: z.object({
                granted: z.number().int().nonnegative(),
                revoked: z.number().int().nonnegative(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A jegy-hozzáférés (ki láthatja a jegyet) beállítása a fiók kapcsolattartói közül. Jog: TULAJDONLÁS — a jegy nyitója VAGY a fiók elsődleges kapcsolattartója (`canManageTicketAccess`) + a láthatósági mag; a portál-szerep a tulajdonlásról nem dönt, de `portal-viewer` tulajdonosként sem oszthat (403, írás). Csak a FIÓK kapcsolattartói adhatók meg (idegen identity → 422). Teljes állapot: a listából kimaradó hozzáférés visszavonódik; az új kedvezményezett értesítést kap (in-app + e-mail), a visszavonás csendes.',
    },
    ticketReply: {
        method: 'POST',
        path: '/portal/tickets/:id/replies',
        body: PortalTicketReplyBodySchema,
        responses: {
            201: z.object({ id: z.string(), createdAt: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Válasz a jegy-szálra. A `kind`-ot a SZERVER dönti el: partnertől MINDIG `external` (belső jegyzetet a partner nem tud írni, akkor sem, ha a body-ban kérné). A jog: a láthatósági mag ugyanaz, mint az olvasásnál + `portal-viewer` nem válaszol. Nem látható jegy → 404.',
    },
    /**
     * A SAJÁT VÁLASZ SZERKESZTÉSE a portálról (SZERZŐ-scope).
     *
     * UGYANAZ a write-orchesztráció fut, mint a belső szerkesztésen (`updateTicketComment`) — egy szabály-
     * készlet: szerző-ellenőrzés, `If-Match`/OCC, ugyanaz a body-validáció. A portál-specifikus rész csak a
     * jog: a láthatósági mag + `portal-viewer` nem szerkeszt + a `kind` marad `external` (a partner belső
     * jegyzetet nem tud írni, tehát szerkesztéssel sem hozhat létre).
     *
     * MIÉRT a szerző, és nem a fiók: egy fiókon több kapcsolattartó is van, és egymás válaszait nem
     * írhatják át (a jegy-szál a beszélgetés nyoma, nem szerkeszthető közös dokumentum).
     */
    ticketReplyUpdate: {
        method: 'PUT',
        path: '/portal/tickets/:id/replies/:commentId',
        headers: ClientIfMatchHeaders,
        body: PortalTicketReplyBodySchema,
        responses: {
            // SZŰK válasz (nem a teljes komment-sor): a felület a szálat már a kezében tartja, és a szerkesztésből
            // pontosan KETTŐ dolog új — a szöveg és az OCC-verzió (a következő szerkesztés `If-Match`-e). A szerző,
            // az avatar és a reakciók változatlanok; ha a teljes sort adnánk vissza, a szervernek újra fel kellene
            // oldania a nevet és a reakciókat egy olyan íráshoz, ami egyiket sem érinti.
            200: PortalTicketCommentEditedSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A partner SAJÁT válaszának szerkesztése a jegy-szálon. SZERZŐ-scope: a komment `authorIdentityId`-je == a hívó token `sub`-ja (idegen szerző → 403, akkor is, ha ugyanazon a fiókon van). A `kind` marad `external`. Jog: a láthatósági mag + `portal-viewer` nem szerkeszt. If-Match/OCC (hiányzó fejléc → 428, elavult verzió → 409). Nem látható jegy → 404.',
    },
    /**
     * EMOJI-REAKCIÓ TOGGLE a portálon — ugyanaz a végpont-alak és ugyanaz a write, mint a belső szálon.
     *
     * A PARTNER-FAL AZ ODIN RLS-ÉBEN ÁLL, és KIND-ÉRZÉKENY: a partner a KÜLSŐ kommenten reagálhat, a belső
     * jegyzeten sem olvasni, sem írni nem tud. A portál-szál eleve csak külső kommenteket tartalmaz, tehát ez
     * itt védelem-mélységben van (a partner-token közvetlenül is hívhatná a buszt).
     */
    ticketReplyReactionToggle: {
        method: 'POST',
        path: '/portal/tickets/:id/replies/:commentId/reactions',
        // A `lang` a „ki reagált" NEVEK sorrendjéhez kell (a válasz dúsított aktor-listát ad) — ugyanaz a minta,
        // mint a lista/részlet végpontokon.
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        body: TicketReactionToggleBodySchema,
        responses: {
            200: z.object({ reactions: z.array(TicketCommentReactionSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Emoji-reakció be/ki a partner-portál jegy-szálán (toggle). A reagáló a token `sub`-ja (SOHA nem kliens-input). Az emoji a KÖTÖTT készletből (`TICKET_REACTION_EMOJIS`) — bármi más 422. Jog: a láthatósági mag; a `portal-viewer` NEM reagál (a reakció írás). Csak KÜLSŐ komment reagálható — belső jegyzetre az Odin RLS-e sem engedi. Nem látható jegy → 404.',
    },
    /**
     * A SZÁL MEGNYITÁSA olvasottra állítja a hívó ide tartozó olvasatlan válasz-értesítéseit.
     *
     * MIÉRT KÜLÖN VÉGPONT ÉS MIÉRT POST: a részlet-olvasás GET, ami nem módosíthat állapotot (cache-elhető,
     * előre-betölthető, újrajátszható) — az olvasottra állítás így SZÁNDÉKOS művelet, nem egy olvasás
     * mellékhatása. A felület a részlet betöltése után hívja, és a lista olvasatlan-jelvénye ettől tisztul.
     */
    ticketMarkRead: {
        method: 'POST',
        path: '/portal/tickets/:id/mark-read',
        body: c.noBody(),
        responses: {
            200: z.object({ updated: z.number().int().nonnegative() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A jegy-szál olvasottra állítása a HÍVÓ számára: a `ticket.comment.external` típusú, ehhez a jegyhez tartozó olvasatlan értesítés-sorai olvasottak lesznek. Idempotens (nincs olvasatlan → `updated: 0`). A jog UGYANAZ a láthatósági mag, mint az olvasásnál; nem látható jegy → 404. A `portal-viewer` is használhatja: a saját feedjét jelöli, nem a jegyet módosítja.',
    },
    /**
     * WYSIWYG-KÉP a partner-portálról — a jegy-leírás szerkesztőjének kép-gombja.
     *
     * A HÁROM LÉPÉS ugyanaz, mint a belső oldalon (upload-url → közvetlen PUT a tárba → commit): a BÁJTOK
     * SOSEM mennek át a BFF-en. A KÜLÖNBSÉG a TENANT és a MEGJELENÍTÉS:
     *
     * • **TENANT:** az `accountId` KÖTELEZŐ, és a cél-ügyfél a hívó SAJÁT portál-hozzáféréséből oldódik fel.
     *   A belső oldal „fiók nélkül a MORGENS ügyfél a gazda" fallbackja itt NEM létezhet: a partner írása nem
     *   kerülhet a mi tenantunkba. Idegen fiók → 403, `portal-viewer` → 403 (a kép-feltöltés ÍRÁS).
     * • **MEGJELENÍTÉS:** a belső feloldó végpont (`/editor-images/:fileId/url`) SZÁNDÉKOSAN nincs nyitva
     *   partnerre — az BÁRMELY fájl-id-t feloldaná, tehát idegen jegyek képei próbálhatók lennének. A partner
     *   ehelyett (a) a szerkesztőben a commit `displayUrl`-jét használja, (b) egy MENTETT jegy képéhez a
     *   jegyhez KÖTÖTT feloldót (`GET /portal/tickets/:id/images/:fileId`), ami csak akkor ad URL-t, ha a
     *   fájl-id tényleg szerepel a LÁTHATÓ jegy leírásában vagy egy külső kommentjében.
     */
    /**
     * JEGY-MELLÉKLET a portálról — a belső `ticketAttachments.{uploadUrl|commit}` PÁRJA, ugyanazzal a
     * háromlépéses alakkal (upload-url → közvetlen PUT a tárba → commit). A body-szerződések is a BELSŐEK
     * (`TicketAttachment*Schema`): egy fogalom, egy mezőnév az API-n.
     *
     * MIÉRT KÜLÖN VÉGPONT, ha a body azonos: a JOG más. A belsőt a `tickets.manage` feature-jog kapuzza, ezt a
     * LÁTHATÓSÁGI MAG + a fiók-szerep (`portal-viewer` nem tölt fel). A belső végpont partnerre nyitása a
     * feature-jog megkerülése lenne.
     */
    ticketAttachmentUploadUrl: {
        method: 'POST',
        path: '/portal/tickets/:id/attachments/upload-url',
        body: TicketAttachmentUploadUrlBodySchema,
        responses: {
            200: TicketAttachmentUploadUrlResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Presigned feltöltő-URL egy jegy-melléklethez a partner-portálról (a bájtok KÖZVETLENÜL a tárba, presigned PUT — sosem a BFF-en át). Jog: a láthatósági mag (nem látható jegy → 404) + a fiók-szerep (`portal-viewer` → 403). BFF-kapu: kiterjesztés-whitelist + per-fájl méret-plafon + max. 10 melléklet/jegy (422). A cél-ügyfél ÉS a cél-fiók a JEGY sorából (szerver-oldal, sosem kliens-input). A path-`:id` plain-UUID (traversal → 404).',
    },
    ticketAttachmentCommit: {
        method: 'POST',
        path: '/portal/tickets/:id/attachments',
        body: TicketAttachmentCommitBodySchema,
        responses: {
            201: PortalTicketAttachmentSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A feltöltött jegy-melléklet véglegesítése (a presigned PUT után): az Odin fájl-commit + a `support/ticket-attachments` sor. A válasz a PARTNER-nézet sora (`filename` + friss presigned `downloadUrl`), nem a belső alak. Jog és cél-tenant ugyanaz, mint az upload-url-nél; a darabszám-plafon a commit-on ÚJRA ellenőrzött (TOCTOU). A path-`:id` és a body-`fileId` plain-UUID.',
    },
    ticketAttachmentDownloadUrl: {
        method: 'GET',
        path: '/portal/tickets/:id/attachments/:attachmentId/download-url',
        responses: {
            200: AttachmentDownloadUrlSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'FRISS presigned letöltő-URL egy jegy-melléklethez a portálról — a belső `ticketAttachments.downloadUrl` párja (kattintáskor kérve, mert a részletben kapott URL 5 perc után lejár). Jog: a láthatósági mag (nem látható jegy → 404); a `portal-viewer` is letölthet. A cél-ügyfél a JEGY sorából. IDOR: idegen melléklet → 404.',
    },
    editorImageUploadUrl: {
        method: 'POST',
        path: '/portal/editor-images/upload-url',
        body: PortalEditorImageUploadBodySchema,
        responses: {
            200: EditorImageUploadUrlSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'WYSIWYG-kép feltöltő-URL a partner-portálról (a jegy-leírás szerkesztőjéhez). A bájtok KÖZVETLENÜL a tárba mennek presigned PUT-tal — a BFF csak az URL-t proxyzza, és azt SOHA nem naplózza. Up-front fal: a KÖZÖS média-whitelist (`EDITOR_MEDIA_TYPES` — kép: jpeg/png/gif/webp, videó: mp4/webm/quicktime) + típus-függő plafon (`editorMediaMaxBytes`: kép 5 MB, videó 25 MB). A CÉL-FIÓK KÖTELEZŐ, és a cél-ügyfél a hívó SAJÁT portál-hozzáféréséből oldódik fel (idegen fiók → 403); a `portal-viewer` nem tölthet fel (403).',
    },
    editorImageCommit: {
        method: 'POST',
        path: '/portal/editor-images/:fileId/commit',
        body: PortalEditorImageCommitBodySchema,
        responses: {
            200: PortalEditorImageCommitSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A feltöltött kép véglegesítése (az Odin fájl-commit ellenőrzi a bájtokat). A válasz KÉT URL: a HTML-be MENTENDŐ STABIL út (`/editor-images/:fileId/content` — ugyanaz az alak, amit a belső felület is renderel), és egy FRISS presigned `displayUrl` KIZÁRÓLAG a szerkesztőn belüli azonnali előnézethez (a mentett HTML-be SOHA nem kerül, mert lejárna). A cél-fiók itt is kötelező és a hívó hozzáféréséből ellenőrzött.',
    },
    ticketImage: {
        method: 'GET',
        path: '/portal/tickets/:id/images/:fileId',
        responses: {
            200: PortalTicketImageSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy jegy-tartalomba beágyazott kép MEGJELENÍTÉSI URL-je (friss presigned GET). A FAL: a `fileId`-t csak akkor oldjuk fel, ha tényleg szerepel a LÁTHATÓ jegy leírásában vagy egy KÜLSŐ kommentjében — a belső jegyzetek szövege ebbe a vizsgálatba sem kerül be. Nem látható jegy vagy oda nem tartozó fájl → 404 (a létezését sem szivárogtatjuk). Ez a szűk feloldó váltja ki a belső `/editor-images/:fileId/url`-t, ami bármely fájl-id-t feloldana.',
    },
    /**
     * FEJLESZTÉSEK a partner-portálon — a KIHIRDETETT és PUBLIKUS tételek (`status=published` +
     * `visibility=public`). A szűrést a BFF végzi a GÉPI service-identityvel olvasott adaton: a partner-token
     * SHARED táblán 0 sort kap, és így a piszkozat-szivárgás EGY helyen dől el (lásd `support.prisma`).
     *
     * Nincs lapozás: a lista kurátori (nem naplófolyam), és a partner számára a legutóbbi kiadások + a
     * publikus útiterv a lényeg — a plafon szerver-oldali, a felület nem kér többet.
     */
    productUpdates: {
        method: 'GET',
        path: '/portal/product-updates',
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: PortalProductUpdateListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A partnernek szánt fejlesztések: a Változásnapló (kiadott tételek, dátum szerint csökkenően) és az Útiterv (még nem kiadott, publikus tételek). CSAK `status=published` + `visibility=public`; a szűrés a gépi identityvel olvasott adaton, a BFF-en fut. A szöveg a hívó nyelvén jön (i18n `hu`-fallback). Jog: élő portál-hozzáférés (a `portal.product_updates.view` minden portál-szerepnél megvan).',
    },
    /**
     * A bejegyzés TÖRZSÉBE ágyazott kép megjelenítési URL-je — PARTNER-oldali, SZŰK feloldó.
     *
     * MIÉRT KELL: a tárolt HTML-ben a kép a stabil belső utat viseli (`/editor-images/:fileId/content`), amit a
     * partner nem hívhat (az a végpont belső-only, és jó, hogy az: ott BÁRMELY fájl-id feloldható lenne, amihez
     * a hívó jogosult). A kép-URL-t a BFF SEM írhatja bele a törzsbe: a web-oldali szanitáló minden `<img>`-et
     * eldob, aminek a `src`-je nem a stabil út (mérve — enélkül a partner csendben kép nélkül látta a bejegyzést).
     *
     * A FAL: a `fileId` CSAK akkor oldódik fel, ha egy `published` + `public` bejegyzés törzse hivatkozik rá —
     * tehát a partner nem tud idegen (jegy-, üzenet-) képet lekérni ezen az úton.
     */
    productUpdateImage: {
        method: 'GET',
        path: '/portal/product-updates/images/:fileId',
        responses: {
            200: z.object({ url: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy PUBLIKUS fejlesztés-bejegyzés törzsébe ágyazott kép friss presigned GET-URL-je. A `fileId` csak akkor oldódik fel, ha `status=published` + `visibility=public` bejegyzés törzse hivatkozik rá (különben 404) — idegen kép ezen az úton nem kérhető. A feloldás a GÉPI service-identityvel megy; a presigned URL sosem kerül a tárolt HTML-be és sosem kerül logba. Jog: élő portál-hozzáférés.',
    },
});
/**
 * FEJLESZTÉSEK (útiterv + változásnapló) sub-router — a `ticketAttachmentsContract` mintájára KÜLÖN
 * `c.router`-be emelve (a monolit `atlasContract` a ts-rest kontextus-típus-inferencia határán van; a lapos
 * bővítés az összes handler tipizálását elrontja). Hivatkozás: `atlasContract.productUpdates.{...}`.
 *
 * A JOG-KAPUK: olvasás `product_updates.view` · szerkesztés `product_updates.manage` · KIHIRDETÉS
 * `product_updates.publish` (KÜLÖN jog: a belsős szerkesztés és a partnereknek szóló kihirdetés más
 * bizalmi szint) · törlés `product_updates.delete`.
 */
const campaignForecastContract = c.router({
    list: {
        method: 'GET',
        path: '/campaign-forecast',
        query: CampaignListQuerySchema,
        responses: {
            200: CampaignListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kampányok listája (belső) a support.campaigns busz-erőforrásból; a fiók/ügyfél/felelős neve feloldva, a státusz (tervezett/fut/lezárt) a dátumokból SZÁMÍTVA. Szűrés: szabad szöveg (név/kód/fiók/ügyfél/felelős), ügyfél, fiók, felelős, státusz, és `from`/`to` ÁTFEDÉS-szűrő (a belelógó kampány is kell — az idősáv ezzel tölt). Rendezés + offset-lapozás a BFF-oldalon. Jog: campaign_forecast.view.',
    },
    refs: {
        method: 'GET',
        path: '/campaign-forecast/refs',
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: CampaignRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kampány-form forrásai: ügyfelek + fiókok (`clientId`-vel a kaszkádhoz) + a választható felelősök (CSAK aktív belső identity; a név a `?lang=` szerinti sorrendben). Jog: campaign_forecast.manage.',
    },
    detail: {
        method: 'GET',
        path: '/campaign-forecast/:id',
        responses: {
            200: CampaignDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy kampány részlete: a sor + a leírás + a hívó jogai (`canManage`/`canDelete`). Jog: campaign_forecast.view. A path-`:id` plain-UUID (traversal → 400).',
    },
    create: {
        method: 'POST',
        path: '/campaign-forecast',
        body: CampaignCreateBodySchema,
        responses: {
            201: CampaignCreateResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kampány(ok) felvétele: az `accountIds` minden fiókjára KÜLÖN sor keletkezik (azonos név/időszak/felelős, saját generált kóddal). A művelet NEM ATOMI (a BFF nem nyit tranzakciót a buszon) — a válasz `created` és `failed` listát ad; ha egy sor sem jött létre, 422/502. Jog: campaign_forecast.manage.',
    },
    update: {
        method: 'PATCH',
        path: '/campaign-forecast/:id',
        headers: ClientIfMatchHeaders,
        body: CampaignUpdateBodySchema,
        responses: {
            200: CampaignDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kampány szerkesztése (a teljes kampány-mag; a kód és a státusz nem írható). OCC: `If-Match` a sor verziójával — hiányzik → 428, elavult → 409. Jog: campaign_forecast.manage.',
    },
    remove: {
        method: 'DELETE',
        path: '/campaign-forecast/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kampány törlése (SOFT — a busz `deleted_at`-je; a kód így újra kiadhatóvá válik). Jog: campaign_forecast.delete. A path-`:id` plain-UUID (traversal → 400).',
    },
});
const productUpdatesContract = c.router({
    list: {
        method: 'GET',
        path: '/product-updates',
        query: ProductUpdateListQuerySchema,
        responses: {
            200: ProductUpdateListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Fejlesztési tételek listája (belső). A `tab` a KÉT NÉZET: `board` = a folyamat (minden nem-`shipped` tétel + a 30 napnál nem régebbi kiadások), `timeline` = a teljes kiadás-történet (`shipped`). Szűrés: szabad szöveg (cím/slug), `stage`, `type`, `status`, `visibility`. A rendezés nézetenként kötött (board: horizont+pozíció, idővonal: kiadás-dátum csökkenően), és a BOARD nem lapoz (egy lapban adja a teljes folyamatot). Jog: product_updates.view.',
    },
    detail: {
        method: 'GET',
        path: '/product-updates/:id',
        responses: {
            200: ProductUpdateDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy fejlesztési tétel részlete: a TÖRZS (szanitált HTML, a képek a szerkesztő stabil útján) + a hívó jogai (`canManage`/`canPublish`/`canDelete`). Jog: product_updates.view. A path-`:id` plain-UUID (traversal → 400).',
    },
    create: {
        method: 'POST',
        path: '/product-updates',
        body: ProductUpdateWriteBodySchema,
        responses: {
            201: ProductUpdateDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Fejlesztési tétel létrehozása. MINDIG `draft` + `internal` állapotban jön létre — a kihirdetés külön jog és külön végpont (`publish`). A `slug` hiányában a szerver a magyar címből képzi (ütközésnél sorszámoz). `shipped` stage-nél a `shippedAt` KÖTELEZŐ, egyébként tilos (422). Jog: product_updates.manage.',
    },
    update: {
        method: 'PATCH',
        path: '/product-updates/:id',
        headers: ClientIfMatchHeaders,
        body: ProductUpdateWriteBodySchema,
        responses: {
            200: ProductUpdateDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Fejlesztési tétel szerkesztése (If-Match/OCC kötelező; hiányzó verzió → 428, ütközés → 409). A `status`/`visibility` ezen az úton NEM állítható — az a `publish`/`unpublish` végpont dolga. Jog: product_updates.manage.',
    },
    publish: {
        method: 'POST',
        path: '/product-updates/:id/publish',
        body: ProductUpdatePublishBodySchema,
        responses: {
            200: ProductUpdateDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Fejlesztési tétel KIHIRDETÉSE: `status=published` + az első publikálás időpontja, a body `visibility`-je szerint belsősnek (`internal`) vagy a partnereknek is (`public`). Jog: product_updates.publish (KÜLÖN a `manage`-től — a külsős kihirdetés más bizalmi szint). PUBLIKUS kihirdetéshez a LEÍRÁS kötelező (a partner-kártya azt mutatja) — enélkül 422.',
    },
    unpublish: {
        method: 'POST',
        path: '/product-updates/:id/unpublish',
        body: c.noBody(),
        responses: {
            200: ProductUpdateDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kihirdetés visszavonása: `status=draft` + `visibility=internal` (a partner-felületről azonnal eltűnik). A `publishedAt` MEGMARAD (történeti tény: egyszer ki volt hirdetve). Jog: product_updates.publish.',
    },
    remove: {
        method: 'DELETE',
        path: '/product-updates/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Fejlesztési tétel törlése (SOFT — a busz `deleted_at`-je). Jog: product_updates.delete. A path-`:id` plain-UUID (traversal → 400).',
    },
});
/**
 * KIADÁSOK — a PÉNZÜGY lap kiadás-oldala (`billing/expenses` + `billing/expense-categories`, INTERNAL).
 *
 * SAJÁT SUB-ROUTER: a route-ok egy `c.router({...})` objektum-literálban élnek, ami a ts-rest
 * kontextus-inferencia határán van — egy új, nyolc-végpontos blokk a lapos bővítésben elronthatná az ÖSSZES
 * handler tipizálását (mérve: `packages/contracts/CLAUDE.md`).
 *
 * JOG: olvasás a Kiadás jog (`finance.expenses.view`), írás/kategorizálás `finance.expenses.manage`, törlés
 * `finance.expenses.delete` (a Bevétel KÜLÖN jog; a kulcs-listák: `@atlas/shared` `FINANCE_PERMISSIONS`).
 */
const expensesContract = c.router({
    analytics: {
        method: 'GET',
        path: '/expenses/analytics',
        query: ExpenseAnalyticsQuerySchema,
        responses: {
            200: ExpenseAnalyticsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kiadás-oldal ELEMZÉSE a Pénzügy Összegzéshez: a cél-hónappal bezárólag vett 12 hónap forint-trendje (devizás rész külön), a főkategóriák 12 havi trendje, a cél-hónap 10 legnagyobb szállítója és a bér / egyéb bontás. Ugyanazon a szabályon, mint a lista összegzése (vonatkozási hónap, az elvetett sor kimarad). Szűrés (a trendre és a hónap-bontásokra egyaránt): szabad szöveg (szállító/adószám/számlaszám), típus (bér/egyéb), kategória-ág(ak) (`none` = besorolatlan), kibocsátó, forrás. Egyetlen busz-olvasás. Jog: finance.expenses.view.',
    },
    list: {
        method: 'GET',
        path: '/expenses',
        query: ExpenseListQuerySchema,
        responses: {
            200: z.object({
                rows: z.array(ExpenseRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                summary: ExpenseSummarySchema,
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy HÓNAP kiadásai a billing/expenses busz-erőforrásból, a kategória-név feloldva, plusz a hónap összegzése. A hónap-hovatartozást a VONATKOZÁSI hónap dönti el (kézi érték, annak híján a bizonylat hónapja: teljesítés, annak híján kiállítás) — ugyanaz a dimenzió, amin a lap összege és az eredménykimutatás áll. Szűrés: kategória (`none` = kategorizálatlan), kibocsátó, forrás (nav/manual), import-köteg, teendő-szűrők (munkatárs nélküli bér-tétel · pótolatlan összeg), elvetettek (alapból rejtve), szabad szöveg (szállító/adószám/számlaszám); rendezés + offset-lapozás. Az összegzés a hónap TELJES halmazán számol, nem a szűrt részén. Jog: finance.expenses.view.',
    },
    create: {
        method: 'POST',
        path: '/expenses',
        body: ExpenseWriteBodySchemaChecked,
        responses: {
            201: ExpenseRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'KÉZI kiadás felvétele (`source: manual`) — erre való a külföldi szállító, a nyugta és az adószám nélküli számla, ami a NAV-adatszolgáltatásban SOHA nem jelenik meg. Ütköző (adószám + számlaszám) → 409. Jog: finance.expenses.manage.',
    },
    update: {
        method: 'PUT',
        path: '/expenses/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: ExpenseWriteBodySchemaChecked,
        responses: {
            200: ExpenseRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'KÉZI kiadás szerkesztése (OCC `If-Match`; hiányzó → 428). NAV-forrású soron 422: az összeg, a szállító és a számlaszám az adóhatóság rekordja — azon csak a besorolás, az elvetés és a hiányzó összeg pótlása megy. Elvetett soron is 422. Jog: finance.expenses.manage.',
    },
    classify: {
        method: 'PUT',
        path: '/expenses/:id/classify',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: ExpenseClassifyBodySchema,
        responses: {
            200: ExpenseRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'KATEGORIZÁLÁS (+ megjegyzés, vonatkozási hónap, munkatárs) — a bizonylat mezőihez nem nyúl, ezért NAV-forrású soron is engedélyezett (az elvetés és a hiányzó összeg pótlása mellett). `categoryId: null` → vissza a „kategorizálatlan" közé; a hiányzó `accountingMonth`/`employeeIdentityId` a meglévő értéket hagyja. Nem létező kategória → 422. Jog: finance.expenses.manage.',
    },
    amount: {
        method: 'PUT',
        path: '/expenses/:id/amount',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: ExpenseAmountBodySchemaChecked,
        responses: {
            200: ExpenseRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'HIÁNYZÓ ÖSSZEG PÓTLÁSA a NAV-soron — a besorolás/elvetés melletti HARMADIK, szűk írás-út. Kizárólag ott, ahol a NAV digestje nem közölt összeget (EGYSZERŰSÍTETT számla): `source=nav` ÉS `amountUnknown`; minden más soron 422. A NAV-fal NEM lazul: a bizonylat többi mezőjéhez ez sem nyúl. `net + áfa === bruttó` szerződés-fal. Jog: finance.expenses.manage.',
    },
    bulkClassify: {
        method: 'POST',
        path: '/expenses/bulk-classify',
        body: ExpenseBulkClassifyBodySchema,
        responses: {
            200: ExpenseBulkClassifyResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'TÖMEGES kategorizálás, hónap-átsorolás és/vagy munkatárs-beállítás (max 100 sor; ha egyiket sem kéri → 422). A fej-verziót SORONKÉNT a szerver olvassa (a listának nincs friss verziója minden kijelölt sorra) — az OCC nem tűnik el, csak a verzió forrása a friss olvasás. Egy sor hibája nem állítja meg a többit: a válasz sor-szintű. Jog: finance.expenses.manage.',
    },
    remove: {
        method: 'DELETE',
        path: '/expenses/:id',
        body: c.noBody(),
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: z.object({ deleted: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kiadás törlése (soft delete). NAV-forrású soron 422: az adóhatóság rekordját nem töröljük — ami tévedésből került be, azt a NAV oldalán javítják, és a módosító bizonylat jön le. Jog: finance.expenses.delete.',
    },
    importTemplate: {
        method: 'GET',
        path: '/expenses/import/template',
        responses: {
            200: ExpenseImportTemplateSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A SABLON kitöltést segítő készlete ÉLŐ adatból: a választható kategória-UTAK (csak levél), a bér-típusú utak, a munkatárs-nevek és a kibocsátók. A sablont a böngésző állítja elő ebből — a kategória-út hosszú és elgépelhető, ezért nem gépelni kell, hanem választani. Jog: finance.expenses.manage.',
    },
    importPreview: {
        method: 'POST',
        path: '/expenses/import/preview',
        body: ExpenseImportPreviewBodySchema,
        responses: {
            200: ExpenseImportPreviewSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'ELŐNÉZET: a nyers sorokból feloldott kiadás-body-k, sor-szintű hibákkal és figyelmeztetésekkel. SEMMIT NEM ÍR. A feloldás (kategória-út → id, munkatárs-név → identity) és MINDEN szabály a szerveré — a felület csak megjeleníti és javíttatja. Jog: finance.expenses.manage.',
    },
    importCommit: {
        method: 'POST',
        path: '/expenses/import/commit',
        body: ExpenseImportCommitBodySchema,
        responses: {
            200: ExpenseImportResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'VÉGLEGESÍTÉS: a (szükség esetén javított) sorok beírása egy KÖTEGKÉNT. A kliens adta `batchId` egyben idempotencia-kulcs — ha ezzel már írtunk sort, 409 (a busz az INTERNAL típuson nem fogad Idempotency-Key-t, tehát platform-szintű dedup nincs). A blokkoló szabályokat a szerver ÚJRA ellenőrzi, nem bízik az előnézetben. Jog: finance.expenses.manage.',
    },
    importBatches: {
        method: 'GET',
        path: '/expenses/import/batches',
        responses: {
            200: z.object({ batches: z.array(ExpenseImportBatchSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A LEGUTÓBBI IMPORT-KÖTEGEK (max 10), a még élő sorszámmal és az érintett hónapokkal — innen érhető el a visszavonás és a köteg megnézése akkor is, ha az import ablaka már bezárult. Jog: finance.expenses.view.',
    },
    importUndo: {
        method: 'DELETE',
        path: '/expenses/import/batches/:batchId',
        body: c.noBody(),
        pathParams: z.object({ batchId: z.string().uuid() }),
        responses: {
            200: z.object({ deleted: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'EGY KÖTEG VISSZAVONÁSA: a köteg élő sorainak soft delete-je. A duplikációt nem előzzük meg (user-döntés) — ez a válasz rá: egy rossz fájl egy kattintással kivezethető. Jog: finance.expenses.delete.',
    },
    categories: {
        method: 'GET',
        path: '/expense-categories',
        responses: {
            200: z.object({ rows: z.array(ExpenseCategoryRowSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kiadás-kategória FA (főkategória → alkategória → tétel), a hivatkozó kiadások darabszámával. A select és a kategória-kezelő panel ebből tölt. Jog: finance.expenses.view.',
    },
    categoryCreate: {
        method: 'POST',
        path: '/expense-categories',
        body: ExpenseCategoryWriteBodySchema,
        responses: {
            201: ExpenseCategoryRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória felvétele. A fa LEGFELJEBB HÁROM szintű (főkategória → alkategória → tétel): negyedik szint 422. Jog: finance.expenses.manage.',
    },
    categoryUpdate: {
        method: 'PUT',
        path: '/expense-categories/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: ExpenseCategoryWriteBodySchema,
        responses: {
            200: ExpenseCategoryRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória átnevezése / átmozgatása (OCC `If-Match`). Jog: finance.expenses.manage.',
    },
    categoryRemove: {
        method: 'DELETE',
        path: '/expense-categories/:id',
        body: c.noBody(),
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: z.object({ deleted: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória törlése. HASZNÁLATBAN lévő kategória (van rá hivatkozó kiadás vagy alkategória) → 422: a törlés némán „kategorizálatlanná" tenné a rá mutató sorokat, és az elvégzett besorolás-munka veszne el. Jog: finance.expenses.delete.',
    },
    dismiss: {
        method: 'PUT',
        path: '/expenses/:id/dismiss',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: ExpenseDismissBodySchema,
        responses: {
            200: ExpenseRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'ELVETÉS / visszavétel (OCC `If-Match`). Az elvetett sor kiesik a listából és a havi összegből, de MEGMARAD — törölni nem lehetne, mert a következő NAV-import visszahozná. NAV-forrású soron is megy (a kategorizálás mellett ez a másik engedett írás-út). Jog: finance.expenses.manage.',
    },
    navStatus: {
        method: 'GET',
        path: '/expenses/nav-status',
        responses: {
            200: NavStatusSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A NAV-import állapota: be van-e kötve, melyik környezet, mikor érkezett a legutóbb importált bizonylat, és mikor jár le az aláírókulcs (a lejárat a NAV API-n NEM kérdezhető le — az üzemeltető adja meg). Jog: finance.expenses.view.',
    },
    navSync: {
        method: 'POST',
        path: '/expenses/nav-sync',
        body: NavSyncBodySchema,
        responses: {
            200: NavImportReportSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A bejövő NAV-számlák lekérése MOST. Tartomány nélkül INKREMENTÁLIS (cégenként a legutóbb importált bizonylat beérkezés-idejétől), `from`/`to` megadásával (budapesti naphatárokon) visszamenőleges pótlás. A lekérés a NAV korlátaihoz igazodik (35 napos ablakok + lapozás), és CSAK beszúr: a már nálunk lévő bizonylatot a szállító adószáma + számlaszám párja szűri. Konfiguráció nélkül 503 (hálózatot sem hív). Jog: finance.expenses.manage.',
    },
});
/**
 * ESZKÖZ-KATALÓGUS (Ilcsa #113) — a szerkeszthető kategóriák és típusok. SAJÁT SUB-ROUTER (a fő router lapos
 * bővítése a ts-rest inferencia határán van — lásd a fájl fejlécét).
 */
const assetCatalogContract = c.router({
    catalog: {
        method: 'GET',
        path: '/asset-catalog',
        responses: { 200: AssetCatalogSchema, 401: ErrorSchema, 403: ErrorSchema, 502: ErrorSchema },
        summary: 'Az eszköz-kategóriák a típusaikkal (sorrendben), típusonként a hivatkozó eszközök/igények számával. Az űrlapok és a szűrők selectjei és a „Kategóriák" kezelő ebből tölt. Jog: asset_inventory.view.',
    },
    categoryCreate: {
        method: 'POST',
        path: '/asset-categories',
        body: AssetCategoryWriteBodySchema,
        responses: {
            201: AssetCategoryRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória felvétele (név, leltári szám előtag, szoftver-jelleg). Foglalt előtag → 422 a mezőn. Jog: assets.manage.',
    },
    categoryUpdate: {
        method: 'PUT',
        path: '/asset-categories/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: AssetCategoryWriteBodySchema,
        responses: {
            200: AssetCategoryRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória módosítása (OCC `If-Match`). Az előtag változása csak az ÚJ leltári számokra hat. Jog: assets.manage.',
    },
    categoryRemove: {
        method: 'DELETE',
        path: '/asset-categories/:id',
        body: c.noBody(),
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: z.object({ deleted: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kategória törlése — ha van típusa, 422 (előbb a típusokat kell törölni). Jog: assets.manage.',
    },
    typeCreate: {
        method: 'POST',
        path: '/asset-types',
        body: AssetTypeWriteBodySchema,
        responses: {
            201: AssetTypeRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Típus felvétele egy kategóriába (a kulcsot a szerver képzi a névből). Jog: assets.manage.',
    },
    typeUpdate: {
        method: 'PUT',
        path: '/asset-types/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: AssetTypeWriteBodySchema,
        responses: {
            200: AssetTypeRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Típus átnevezése / áthelyezése másik kategóriába (OCC). Használatban lévő típus nem helyezhető át (422) — az eszközök kategóriája elcsúszna. Jog: assets.manage.',
    },
    typeRemove: {
        method: 'DELETE',
        path: '/asset-types/:id',
        body: c.noBody(),
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: z.object({ deleted: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Típus törlése — ha BÁRMELY eszköz vagy igény (bármely státuszban) hivatkozik rá, 422. Jog: assets.manage.',
    },
});
/**
 * ESZKÖZ-IGÉNY egyedi olvasása és szerkesztése (Ilcsa #116, #119) — SAJÁT SUB-ROUTER (lásd fent).
 */
const assetRequestsContract = c.router({
    get: {
        method: 'GET',
        path: '/assets/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: AssetRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy eszköz / igény (a levél és a harang mélylinkje ezt nyitja — a lista betöltött lapjától függetlenül). Jog: asset_inventory.view.',
    },
    update: {
        method: 'PATCH',
        path: '/assets/:id/request',
        pathParams: z.object({ id: z.string().uuid() }),
        headers: z.object({ 'if-match': z.string().optional() }),
        body: AssetRequestUpdateBodySchema,
        responses: {
            200: AssetRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Igény szerkesztése (OCC). Az igénylő a SAJÁT igényét a döntésig, a bíráló (asset_requests.decide) a beérkezésig szerkesztheti; más esetben 403, lezárt / beérkezett igénynél 409. Bírálói módosításról az igénylő értesítést kap.',
    },
});
/**
 * Felhasználói beállítások (`/preferences/*`) — SAJÁT sub-router (a lapos bővítés a ts-rest típus-inferenciát törné).
 * Az Odin `/iam/me/preferences/:key` proxyja a hívó tokenjével; az app a token `aud`-jából jön, más felhasználóét
 * nem lehet címezni (nincs `:id`). Írás/törlés `If-Match` (OCC) nélkül 428; eltérő verzió 409; megszemélyesítés alatt
 * az Odin az írást tiltja (403). Az érték sémáját a BFF validálja (`@atlas/contracts` `preferences/schemas`).
 */
const preferencesContract = c.router({
    dashboardGet: {
        method: 'GET',
        path: '/preferences/dashboard',
        responses: {
            200: DashboardLayoutEnvelopeSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kezdőlap mentett elrendezése. Nincs mentett → 200 `{ value: null, version: 0 }` (a normál állapot, nem 404): ilyenkor az alapértelmezés érvényes.',
    },
    dashboardPut: {
        method: 'PUT',
        path: '/preferences/dashboard',
        headers: PreferenceIfMatchHeaders,
        body: DashboardLayoutSchema,
        responses: {
            200: DashboardLayoutEnvelopeSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            413: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            429: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kezdőlap-elrendezés mentése. `If-Match: <version>` KÖTELEZŐ (új sorhoz 0; hiányában 428, eltérésnél 409). A BFF a katalógus szerint validál (ismert id, nincs duplikátum, engedett méret) — a jogot NEM nézi. Megszemélyesítés alatt tiltott (403).',
    },
    dashboardReset: {
        method: 'DELETE',
        path: '/preferences/dashboard',
        headers: PreferenceIfMatchHeaders,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            429: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az elrendezés visszaállítása: a mentett kulcs törlése („sosem szabott" állapot — az új alap-csempéket is megkapja). `If-Match` kötelező; ha nincs mit törölni → 204 (idempotens).',
    },
    notesGet: {
        method: 'GET',
        path: '/preferences/notes',
        responses: {
            200: NotesPrefEnvelopeSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A személyes jegyzet (csak szöveg, ≤4000 karakter). Nincs mentett → `{ value: null, version: 0 }`.',
    },
    notesPut: {
        method: 'PUT',
        path: '/preferences/notes',
        headers: PreferenceIfMatchHeaders,
        body: NotesPrefSchema,
        responses: {
            200: NotesPrefEnvelopeSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            413: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            429: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A személyes jegyzet mentése (autosave). `If-Match` kötelező (428/409 mint az elrendezésnél).',
    },
});
export const atlasContract = c.router({
    portal: portalContract,
    preferences: preferencesContract,
    /**
     * Globális kereszt-entitás kereső a command palette-hez. A user tokenjével (on-behalf → RLS + jog-szűrt:
     * mindenki csak azt találja, amit láthat) párhuzamosan lekérdezi az összes olyan entitás-típust, amelyre
     * `.view` joga van, és típusonként a top-N találatot adja lapos listában. `AuthGuard` (bejelentkezés kell);
     * a per-típus jog-gate a service-ben (nincs egyetlen közös jog). Best-effort: egy típus hibája/403-a a
     * többit nem dönti meg. Üres/túl rövid `q` → üres eredmény.
     */
    globalSearch: {
        method: 'GET',
        path: '/search',
        query: GlobalSearchQuerySchema,
        responses: {
            200: GlobalSearchResponseSchema,
            401: ErrorSchema,
        },
        summary: 'Globális kereszt-entitás kereső (command palette): a user tokenjével párhuzamosan, per-típus .view-jog szerint szűrve, típusonként top-N találat {type,id,label,sublabel} lapos listában. Best-effort (egy típus hibája nem dönti meg a keresést).',
    },
    login: {
        method: 'POST',
        path: '/auth/login',
        body: LoginBodySchema,
        responses: {
            200: z.object({ accessToken: z.string() }),
            401: LoginErrorSchema,
            // A 401 KIZÁRÓLAG hitelesítési VERDIKT (rossz jelszó / 2FA). Ami NEM verdikt, az nem lehet 401:
            //  • 429 — az Odin login-throttle-je. Korábban ez is „hibás jelszó"-ként jött vissza, amitől a
            //    felhasználó tovább próbálkozott, és ezzel MÉG JOBBAN kizárta magát.
            //  • 503 — az auth-szolgáltatás (Odin) elérhetetlen / hibázik. Ez INFRASTRUKTÚRA-hiba, nem a jelszóé.
            //  • 403 — a HITELESÍTÉS SIKERÜLT (a jelszó jó), de a személynek nincs Atlas-hozzáférése (nem tagja az
            //    appnak — pl. frissen felvett munkatárs, akinek a Jogosultságok-oldal még nem adott szerepet).
            //    NEM enumeráció: az Odin a jelszó-ellenőrzés UTÁN dönti (rossz jelszó → 401, nem jutunk ide).
            //    Korábban ez 503-ra esett („átmenetileg nem elérhető"), ami félrevezette a felhasználót ÉS az admint.
            403: ErrorSchema,
            429: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Bejelentkezés — az Odin iam login proxyja; access-tokent ad vissza. 2FA-s usernél az mfaCode-ot továbbítja; a 401 `code` (mfa_required/mfa_invalid) jelzi a frontendnek a kód-promptot. A 401 CSAK hitelesítési verdikt: a rate-limit 429, a hozzáférés-hiány (nem app-tag) 403, az elérhetetlen auth-szolgáltatás 503.',
    },
    refresh: {
        method: 'POST',
        path: '/auth/refresh',
        body: z.object({}).optional(),
        responses: {
            200: z.object({ accessToken: z.string() }),
            401: ErrorSchema,
            /** Az auth-szolgáltatás elérhetetlen — a refresh-cookie ilyenkor NEM törlődik (lásd a controllerben). */
            503: ErrorSchema,
        },
        summary: 'Access-token megújítás a httpOnly refresh-cookie-ból (BFF) — a cookie-t rotálja; érvénytelen/hiányzó cookie → 401. Ha az Odin elérhetetlen: 503, és a cookie MEGMARAD (egy átmeneti hiba ne léptesse ki a felhasználót).',
    },
    logout: {
        method: 'POST',
        path: '/auth/logout',
        body: z.object({}).optional(),
        responses: {
            200: z.object({ ok: z.boolean() }),
        },
        summary: 'Kijelentkezés — a refresh-token visszavonása az Odin iam-ben + a cookie törlése.',
    },
    /**
     * MEGSZEMÉLYESÍTÉS (impersonation) indítása — „belépés más nevében".
     *
     * JOG (`docs/foundations/auth.md` §11): a hívó SAJÁT tokenjének `iam:impersonate` scope-ja — a
     * jogosultság-konzolban ez a „Megszemélyesítés (belépés más nevében)" pipa. A BFF is ellenőrzi (beszédes
     * 403), az érvényesítés az Odinban van, a cél-oldali eszkaláció-fallal együtt (emelt jogú, gépi, inaktív
     * vagy nem-app-tag célt SOHA). A válasz a rövid életű megszemélyesítő access-token — refresh NEM jár
     * hozzá, tehát a művelet magától befejeződik.
     */
    impersonationStart: {
        method: 'POST',
        path: '/auth/impersonation/start',
        body: ImpersonationStartBodySchema,
        responses: {
            200: ImpersonationStartResponseSchema,
            401: ErrorSchema,
            // 403: nincs feature-jog VAGY nincs `iam:impersonate` scope VAGY a cél emelt jogú/gépi (Odin-fal).
            403: ErrorSchema,
            404: ErrorSchema,
            // 422: érvénytelen cél (önmaga · inaktív · nem tagja az appnak).
            422: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Megszemélyesítés indítása: rövid életű access-token a cél identity jogképével (audit: a VALÓDI aktorral).',
    },
    /**
     * A megszemélyesítés BEFEJEZÉSE: audit-nyom az Odinban + az AKTOR friss access-tokenje a saját
     * refresh-cookie-jából. A hívást a MEGSZEMÉLYESÍTŐ tokennel kell indítani (abból derül ki, ki kit hagy el);
     * ha nem az, a művelet üres kézzel, de hibátlanul tér vissza (idempotens „vissza" gomb).
     */
    impersonationStop: {
        method: 'POST',
        path: '/auth/impersonation/stop',
        body: c.noBody(),
        responses: {
            200: ImpersonationStopResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Megszemélyesítés befejezése — audit + az aktor friss tokenje (a refresh-cookie-ból).',
    },
    session: {
        method: 'GET',
        path: '/session',
        responses: {
            200: SessionSchema,
            401: ErrorSchema,
        },
        summary: 'A bejelentkezett identity (a JWKS-validált token claimjei + best-effort avatar/csapatszín a profilból, megszemélyesítéskor az aktor név-részei a címtárból).',
    },
    // ── Jelszó-visszaállítás (PUBLIKUS, `AuthGuard` NÉLKÜL) — az Odin iam/password-reset primitív proxyja ──
    //
    // A KÉT végpont tudatosan aszimmetrikus: a kibocsátás GÉPI (a BFF a dedikált service-identitásával kéri a
    // tokent, a böngésző SOSEM lát tokent — azt a felhasználó a LEVÉLBŐL kapja), a beváltás pedig hitelesítés
    // nélküli (a token maga a bizonyíték: a user épp azért van itt, mert nem tud belépni).
    // Mindkettő a szigorú `login` kérés-limit-tierbe esik (brute-force / enumerációs felület).
    forgotPassword: {
        method: 'POST',
        path: '/auth/forgot-password',
        body: ForgotPasswordBodySchema,
        responses: {
            // 202 = „átvettük" — SOHA nem árulja el, van-e ilyen fiók (az Odin `issued:false` ága is ezt kapja).
            202: ForgotPasswordResponseSchema,
            429: ErrorSchema,
            // 503 KIZÁRÓLAG üzem-zavarra (nincs gépi kredenciál / az Odin elérhetetlen). Ez NEM fiók-információ,
            // tehát nem enumerációs csatorna — és a néma 202 ilyenkor hazugság lenne (levél sosem érkezne).
            503: ErrorSchema,
        },
        summary: 'Jelszó-visszaállító levél kérése (PUBLIKUS). A BFF a dedikált gépi identitásával tokent kér az Odintól (POST /iam/password-reset/tokens, kind=reset), majd a `identity.password_reset` TRANZAKCIÓS levelet küldi. A válasz létező és nem létező fiókra AZONOS (202, generikus üzenet) — anti-enumeráció; a token a böngészőhöz SOHA nem kerül. 503 csak üzem-zavarra.',
    },
    resetPassword: {
        method: 'POST',
        path: '/auth/reset-password',
        body: ResetPasswordBodySchema,
        responses: {
            200: z.object({ ok: z.literal(true) }),
            // GENERIKUS: „nincs ilyen" / „lejárt" / „már felhasználták" / „felülírta egy újabb link" / „a fiók
            // közben inaktív" MIND ez. Az Odin sem bontja — a különbség-tétel oracle lenne a link-életciklusra.
            400: ErrorSchema,
            // Jelszó-policy → mezőnkénti GÉPI KÓDOK az `errors`-ban (pl. `{ newPassword: ['too_short'] }`), amiket a
            // UI fordít. A token NEM ég el: a felhasználó javíthat és ugyanazzal a linkkel újrapróbálhat.
            422: ValidationErrorSchema,
            429: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Új jelszó beállítása a levélből kapott linkkel (PUBLIKUS — a token a bizonyíték; az Odin POST /iam/password-reset/redeem proxyja). Siker: Argon2id hash + a felhasználó ÖSSZES refresh-tokenje visszavonva (session-invalidálás) → a UI a loginra irányít. 400 GENERIKUS, 422 mezőhöz kötött policy-kódok.',
    },
    // ── Értesítés-feed („harang") — az Odin /v1/notify/notifications proxyja a user tokenjével ──
    // A feed a SAJÁT értesítéseket adja (Odin-oldali RLS: címzett + aud-app scope) — nincs külön jog-gate,
    // az `AuthGuard` a kapu. A megjelenített SZÖVEGET a web rendereli a `type` + `data` párból (i18n), ezért
    // a sor nem hordoz kész címet/törzset — így a nyelvváltás a MÁR kiküldött értesítésekre is visszamenőleg hat.
    notifications: {
        method: 'GET',
        path: '/notifications',
        query: NotificationListQuerySchema,
        responses: {
            200: NotificationListResponseSchema,
            401: ErrorSchema,
            // A summary MÁR kimondta, hogy upstream-hibánál 502 megy — a `responses` viszont nem hirdette.
            502: ErrorSchema,
        },
        summary: 'A bejelentkezett user SAJÁT értesítés-feedje (az Odin GET /v1/notify/notifications proxyja). `unreadOnly`/`type`/`cursor`/`limit` szűrők; a válasz `unreadCount`-ot is ad a harang-badge-hez (plafon 100 = „99+"). Upstream-hiba → 502 (NEM üres feed 200-cal: a „nincs értesítésed" hazugság lenne).',
    },
    notificationMarkRead: {
        method: 'POST',
        path: '/notifications/:id/read',
        body: z.object({}).optional(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'EGY értesítés olvasottnak jelölése (az Odin POST /v1/notify/notifications/:id/read proxyja). Idempotens: a már olvasott sor is 200.',
    },
    notificationsReadAll: {
        method: 'POST',
        path: '/notifications/read-all',
        body: z.object({ type: z.string().max(200).optional() }).optional(),
        responses: {
            200: z.object({ ok: z.boolean(), updated: z.number() }),
            401: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'MINDEN (vagy egy típusra szűrt) értesítés olvasottnak jelölése (az Odin POST /v1/notify/notifications/read-all proxyja). Upstream-hiba → 502 (nincs hamis siker: a UI visszagörgeti az optimista frissítést).',
    },
    // ── Profil (self-service) — az Odin /iam/me/* proxyja a user SAJÁT tokenjével (on-behalf, self-scope) ──
    profile: {
        method: 'GET',
        path: '/profile',
        responses: {
            200: ProfileSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'A bejelentkezett user SAJÁT profilja (az Odin GET /iam/me/profile proxyja a user tokenjével). Kiegészíti a /session claim-adatot — külön, gazdagabb self-profil erőforrás.',
    },
    profileUpdate: {
        method: 'PATCH',
        path: '/profile',
        body: ProfileUpdateBodySchema,
        responses: {
            200: ProfileSchema,
            400: ErrorSchema, // hiányzó/elavult OCC-verzió (meta)
            401: ErrorSchema,
            403: ErrorSchema, // hibás/hiányzó step-up jelszó (e-mail-váltás) VAGY jelszó-mentes identity
            409: ErrorSchema, // foglalt e-mail / discord
            422: ValidationErrorSchema, // formátum-hiba
        },
        summary: 'A saját profil frissítése. E-mail-VÁLTOZÁSNÁL a currentPassword (step-up) + meta.identityVersion (OCC) kötelező. Az Odin PATCH /iam/me/profile proxyja.',
    },
    profileSetAsanaPat: {
        method: 'PUT',
        path: '/profile/asana-pat',
        body: AsanaPatBodySchema,
        responses: {
            200: AsanaPatStateSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'A saját Asana PAT beállítása (write-only — a plaintext sosem jön vissza). Az Odin PUT /iam/me/asana-pat proxyja.',
    },
    profileClearAsanaPat: {
        method: 'DELETE',
        path: '/profile/asana-pat',
        body: c.noBody(),
        responses: {
            200: AsanaPatStateSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'A saját Asana PAT törlése. Az Odin DELETE /iam/me/asana-pat proxyja.',
    },
    profilePersonal: {
        method: 'GET',
        path: '/profile/personal',
        responses: {
            200: ProfilePersonalSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A saját személyes adatok (születésnap, névnap, gyerekek) a belső Profilom „Egyéb adatok" részéhez. CSAK belső munkatárs (partner → 403; HR-sor nélkül 404). Az Odin GET /iam/me/personal proxyja.',
    },
    profilePersonalUpdate: {
        method: 'PATCH',
        path: '/profile/personal',
        body: ProfilePersonalUpdateBodySchema,
        responses: {
            200: ProfilePersonalSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A saját személyes adatok írása (parciális; `version` = OCC). CSAK belső munkatárs; a belépés/kilépés napja itt NEM írható (azt a HR). Megszemélyesítés alatt tiltott. Az Odin PATCH /iam/me/personal proxyja.',
    },
    profileChangePassword: {
        method: 'POST',
        path: '/profile/password',
        body: PasswordChangeBodySchema,
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema, // hibás jelenlegi jelszó VAGY jelszó-mentes identity
            422: PasswordPolicyErrorSchema, // policy-sértés (gépi kódok a UI-nak)
            429: ErrorSchema, // rate-limit
        },
        summary: 'A saját jelszó módosítása (policy: min. 12 kar., kis+nagy+szám). Siker → az Odin MINDEN refresh-tokent visszavon (a user más eszközökön kiléptetődik). Az Odin POST /iam/me/password proxyja.',
    },
    profileTwoFactorSetup: {
        method: 'POST',
        path: '/profile/2fa/setup',
        body: z.object({}).optional(),
        responses: {
            200: TwoFactorSetupSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema, // már aktív
        },
        summary: '2FA (TOTP) beállítás indítása — secret + otpauth URI a QR-kódhoz. 409 ha már aktív. Az Odin POST /iam/me/2fa/setup proxyja.',
    },
    profileTwoFactorEnable: {
        method: 'POST',
        path: '/profile/2fa/enable',
        body: TwoFactorEnableBodySchema,
        responses: {
            200: RecoveryCodesSchema,
            400: ErrorSchema, // nincs folyamatban lévő beállítás
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema, // már aktív
            422: ErrorSchema, // rossz kód
        },
        summary: '2FA aktiválása a beírt TOTP-kóddal → 10 egyszeri recovery-kód (csak most jelenik meg). Az Odin POST /iam/me/2fa/enable proxyja.',
    },
    profileTwoFactorDisable: {
        method: 'POST',
        path: '/profile/2fa/disable',
        body: TwoFactorStepUpBodySchema,
        responses: {
            200: z.object({ mfaEnabled: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema, // step-up nélkül
        },
        summary: '2FA kikapcsolása step-up-pal (TOTP/recovery kód VAGY jelenlegi jelszó). Az Odin POST /iam/me/2fa/disable proxyja.',
    },
    profileTwoFactorRecoveryCodes: {
        method: 'POST',
        path: '/profile/2fa/recovery-codes',
        body: TwoFactorStepUpBodySchema,
        responses: {
            200: RecoveryCodesSchema,
            401: ErrorSchema,
            403: ErrorSchema, // step-up nélkül
        },
        summary: 'Új 2FA recovery-kódok generálása step-up-pal (a régiek érvénytelenednek). Az Odin POST /iam/me/2fa/recovery-codes proxyja.',
    },
    profileAvatarUploadUrl: {
        method: 'POST',
        path: '/profile/avatar/upload-url',
        body: AvatarUploadUrlBodySchema,
        responses: {
            200: AvatarUploadUrlSchema,
            401: ErrorSchema,
            403: ErrorSchema, // inaktív identity
            422: ValidationErrorSchema, // rossz típus / méret (≤0 vagy > 2 MiB)
            429: ErrorSchema, // >20/15perc egy usertől
        },
        summary: 'Presigned PUT-URL a SAJÁT avatarhoz (a bájtok KÖZVETLENÜL a tárba mennek, Odinon SOSE át). A típus + méret az aláírásba kötve. Az Odin POST /iam/me/avatar/upload-url proxyja.',
    },
    profileAvatarCommit: {
        method: 'POST',
        path: '/profile/avatar/commit',
        body: z.object({}).optional(),
        responses: {
            200: AvatarCommitSchema,
            401: ErrorSchema,
            403: ErrorSchema, // inaktív identity
            422: ErrorSchema, // nincs feltöltött bájt / érvénytelen tárolt objektum
        },
        summary: 'Az avatar-feltöltés véglegesítése → friss presigned GET-URL (avatarUrl). Az Odin POST /iam/me/avatar/commit proxyja.',
    },
    profileAvatarDelete: {
        method: 'DELETE',
        path: '/profile/avatar',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema, // inaktív identity
        },
        summary: 'A SAJÁT avatar törlése (idempotens). Az Odin DELETE /iam/me/avatar proxyja.',
    },
    demoItems: {
        method: 'GET',
        path: '/demo/items',
        responses: {
            200: z.object({ items: z.array(OdinResourceSchema), nextCursor: z.string().nullable() }),
            401: ErrorSchema,
        },
        summary: 'Walking skeleton tracer — a demo/widgets erőforrások listája az Odin resource buszról.',
    },
    clients: clientsContract,
    accounts: {
        method: 'GET',
        path: '/accounts',
        query: AccountListQuerySchema,
        responses: {
            200: z.object({
                accounts: z.array(AccountRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /** A szolgáltatás-facet választható opciói (distinct id+név a teljes — facet-szűrt — listából, névre rendezve). */
                serviceOptions: z.array(z.object({ id: z.string(), name: z.string() })),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // 502: átmeneti upstream-hiba (Odin 429/5xx) — a felület „Újra"-t ajánl, NEM jog-hibát mutat.
            502: ErrorSchema,
        },
        summary: 'Account-lista a CRM adat-parity oszlopaival (clients/account domain-resource) — kereső/szűrő/rendezés/lapozás.',
    },
    accountGet: {
        method: 'GET',
        path: '/accounts/:id',
        responses: { 200: AccountDetailSchema, 401: ErrorSchema, 403: ErrorSchema, 404: ErrorSchema },
        summary: 'Egy fiók detail-nézete: a lista-oszlopokkal AZONOS mezők (clients/account domain-resource + kötődés/szolgáltatás dúsítás) + az OCC-verzió (a szerkesztő If-Match forrása).',
    },
    accountUpdate: {
        method: 'PATCH',
        path: '/accounts/:id',
        headers: ClientIfMatchHeaders,
        body: AccountUpdateBodySchema,
        responses: {
            200: AccountDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Fiók frissítése (If-Match/OCC, kötelező fejléc) — a clients/account domain-resource NYERS data-írása az Odin buszra (a cél-tenant fejléccel). Jog-kapu: accounts.manage. A writable mezőket a Schema Registry account json_schema-ja határozza meg.',
    },
    accountDelete: {
        method: 'DELETE',
        path: '/accounts/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Fiók soft-delete KASZKÁDDAL (a cél-tenant fejléccel): ELŐBB a fiók összes partner-portál grantjának visszavonása (fail-closed — hibánál a fiók marad), majd a fiók hozzárendelései, kötései, domainjei és a hozzárendelés-cél-pivotok, utána a clients/account domain-resource törlése, végül a platform-root archiválása (DELETE /v1/admin/accounts/:id). Jog-kapu: accounts.delete. Kliens-oldali OCC-fejléc nincs (a resource-busz remove nem támogat If-Match-et; a root-archiválás If-Match-ét a BFF a root verziójából képzi); az izolációt az Odin RLS adja.',
    },
    accountCreate: {
        method: 'POST',
        path: '/accounts',
        headers: ClientIdempotencyHeaders,
        body: AccountCreateBodySchema,
        responses: {
            201: AccountCreateResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Fiók létrehozása (ATOMI, egy hívás): Odin foundation POST /v1/admin/accounts — a foundation-root (clientId+name, platform-status default active) ÉS a clients/account CRM-profil (a CRM-status + writable mezők, account_id szerver-oldalon kötve) EGY tranzakcióban. Idempotency-Key. Jog-kapu: accounts.manage.',
    },
    accountAffiliationUpsert: {
        method: 'POST',
        path: '/accounts/:id/affiliations',
        headers: ClientIdempotencyHeaders,
        body: AccountAffiliationUpsertBodySchema,
        responses: {
            200: AccountDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Fiók-oldali kötés (ügyféladminisztrátor/kapcsolattartó) UPSERT: egy LÉTEZŐ identity kötése a fiókhoz (kind=admin|contact) a clients/account-affiliations resource-on. A cél-tenant az :id fiókból SZERVER-oldalon feloldva (nem kliens-input). isPrimary → kind-on belüli kizárólagosság (a másik elsődleges demótálva, OCC) + a LEÍRÓ portalAccess jelző igazra áll (jogot nem ad — a hozzáférés az Odin portál-grantja). Meglévő (fiók,identity,kind) kötés → OCC-frissítés (nincs duplikátum). Válasz: a friss fiók-detail. Jog-kapu: a kind dönti — admin → admins.assign, contact → contacts.assign (a dekorátor a kettő VAGY-a, a pontos kapu a body után).',
    },
    accountAffiliationRemove: {
        method: 'DELETE',
        path: '/accounts/:id/affiliations/:kind/:identityId',
        body: c.noBody(),
        responses: {
            200: AccountDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Fiók-oldali kötés eltávolítása: a (fiók, identity, kind) account-affiliations sor soft-remove-ja az Odin buszra (a cél-tenant a fiókból feloldva). ELŐTTE a kontakt portál-grantja ezen a fiókon visszavonódik (fail-closed: ha a visszavonás bukik, a kötés marad), utána a fiók-szintű hozzárendelés-felelős pivotjai is lekerülnek. NEM offboardolja az identity-t (csak a kötést bontja). Válasz: a friss fiók-detail. Jog-kapu: a kind dönti — admin → admins.remove, contact → contacts.remove.',
    },
    /**
     * PARTNER-PORTÁL HOZZÁFÉRÉS a fiók-oldalon — a napi ügyfélkapcsolati munka helye (a jogosultság-oldal
     * identity-központú nézete a másik belépő). A sor-lista a fiók KÖTÉSEIBŐL indul, hogy azoknak is lehessen
     * hozzáférést adni, akiknek még nincs.
     *
     * A JOG KÉT SZINTEN áll: az Atlas feature-jog (`portal_access.view|manage`) a felületet nyitja, az Odin
     * `iam:portal-access` képesség-scope pedig a tényleges műveletet (a hívó SAJÁT tokenjével megy). Ezért a
     * `member` tier látja a hozzáféréseket, de nem oszthat (az Odin 403-mal zárná).
     */
    /**
     * KERESZT-FIÓKOS ÁTTEKINTŐ (a jogosultság-oldal partner-füléhez): az app ÖSSZES élő portál-hozzáférése,
     * fiók-névvel. Személy-központú nézet — de CSAK olvasás: a szerep beállítása a FIÓK-oldalon történik, mert a
     * jogkör a (személy, fiók) páré, és egy identity-központú szerkesztő ezt nem tudná kifejezni.
     */
    portalAccessOverview: {
        method: 'GET',
        path: '/portal-access',
        responses: {
            200: PortalAccessOverviewResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az app ÖSSZES élő partner-portál hozzáférése (identity + fiók + szerep), fiók-névvel dúsítva — a jogosultság-oldal partner-fülének read-only áttekintője. Jog-kapu: portal_access.view.',
    },
    portalAccessList: {
        method: 'GET',
        path: '/accounts/:id/portal-access',
        responses: {
            200: PortalAccessListResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A fiók kötött személyei (kapcsolattartók + ügyféladminisztrátorok) + a HATÁLYOS partner-portál szerepük (fiókonként eltérhet). A sorok a clients/account-affiliations kötésekből jönnek (azok is, akiknek MÉG nincs hozzáférésük: role=null); a szerep az Odin GET /v1/admin/portal-access?accountId=… válaszából. Jog-kapu: portal_access.view.',
    },
    portalAccessSet: {
        method: 'PUT',
        path: '/accounts/:id/portal-access/:identityId',
        body: PortalAccessSetBodySchema,
        responses: {
            200: PortalAccessRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Portál-szerep adása/váltása EGY fiókon (idempotens upsert): Odin POST /v1/admin/portal-access — app-tagság + access-grant + portál-szerep egy tranzakcióban, a hívó tokenjével (iam:portal-access). A cél KIZÁRÓLAG a fiók kapcsolattartója (partner identity). Jog-kapu: portal_access.manage.',
    },
    portalAccessRevoke: {
        method: 'DELETE',
        path: '/accounts/:id/portal-access/:identityId',
        body: c.noBody(),
        responses: {
            200: PortalAccessRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Portál-hozzáférés visszavonása EGY fiókon (idempotens): Odin DELETE /v1/admin/portal-access/:grantId. A kötés (kapcsolattartó marad a fiókon) és az identity ÉRINTETLEN — csak a portál-szerep + a hozzáférés szűnik meg. Jog-kapu: portal_access.manage.',
    },
    contacts: {
        method: 'GET',
        path: '/contacts',
        query: ContactListQuerySchema,
        responses: {
            200: z.object({
                contacts: z.array(ContactRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kapcsolattartó-lista: a kind=contact account-affiliation-ök, identity (név/e-mail) + fiók-adattal dúsítva, identityre aggregálva — név/e-mail keresés, név szerint rendezve, offset-lapozással.',
    },
    contactCreate: {
        method: 'POST',
        path: '/contacts',
        body: ContactCreateBodySchema,
        responses: {
            201: z.object({ identityId: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Új kapcsolattartó (partner-provisioning): e-mail-kulcsú identity-upsert az Odin admin-API-n (POST /v1/admin/identities, type=partner — nincs → létrejön, van → a profil-mezők PATCH-elődnek). Fiók-kötést NEM hoz létre: azt a fiók oldalán lehet megadni (accountAffiliationUpsert). Jog-kapu: contacts.manage.',
    },
    contactUpdate: {
        method: 'PATCH',
        path: '/contacts/:id',
        body: ContactUpdateBodySchema,
        responses: {
            200: z.object({ identityId: z.string() }),
            400: ErrorSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Kapcsolattartó szerkesztése: az identity-mag + partner-profil PATCH-e (PATCH /v1/admin/identities/:id — e-mail/nevek/telefon/beosztás/becenév; OCC nincs). Az :id a kontakt identity-je, és a PARTNER-populáció tagja kell legyen (különben 404 — a végpont nem írhat munkatársat). A fiók-kötés flag-jei a fiók oldalán szerkeszthetők. Jog-kapu: contacts.manage.',
    },
    contactDelete: {
        method: 'DELETE',
        path: '/contacts/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            400: ErrorSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Kapcsolattartó GLOBÁLIS offboardingja: ELŐBB a kontakt összes portál-grantjának visszavonása (fail-closed — hibánál semmi nem törlődik), majd az Odin DELETE /v1/admin/identities/:id (soft-delete, NEM hard-delete), utána best-effort a kontakt összes clients/account-affiliations kötésének soft-remove-ja a buszon. Csak partner identity a cél (más → 404); saját magát a hívó nem vezetheti ki (422). Jog-kapu: contacts.delete.',
    },
    contactRestore: {
        method: 'POST',
        path: '/contacts/:id/restore',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            400: ErrorSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            // A sor NEM kivezetett — a hívó elavult listát lát (két admin egyszerre). Megkülönböztetve a 404-től,
            // hogy a felület újratöltésre tudjon kérni.
            409: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Kivezetett kapcsolattartó VISSZAVÉTELE (re-hire): az Odin POST /v1/admin/identities/:id/restore. A személy visszakerül a címtárba, de JELSZÓ NÉLKÜL (a régi hash törlődik → a belépés a jelszó-beállító folyamaton megy), és sem app-tagságot, sem portál-hozzáférést, sem fiók-kötődést NEM kap vissza — azokat külön, auditáltan kell újra kiosztani. Jog-kapu: contacts.manage.',
    },
    contactSendWelcome: {
        method: 'POST',
        path: '/contacts/:id/welcome',
        body: c.noBody(),
        responses: {
            202: z.object({ accepted: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            // A cél NEM kaphat meghívót (nincs aktív app-tagsága · inaktív · governance-admin · service-fiók)
            // — az Odin `issued: false` ága. Generikus üzenet: az okot sem az Odin, sem mi nem bontjuk.
            409: ErrorSchema,
            429: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Üdvözlő levél küldése egy kapcsolattartónak (a legacy manuális „welcome" gombja). A BFF feloldja a kontakt e-mail-címét a HÍVÓ tokenjével, majd a dedikált gépi identitással `invite` (7 napos; ha a célnak már van jelszava, az Odin auditáltan 60 perces `reset`-re fokozza le) jelszó-beállító tokent kér az Odintól, és a `identity.welcome` TRANZAKCIÓS levelet küldi PARTNER hangnemben. Jelszó SOHA nem megy a levélben. Jog-kapu: contacts.manage.',
    },
    identities: {
        method: 'GET',
        path: '/identities',
        query: ListQuerySchema.extend({ type: z.string().optional() }),
        responses: {
            200: z.object({ identities: z.array(IdentitySchema), nextCursor: z.string().nullable() }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Felhasználók (identity) listája — típus (internal/partner/service) szerint szűrhető.',
    },
    employees: {
        method: 'GET',
        path: '/employees',
        // A rendezés-KULCS fixen a név — de a rendezés LOCALE-tudatos: `lang=hu` → VEZETÉKNÉV elöl (magyar
        // címtár-konvenció), `lang=en` → keresztnév elöl (a displayName „given family" sorrendjét követve). Csak az
        // irány (`order`) váltható. Offset-lapozás a végtelen görgetéshez (a belső létszám kicsi → gyakorlatban egy lap).
        query: z.object({
            q: z.string().optional(),
            order: z.enum(['asc', 'desc']).optional(),
            lang: z.enum(['hu', 'en']).optional(),
            /** App-scope a KÖZPONTI admin-konzolhoz: a kiválasztott app tagjainak címtára; üres → a saját app (atlas). */
            app: z.string().optional(),
            /**
             * ÉLETCIKLUS-SZŰRŐ — `active` (ALAP) · `archived` (kivezetettek) · `all`.
             *
             * SZERVER-OLDALI, mert a kivezetett személyt az upstream alapból vissza sem adja: kliens-oldali
             * facetből ez a halmaz SOHA nem állt elő (ezért látszott a szűrő „mind + aktív"-nak). Az alapértelmezés
             * `active` — a címtár a dolgozó kollégákat mutatja, a kivezetettek külön kérésre jönnek.
             */
            status: z.enum(['active', 'archived', 'all']).optional(),
            limit: z.coerce.number().int().positive().max(200).optional(),
            offset: z.coerce.number().int().nonnegative().optional(),
        }),
        responses: {
            200: z.object({
                employees: z.array(EmployeeRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Belső munkatársak címtára (identity type=internal) + best-effort munkaviszony- és support-csapat-dúsítás (szerep-badge NINCS: a szerep app-függő, a `roles` mindig üres) — név/email keresés, név szerint rendezve.',
    },
    employeeRefs: {
        method: 'GET',
        path: '/employees/refs',
        // Locale-tudatos opció-nevek (a lista `lang`-jával egyezően) — a pozíció/support-csapat i18n JSON-jából.
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: EmployeeRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Munkatárs-szerkesztő opció-listák: pozíciók (workforce/positions). Jog-kapu: employees.view.',
    },
    employeeUpdate: {
        method: 'PATCH',
        path: '/employees/:id',
        headers: ClientIfMatchHeaders,
        body: EmployeeUpdateBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            400: ErrorSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Munkatárs szerkesztése KÉT LÉPÉSBEN: (1) IDENTITY-CORE PATCH az Odin `PATCH /v1/admin/identities/:id`-jén (e-mail · givenName · familyName · displayName · nickname) — SZÁNDÉKOSAN ELŐSZÖR, mert az e-mail-ütközés így 409-ként a mezőn jelenik meg, mielőtt bármi más íródna; (2) munkaviszony-adat (workforce/employee-details PATCH If-Match/OCC: positionId/birthDate/employmentStartDate whitelist-merge — a `discordId` NEM ide tartozik: az azonosító SZEMÉLYHEZ kötött, egyetlen helye a `platform/identity-profiles`, és a workforce-oldali oszlop megszűnt). Az :id a MUNKATÁRS identity-je (nem a detail-sor PK-ja). Jog-kapu: employees.manage (+ Odin write:all:workforce). A support-csapat-tagsághoz ez az út NEM nyúl (a modul csak OLVAS csapatot a lista-dúsításhoz; a tagságok bontása az OFFBOARDING dolga).',
    },
    employeeCreate: {
        method: 'POST',
        path: '/employees',
        body: EmployeeCreateBodySchema,
        responses: {
            // `passwordSet`: a KEZDŐ JELSZÓ tényleg beíródott-e. Létező e-mail (match) esetén az Odin SZÁNDÉKOSAN nem
            // ír jelszót (fiók-átvétel elleni fal) → `false`, és a UI ezt jelzi (különben tévesen hinné az admin,
            // hogy az általa beírt jelszóval lép be a kolléga).
            201: z.object({ id: z.string(), passwordSet: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Új munkatárs (IdP-provisioning): e-mail-kulcsú identity-upsert az Odin admin-API-n (nincs → létrejön type=internal; van → a core-t nem érinti [A döntés]) + workforce/employee-details. Opcionális kezdő jelszó (csak ÚJ identityre íródik; policy-sértés → 422 mezőnkénti gépi kódokkal) — üresen hagyva a belépést a köszöntő-levél linkje adja. Már-munkatárs → 409. Jog-kapu: employees.manage (+ Odin iam:provision & write:all:workforce).',
    },
    employeeDelete: {
        method: 'DELETE',
        path: '/employees/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Munkatárs GLOBÁLIS offboardingja (IdP-modell): az Odin `DELETE /v1/admin/identities/:id` a személyt a teljes cégből kivezeti (soft-delete + minden app-tagság offboardolva), utána best-effort employee-details + support-tagság + ügyféladminisztrátor-kötés + hozzárendelés-felelős pivot takarítás; saját magát a hívó nem vezetheti ki (422). Jog-kapu: employees.delete (a globális identity-életciklust az Odin platform:admin-fala védi — a UI a `iam:provision` SCOPE-ra kapuz (nem feature-jogra, és nem `isPlatformAdmin`-ra — a platform-admin is azért megy át, mert az Odin `iam:provision`-ként fogadja el)).',
    },
    employeeSendWelcome: {
        method: 'POST',
        path: '/employees/:id/welcome',
        body: c.noBody(),
        responses: {
            202: z.object({ accepted: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            // A cél NEM kaphat meghívót (nincs aktív app-tagsága · inaktív · governance-admin · service-fiók)
            // — az Odin `issued: false` ága. Generikus üzenet: az okot sem az Odin, sem mi nem bontjuk.
            409: ErrorSchema,
            429: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Üdvözlő levél küldése egy munkatársnak (a legacy manuális „welcome" gombja). A BFF feloldja a munkatárs e-mail-címét a HÍVÓ tokenjével, majd a dedikált gépi identitással `invite` (7 napos; ha a célnak már van jelszava, az Odin auditáltan 60 perces `reset`-re fokozza le) jelszó-beállító tokent kér az Odintól, és a `identity.welcome` TRANZAKCIÓS levelet küldi BELSŐS hangnemben (MORGENS Vezetőség aláírással). Jelszó SOHA nem megy a levélben. Jog-kapu: employees.manage.',
    },
    supportTeams: {
        method: 'GET',
        path: '/support-teams',
        // Support-csapatok (workforce/support-teams) + tagságok. Kereső a névre/leírásra/tag-névre; locale-tudatos
        // rendezés (név). Offset-lapozás (a csapatok száma kicsi → gyakorlatban egy lap).
        query: z.object({
            q: z.string().optional(),
            order: z.enum(['asc', 'desc']).optional(),
            lang: z.enum(['hu', 'en']).optional(),
            limit: z.coerce.number().int().positive().max(200).optional(),
            offset: z.coerce.number().int().nonnegative().optional(),
        }),
        responses: {
            200: z.object({
                supportTeams: z.array(SupportTeamRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Support-csapatok listája (workforce/support-teams) + tagságok — név/leírás/tag keresés, név szerint rendezve.',
    },
    supportTeamRefs: {
        method: 'GET',
        path: '/support-teams/refs',
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: z.object({
                members: z.array(z.object({ id: z.string(), name: z.string() })),
                /** A választható szolgáltatások (billing katalógus, SERVICE-szint; locale-tudatos név) — a csapat↔szolgáltatás kötéshez. */
                services: z.array(z.object({ id: z.string(), name: z.string() })),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            /**
             * UPSTREAM-ZAVAR. A handler SZÁNDÉKOSAN szétválasztja a két hiba-osztályt: a foundation 401/403-a →
             * 403 („nincs jogod"), MINDEN más (429/5xx) → 502 („Újra"). A 502 eddig KIMARADT a szerződésből,
             * tehát a kliens típus szerint nem is létezett ez az ág (mérve, a refs-végpont első
             * tesztjének írásakor) — a szerződés a forrás-igazság, ezért itt a szerződés hiányzott, nem a kód.
             */
            502: ErrorSchema,
        },
        summary: 'Support-csapat-szerkesztő opció-lista: a felvehető tagok (belső identityk) + a választható szolgáltatások (billing katalógus), locale-tudatos névvel. Jog-kapu: support_teams.view.',
    },
    supportTeamCreate: {
        method: 'POST',
        path: '/support-teams',
        body: SupportTeamWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Új support-csapat (workforce/support-teams): kétnyelvű név/leírás + e-mail + tagok (support-team-members create) + szolgáltatás-kötések (billing/services). Nem-atomi cross-domain write → részleges hiba esetén best-effort rollback (tagságok + csapat-mag törlése) + 502 (árva csapat sosem marad). Jog-kapu: support_teams.manage (+ Odin write:all:workforce).',
    },
    supportTeamUpdate: {
        method: 'PATCH',
        path: '/support-teams/:id',
        headers: ClientIfMatchHeaders,
        body: SupportTeamWriteBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            // A `400` KIVEZETVE : a BFF egyetlen úton sem állította elő — a hiányzó/elavult OCC a
            // `428`/`409`, a séma-hiba a `422`. A deklarált-de-soha-nem-küldött státusz félrevezető szerződés.
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Support-csapat szerkesztése (név/leírás/e-mail PATCH If-Match/OCC) + tag-halmaz diff-szinkron (support-team-members add/remove) + szolgáltatás-kötés diff (billing/services). Nem-atomi cross-domain write: a fej-PUT/tag-diff már perzisztálhat, mielőtt egy rákövetkező lépés elhasal → 502 „részlegesen alkalmazva, töltsd újra" (a fej-verzió elmozdulhatott, teljes undo kockázatos). Jog-kapu: support_teams.manage.',
    },
    supportTeamDelete: {
        method: 'DELETE',
        path: '/support-teams/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.literal(true) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Support-csapat törlése: tagságok eltávolítása → a csapat soft-delete. Referencia-integritás delete-guard: ha ticket hivatkozik a csapatra, VAGY egy katalógus-szolgáltatás felelős csapata (a jegy útjának forrása) → 409 (nem törölhető). Jog-kapu: support_teams.delete.',
    },
    accountRefs: {
        method: 'GET',
        path: '/account-refs',
        responses: {
            200: z.object({
                type: z.record(z.string()),
                pms: z.record(z.string()),
                bookingEngine: z.record(z.string()),
                channelManager: z.record(z.string()),
                hosting: z.record(z.string()),
                /** Régió KÓD → hivatalos név (a `reference/account-region`-ból) — a régió-select opció-forrása. */
                region: z.record(z.string()),
                /** Export-kampány kulcs → címke (`reference/export-campaign`) — a többválasztós `exportCampaigns` forrása. */
                exportCampaign: z.record(z.string()),
                /** SMTP-szolgáltató kulcs → címke (`reference/smtp`) — a többválasztós `smtps` forrása. */
                smtp: z.record(z.string()),
                /** Levelezés-szolgáltató kulcs → címke (`reference/mailing`) — a többválasztós `mailings` forrása. */
                mailing: z.record(z.string()),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Account reference-szótárak (slug → hivatalos név) az Odin reference-data-ból (type/pms/bookingEngine/channelManager/hosting/region + exportCampaign/smtp/mailing). A többválasztós `types` a `type` szótárt használja (nincs külön forrás).',
    },
    tickets: {
        method: 'GET',
        path: '/tickets',
        query: TicketListQuerySchema,
        responses: {
            200: z.object({
                tickets: z.array(TicketListItemSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /**
                 * Az életciklus-fülek DARABSZÁMAI (a legacy fül-badge-ei), a jelenlegi kereső/típus/„csak az enyém"
                 * szűrőkkel — tehát a badge azt mutatja, mennyit LÁTNÁL fülváltás után.
                 */
                groups: z.object({
                    in_progress: z.number(),
                    closed: z.number(),
                }),
                /**
                 * UGYANEZ a hívóra osztott jegyekre szűkítve (a „csak az enyém" állapotától FÜGGETLENÜL). Ebből dönti
                 * el a UI, hogy a legacy-hű „a saját jegyeim" alap-szűrő bekapcsolva induljon-e.
                 */
                mineGroups: z.object({
                    in_progress: z.number(),
                    closed: z.number(),
                }),
                /**
                 * `true` → az upstream lapozás elérte a védő-plafont (10 000 sor), tehát a lista és MINDEN belőle
                 * számolt aggregátum (darabszám, fül-badge) CSONKA. A felület ezt JELEZI: egy csendben elvágott lista
                 * rosszabb, mint egy látható figyelmeztetés. Mérve a legnagyobb állomány 2577 sor, tehát
                 * ma nem fordul elő — a jelzés azért van, hogy amikor előfordul, ne a hibás számból derüljön ki.
                 */
                truncated: z.boolean(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Ticket-lista (support, belső) a support.tickets busz-erőforrásból — fiók/assignee/requester/csapat feloldva. ÉLETCIKLUS-FÜLEK (`group`: in_progress = open+in_progress+reopened · closed = closed+resolved — a „Nyitott" fül kivezetve, lásd `TICKET_GROUP_STATUSES`) + „csak az enyém" szűrő (`mine`, a hívó token-sub-ja alapján — nem kliens-input az identity), státusz/típus-szűrő, egyesített kereső, offset-lapozás (BFF-oldal). A válasz a fülek darabszámait is adja (`groups`), plusz a hívó saját jegyeinek darabszámát fülönként (`mineGroups`) — utóbbiból a UI a legacy „a saját jegyeim" alap-szűrőjét dönti el.',
    },
    ticketRefs: {
        method: 'GET',
        path: '/tickets/refs',
        responses: {
            200: TicketRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Ticket-szerkesztő opció-listák: felelős-jelöltek (belső identity-k) + support-csapatok — a szerkesztő-form select-jeihez. Jog-kapu: tickets.view.',
    },
    ticketFormOptions: {
        method: 'GET',
        path: '/tickets/form-options',
        query: z.object({ accountId: z.string().min(1) }),
        responses: {
            200: TicketFormOptionsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'A ticket-nyitó form FIÓK-FÜGGŐ opciói (a legacy `updatedSelectedAccount` szerver-oldali párja): szolgáltatás-lista két csoportban (a fiókhoz aktívan hozzárendelt, ismétlődő díjú szolgáltatások — majd a többi választható; mindkettő CSAK a ticketen megjelenítendő szolgáltatásokból, név szerint rendezve) + a fiók kapcsolattartói (igénylő-jelöltek). Jog-kapu: tickets.view.',
    },
    ticketCreate: {
        method: 'POST',
        path: '/tickets',
        body: TicketCreateBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Ticket létrehozása („Új jegy", belső). Jog-kapu: tickets.manage. A leírás min. 25 karakter (legacy). A ticketNumber generált (legacy-hű random); a cél-tenant a fiók forrásából (szerver-oldal). A JEGY ÚTJA SZERVER-OLDALI (a legacy 1:1): a támogatói csapat a `serviceId`-hoz rendelt csapat, az első felelős pedig az a belső kolléga, aki EGYSZERRE a fiók adminja és a csapat tagja (nincs ilyen → felelős nélkül nyílik). A kliens NEM küld csapatot/felelőst — azok a szerkesztőben módosíthatók.',
    },
    ticketUpdate: {
        method: 'PATCH',
        path: '/tickets/:id',
        headers: ClientIfMatchHeaders,
        body: TicketUpdateBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Ticket szerkesztése (státusz/lezárás-ok/felelős/csapat; If-Match/OCC, hiányzó If-Match → 428). Jog-kapu: tickets.manage. A BFF-en (422) a valid státusz-enum + a closeReason kötelezősége (csak closed) a fal — átmenet-mátrix tudatosan nincs (a legacy any→any). A cél-tenant a ticket forrásából. A sikeres írás UTÁN értesítés megy (ticket.assigned / status_changed / closed / resolved); a `shareCloseReason` csak az értesítésre hat, a tárolt sorra nem. A komment KÜLÖN végpont.',
    },
    ticketMarkRead: {
        method: 'POST',
        path: '/tickets/:id/read',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Olvasottnak jelölés: a HÍVÓ munkatárs utolsó megnyitásának ideje (upsert `support/ticket-reads`, readAt=now — az identity a tokenből, nem kliens-input). A jegy megnyitásakor és a saját hozzászólás után hívja a felület; ebből számol a lista `unread`-je. Jog-kapu: tickets.view.',
    },
    ticketGet: {
        method: 'GET',
        path: '/tickets/:id',
        responses: {
            200: TicketDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Ticket-detail (support, belső): egy `support.tickets` sor teljes mezőkészlete (leírás + online-linkek + Asana-URL) a lista-dúsítással (fiók/assignee/requester/csapat feloldva) + commentCount. Jog-kapu: tickets.view. A path-`:id` plain-UUID (path-traversal choke-point → 400).',
    },
    ticketCreateAsanaTask: {
        method: 'POST',
        path: '/tickets/:id/asana-task',
        headers: ClientIfMatchHeaders,
        body: z.object({}).optional(),
        responses: {
            200: TicketAsanaTaskSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Asana-feladat létrehozása a jegyből, a HÍVÓ SAJÁT Asana-tokenjével (az Odin `POST /iam/me/asana/tasks` proxyja — a PAT sosem jön át a BFF-re). A feladat tartalma és a cél-projekt SZERVER-oldali (nincs kérés-body). Jog-kapu: tickets.manage; If-Match/OCC kötelező. Már kötött jegy → 409 (nincs néma duplikátum). Nincs beállított/elfogadott PAT vagy nincs konfigurált projekt → 422 mezőnkénti okkal; az Asana elérhetetlen → 502.',
    },
    ticketUnlinkAsanaTask: {
        method: 'DELETE',
        path: '/tickets/:id/asana-task',
        headers: ClientIfMatchHeaders,
        // A ts-rest a DELETE-et CSAK explicit `body`-val ismeri fel mutációnak (enélkül a kliensen nincs
        // `.mutation()`) — a `c.noBody()` a repo mintája a többi törlő végponton.
        body: c.noBody(),
        responses: {
            200: TicketAsanaTaskSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A jegy Asana-hivatkozásának LEVÁLASZTÁSA (a jegyen `asanaTaskUrl = null`). Az Asana-feladatot NEM törli — idegen rendszerben visszavonhatatlanul törölni nem a jegy dolga, és a legacy sem tette. Jog-kapu: tickets.manage; If-Match/OCC kötelező. Nincs mit leválasztani → 409.',
    },
    ticketComments: {
        method: 'GET',
        path: '/tickets/:id/comments',
        responses: {
            200: z.array(TicketCommentRowSchema),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Ticket-kommentek (belső nézet) egy tickethez — a `support.ticket-comments` busz-erőforrásból ticketId-re szűrve, createdAt szerint rendezve, a szerző nevével dúsítva. Ismeretlen ticketId → üres lista. A belső felület MINDKÉT kind-et (internal+external) mutatja. Jog-kapu: tickets.view.',
    },
    ticketCommentCreate: {
        method: 'POST',
        path: '/tickets/:id/comments',
        body: TicketCommentCreateBodySchema,
        responses: {
            201: TicketCommentRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Ticket-komment létrehozása (belső nézet). Jog-kapu: tickets.manage. A cél-tenant + accountId a ticket FORRÁSÁBÓL (szerver-oldal), a szerző a token `sub`-ja (SOHA nem kliens-input — IDOR-védelem). A path-`:id` plain-UUID (traversal choke-point → 400). A sikeres írás UTÁN komment-értesítés megy (fire-and-forget — a hibája nem buktatja a kommentet).',
    },
    /**
     * KOMMENT-REAKCIÓ TOGGLE (emoji). Egy hívás: ha a hívó MÁR adta ezt az emojit erre a kommentre, a reakció
     * MEGSZŰNIK; egyébként létrejön. MIÉRT toggle és nem külön POST+DELETE: a felületen ez EGY kattintás
     * ugyanazon a gombon, és a kliens amúgy is a szerver állapotát tükrözi (`reactedByMe`) — két végponttal a
     * kliensnek kellene eldöntenie, melyiket hívja, ami versenyhelyzetben félrelőne.
     *
     * A válasz a KOMMENT FRISS reakció-listája (nem a teljes komment): ebből a felület azonnal, újratöltés
     * nélkül átrajzolja a sávot.
     */
    ticketCommentReactionToggle: {
        method: 'POST',
        path: '/tickets/:id/comments/:commentId/reactions',
        body: TicketReactionToggleBodySchema,
        responses: {
            200: z.object({ reactions: z.array(TicketCommentReactionSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Emoji-reakció be/ki egy jegy-kommenten (toggle). Jog-kapu: tickets.view (a reagálás OLVASÓI együttműködés — nem jegy-KEZELÉS). A reagáló a token `sub`-ja (SOHA nem kliens-input). A path-`:id`/`:commentId` plain-UUID (traversal choke-point → 400). Az emoji a KÖTÖTT készletből (`TICKET_REACTION_EMOJIS`) — bármi más 422. A BELSŐ JEGYZET reakciója partner elől zárt (az Odin RLS-e sem engedi neki); a KÜLSŐ kommenten a partner is reagálhat a saját, szűk portál-végpontján (`portal.ticketReplyReactionToggle`).',
    },
    ticketCommentUpdate: {
        method: 'PUT',
        path: '/tickets/:id/comments/:commentId',
        headers: ClientIfMatchHeaders,
        body: TicketCommentUpdateBodySchema,
        responses: {
            200: TicketCommentRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Ticket-komment szerkesztése (SZERZŐ-scope): a komment `authorIdentityId`-je == a hívó token `sub`-ja (a legacy `sender_id == Auth::id()`), amit a BFF szerver-oldalon ellenőriz (idegen szerző → 403). If-Match/OCC. Jog-kapu: tickets.manage. A path-`:id`/`:commentId` plain-UUID (traversal → 400).',
    },
    ticketCommentTemplates: ticketCommentTemplatesContract,
    ticketAttachments: ticketAttachmentsContract,
    productUpdates: productUpdatesContract,
    campaignForecast: campaignForecastContract,
    expenses: expensesContract,
    messageAttachments: messageAttachmentsContract,
    assetCatalog: assetCatalogContract,
    assetRequests: assetRequestsContract,
    assets: {
        method: 'GET',
        path: '/assets',
        query: AssetListQuerySchema,
        responses: {
            200: z.object({
                assets: z.array(AssetRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /**
                 * `true` → az upstream lapozás elérte a védő-plafont (10 000 sor) → a lista és a darabszám
                 * CSONKA. A felület jelzi (a csendes csonkolás rosszabb, mint egy látható figyelmeztetés).
                 */
                truncated: z.boolean(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // ÁTMENETI upstream-hiba (Odin-fojtás/5xx) — NEM jog-hiba: a felület „Újra" gombot mutat rá
            502: ErrorSchema,
        },
        summary: 'Eszköz-lista (workforce leltár + igénylés-workflow, belső) a workforce.assets busz-erőforrásból — birtokló/igénylő/kedvezményezett identity + csapat- és részleg-név feloldva; fül-scope (inventory|request) + státusz/kategória/típus/használati hely/csapat szűrő + kereső (név, leltári szám, sorozatszám, birtokló/igénylő/kedvezményezett neve) + rendezés + offset-lapozás (BFF-oldal).',
    },
    assetRefs: {
        method: 'GET',
        path: '/assets/refs',
        responses: {
            200: AssetRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            // ÁTMENETI upstream-hiba (Odin-fojtás/5xx) — NEM jog-hiba: a felület „Újra" gombot mutat rá
            502: ErrorSchema,
        },
        summary: 'Eszköz-szerkesztő opció-listák: CSAPATOK (a részleg nevével együtt — `teams[]`, nem részleg-lista), a birtokló EntityCombobox-szal. Jog-kapu: asset_inventory.view.',
    },
    assetCreate: {
        method: 'POST',
        path: '/assets',
        body: AssetWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Eszköz létrehozása (workforce INTERNAL — write:all:workforce scope). Jog-kapu: assets.manage. Az assetNumber generált (MOR-prefix + per-kategória sorszám); a category a type-ból származik; a status a retired-hez retirementDate-et kíván (422).',
    },
    assetUpdate: {
        method: 'PATCH',
        path: '/assets/:id',
        headers: ClientIfMatchHeaders,
        body: AssetWriteBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Eszköz frissítése / selejtezése (If-Match/OCC). Jog-kapu: assets.manage. status=retired → retirementDate kötelező (422).',
    },
    assetDelete: {
        method: 'DELETE',
        path: '/assets/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Eszköz soft-delete (workforce INTERNAL). Jog-kapu: assets.delete.',
    },
    assetRequestCreate: {
        method: 'POST',
        path: '/assets/requests',
        body: AssetRequestCreateBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Eszköz-igénylés létrehozása (workflow): status=requested, requester=aktuális identity, category a type-ból; opcionális megjegyzés + kedvezményezett (akinek igénylik — a munkatárs-címtárból). Más nevére igényelni csak employees.view joggal lehet (különben 422 — szerver-oldalon is). Jog-kapu: asset_requests.manage. Az Idempotency-Key-t az internal busz elutasítja — a dupla-submitet a UI busy-állapota védi; a create után asset-events sor (requested).',
    },
    assetDecide: {
        method: 'POST',
        path: '/assets/:id/decide',
        headers: ClientIfMatchHeaders,
        body: AssetDecideBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Igénylés-döntés (approve|reject) — CSAK `requested` státuszból (állapotgép, 409); If-Match/OCC kötelező (428). Jog-kapu: asset_requests.decide (approve+reject SZIMMETRIKUSAN — user-döntés). reject → rejectReason a sorra + note az asset-events sorban; approve mellett a rejectReason tilos (422).',
    },
    assetProgress: {
        method: 'POST',
        path: '/assets/:id/progress',
        headers: ClientIfMatchHeaders,
        body: AssetProgressBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Beszerzés-előrehaladás — SZIGORÚ sorrend: approved→being_procured→purchased→received (állapotgép, 409); If-Match/OCC kötelező (428). Jog-kapu: asset_requests.manage. purchased → purchaseDate=ma (a purchaseLocation NULL marad — nincs N/A hardcode); received → holder=a kedvezményezett, ennek híján a requester; minden lépés asset-events sort ír.',
    },
    orgDepartments: {
        method: 'GET',
        path: '/org-structure/departments',
        query: OrgListQuerySchema,
        responses: {
            200: z.object({
                departments: z.array(OrgDepartmentRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // ÁTMENETI upstream-hiba (Odin-fojtás/5xx) — NEM jog-hiba: a felület „Újra" gombot mutat rá
            502: ErrorSchema,
        },
        summary: 'Részleg-lista a workforce.departments busz-erőforrásból (i18n név/leírás hu/en-re bontva) — a szervezeti fa LEGFELSŐ szintje. A sor hozza a csapatait (név+szín) és a két szinten át aggregált munkatársait. Szabad-szöveg (név/slug, a csapatai nevére is illeszt) + rendezés + offset-lapozás (BFF-oldal). Jog-kapu: organizational_structure.view.',
    },
    orgTeams: {
        method: 'GET',
        path: '/org-structure/teams',
        query: OrgListQuerySchema,
        responses: {
            200: z.object({
                teams: z.array(OrgTeamRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // ÁTMENETI upstream-hiba (Odin-fojtás/5xx) — NEM jog-hiba: a felület „Újra" gombot mutat rá
            502: ErrorSchema,
        },
        summary: 'Csapat-lista a workforce.teams busz-erőforrásból — a részleg-név (departmentId → departmentName) feloldva. A SZÍN itt él (a részlegnek nincs). Szabad-szöveg + rendezés + offset-lapozás. Jog-kapu: organizational_structure.view.',
    },
    orgPositions: {
        method: 'GET',
        path: '/org-structure/positions',
        query: OrgListQuerySchema,
        responses: {
            200: z.object({
                positions: z.array(OrgPositionRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // ÁTMENETI upstream-hiba (Odin-fojtás/5xx) — NEM jog-hiba: a felület „Újra" gombot mutat rá
            502: ErrorSchema,
        },
        summary: 'Beosztás-lista a workforce.positions busz-erőforrásból — a csapat (teamId → teamName/teamColor) ÉS a csapat részlege (departmentName) feloldva. Szabad-szöveg + rendezés + offset-lapozás. Jog-kapu: organizational_structure.view.',
    },
    departmentCreate: {
        method: 'POST',
        path: '/org-structure/departments',
        body: DepartmentWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Részleg létrehozása (workforce INTERNAL — write:all:workforce). Jog-kapu: organizational_structure.manage. A slug a nameEn-ből generált (ütközésre suffix); a név/leírás i18n JSON. SZÍNT NEM vesz át: az a csapat szintjén él.',
    },
    departmentUpdate: {
        method: 'PATCH',
        path: '/org-structure/departments/:id',
        headers: ClientIfMatchHeaders,
        body: DepartmentWriteBodySchema,
        responses: {
            400: ErrorSchema,
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Részleg frissítése (If-Match/OCC). Jog-kapu: organizational_structure.manage. A slug STABIL (nem regenerálódik); a név/leírás i18n JSON újraépül.',
    },
    departmentDelete: {
        method: 'DELETE',
        path: '/org-structure/departments/:id',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            400: ErrorSchema,
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Részleg törlése (If-Match/OCC). Jog-kapu: organizational_structure.delete. REFERENCIA-GUARD: ha van hozzá tartozó CSAPAT → 409 (részleg használatban).',
    },
    teamCreate: {
        method: 'POST',
        path: '/org-structure/teams',
        body: TeamWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Csapat létrehozása (workforce INTERNAL). Jog-kapu: organizational_structure.manage. A departmentId KÖTELEZŐ és létező részleg (422 különben) — csapat nem létezhet részleg nélkül; a slug a nameEn-ből generált.',
    },
    teamUpdate: {
        method: 'PATCH',
        path: '/org-structure/teams/:id',
        headers: ClientIfMatchHeaders,
        body: TeamWriteBodySchema,
        responses: {
            400: ErrorSchema,
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Csapat frissítése (If-Match/OCC). Jog-kapu: organizational_structure.manage. A departmentId létező részleg (422); a slug STABIL. A csapat átsorolható másik részleg alá.',
    },
    teamDelete: {
        method: 'DELETE',
        path: '/org-structure/teams/:id',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            400: ErrorSchema,
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Csapat törlése (If-Match/OCC). Jog-kapu: organizational_structure.delete. REFERENCIA-GUARD: ha van hozzá tartozó beosztás VAGY hozzá rendelt eszköz → 409 (csapat használatban).',
    },
    positionCreate: {
        method: 'POST',
        path: '/org-structure/positions',
        body: PositionWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Beosztás létrehozása (workforce INTERNAL). Jog-kapu: organizational_structure.manage. A teamId létező CSAPAT (422 különben); a slug a nameEn-ből generált. Részleget nem vesz át: az a csapatból következik.',
    },
    positionUpdate: {
        method: 'PATCH',
        path: '/org-structure/positions/:id',
        headers: ClientIfMatchHeaders,
        body: PositionWriteBodySchema,
        responses: {
            400: ErrorSchema,
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Beosztás frissítése (If-Match/OCC). Jog-kapu: organizational_structure.manage. A teamId létező CSAPAT (422); a slug STABIL. A beosztás átsorolható másik csapat alá.',
    },
    positionDelete: {
        method: 'DELETE',
        path: '/org-structure/positions/:id',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            400: ErrorSchema,
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Beosztás törlése (If-Match/OCC). Jog-kapu: organizational_structure.delete. REFERENCIA-GUARD: ha van rá hivatkozó munkatárs (employee-details.positionId) → 409 (beosztás használatban).',
    },
    invoices: {
        method: 'GET',
        path: '/invoices',
        query: InvoiceListQuerySchema,
        responses: {
            200: z.object({
                invoices: z.array(InvoiceRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /**
                 * `true` → az upstream lapozás elérte a védő-plafont (10 000 sor) → a lista és a darabszám
                 * CSONKA. A felület jelzi (a csendes csonkolás rosszabb, mint egy látható figyelmeztetés).
                 */
                truncated: z.boolean(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Számla-lista (billing, belső) a billing.invoices busz-erőforrásból — kibocsátó + számlázott fiók(ok) feloldva, pénz Decimal→string; státusz-szűrő (konkrét státusz vagy fül-csoport) + típus/kibocsátó/kiállítás-/határidő-dátum szűrő + szabad-szöveges kereső (számlaszám, felek neve/címe, összegek) + rendezés + offset-lapozás (BFF-oldal).',
    },
    invoiceRefs: {
        method: 'GET',
        path: '/invoices/refs',
        query: InvoiceRefsQuerySchema,
        responses: {
            200: InvoiceRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Számla-szerkesztő picker-adatok: {clients, issuers} + ?clientId= mellett {accounts, billingProfiles} (a profil a snapshot-mezőkkel + defaultInvoicePaymentDeadlineDays + defaultInvoiceNote — elő-kitöltéshez). Jog-kapu: invoices.manage.',
    },
    invoiceDetail: {
        method: 'GET',
        path: '/invoices/:id',
        responses: {
            200: InvoiceDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Számla-detail: teljes fej (form-mezők + OCC-verzió) + tétel-lista (tétel-id + version + számolt összegek) + client-scope accountIds — a szerkesztő ÉS a read-only nézet forrása. Jog-kapu: invoices.view.',
    },
    invoiceCreate: {
        method: 'POST',
        path: '/invoices',
        body: InvoiceCreateBodySchema,
        responses: {
            201: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Draft-számla létrehozása (billing [T] — tenant-cél a body clientId-jából, validálva). Orchestráció: fej-POST (status=draft, ideiglenes TMP-bizonylatszám — ütközéskor a következő sorszámmal újra, végső tartalékként DRAFT-{uuid}) → tételek-POST → client-scope pivotok; a totálok + issuer-snapshot SZERVER-oldalon számolódnak/másolódnak. Részleges hiba → best-effort rollback + 502 (a form-állapot a kliensen megmarad). Jog-kapu: invoices.manage.',
    },
    invoiceUpdate: {
        method: 'PUT',
        path: '/invoices/:id',
        headers: ClientIfMatchHeaders,
        body: InvoiceDraftBodySchema,
        responses: {
            200: z.object({
                id: z.string(),
                version: z.number(),
                netTotal: z.string(),
                vatTotal: z.string(),
                grossTotal: z.string(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Draft-számla frissítése TELJES állapottal (fej + teljes tétel-lista — a BFF diff-el, id a kulcs). FEJ-CLAIM-FIRST: az első írás a fej PATCH-e If-Match-csel (ütközés → 409 és NULLA tétel-írás; hiányzó If-Match → 428); utána tétel-diff (create/update/delete) + pivot-diff. Csak draft (nem-draft → 409). Tétel/pivot-hiba → 502, nincs rollback (a következő PUT önjavító). Jog-kapu: invoices.manage.',
    },
    invoiceDelete: {
        method: 'DELETE',
        path: '/invoices/:id',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Draft-számla törlése (CSAK draft — nem-draft → 409; If-Match kötelező → 428). Fej-claim ELŐSZÖR (a lista azonnal tisztul), utána best-effort tétel- + pivot-takarítás (hiba → log, az árva gyerek-sor a fej soft-delete-je alatt láthatatlan). Jog-kapu: invoices.delete.',
    },
    invoiceIssue: {
        method: 'POST',
        path: '/invoices/:id/issue',
        headers: ClientIfMatchHeaders,
        body: InvoiceIssueBodySchema,
        responses: {
            200: InvoiceLifecycleResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Számla/díjbekérő kiállítása (CSAK draft → issued; nem-draft → 409). A DÁTUMOK a kiállítás napjára igazodnak: a kelte a mai nap, a teljesítés és a fizetési határidő az EREDETI eltolását megőrizve mozdul vele (jövőbeli keltű piszkozat érintetlen) — a body-ban beküldött dátum ezt felülírja. Számlázz.hu generateInvoice/generateProforma → SIKER esetén az Odin fej-sor busz-frissítése (invoice_number + status=issued + a dátumok), If-Match OCC-vel. Számlázz.hu-hiba → 502 (draft marad); nincs agent-kulcs → 503; hiányzó If-Match → 428. Jog-kapu: invoices.manage.',
    },
    invoiceBulkIssue: {
        method: 'POST',
        path: '/invoices/bulk-issue',
        body: InvoiceBulkIssueBodySchema,
        responses: {
            200: InvoiceBulkIssueResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'TÖMEGES kiállítás (a legacy „bulk issue" 1:1): a megadott számlák/díjbekérők kiállítása EGYENKÉNT, a szimpla `issue` úton (Számlázz.hu + Odin fej-frissítés If-Match-csel). A hívó NEM ad If-Match-et: a fej-verziót a szerver olvassa ki soronként (a UI-nak nincs is friss verziója minden kijelölt sorra). SOR-SZINTŰ eredmény: egy sor hibája NEM állítja meg a többit — a válasz típusonként (számla/díjbekérő) bontott siker/hiba-darabszámot és per-sor kimenetet ad. Jog-kapu: invoices.manage.',
    },
    editorImageUploadUrl: {
        method: 'POST',
        path: '/editor-images/upload-url',
        body: EditorImageUploadBodySchema,
        responses: {
            200: EditorImageUploadUrlSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
        },
        summary: 'WYSIWYG-KÉP feltöltő-URL (a legacy `wysiwyg-editor-upload-image` utódja). A BFF az Odin `support` fájl-tárának presigned PUT-URL-jét proxyzza — a BÁJTOK SOSEM mennek át rajtunk, és a presigned URL SOHA nem kerül logba. Up-front (defense-in-depth) fal: a KÖZÖS média-whitelist (`EDITOR_MEDIA_TYPES` — kép: jpeg/png/gif/webp, videó: mp4/webm/quicktime) + TÍPUS-FÜGGŐ plafon (`editorMediaMaxBytes`: kép 5 MB — a legacy `image|mimes:jpg,jpeg,png,gif,webp|max:5120` szabálya 1:1 —, videó 25 MB, mert egy néhány tíz másodperces képernyőfelvétel nem fér 5 MB-ba). A CÉL-TENANT szerver-oldalon oldódik fel (nem kliens-input): FIÓK-azonosítóval a fiók ügyfele, fiók NÉLKÜL a BELSŐ (MORGENS) ügyfél-rekord (`ATLAS_INTERNAL_CLIENT_ID`) — enélkül 503 (fail-closed). Jog-kapu: tickets.manage VAGY messages.manage VAGY product_updates.manage (a szerkesztő mindhárom modulban él).',
    },
    editorImageCommit: {
        method: 'POST',
        path: '/editor-images/:fileId/commit',
        body: EditorImageCommitBodySchema,
        responses: {
            200: EditorImageCommitSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
        },
        summary: 'A feltöltött WYSIWYG-kép véglegesítése (az Odin fájl-commit ellenőrzi a bájtokat), és a STABIL megjelenítő-URL visszaadása. FONTOS: a szerkesztő a stabil `/editor-images/:fileId/content` utat menti a HTML-be, NEM a presigned URL-t — az lejárna, és a beágyazott kép egy nap múlva eltörne. Jog-kapu: tickets.manage VAGY messages.manage VAGY product_updates.manage (a WYSIWYG mindhárom felületen fut).',
    },
    editorImageResolve: {
        method: 'GET',
        path: '/editor-images/:fileId/url',
        responses: {
            200: EditorImageResolveSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'A beágyazott WYSIWYG-kép MEGJELENÍTÉSI URL-je: friss presigned GET-URL a stabil `/editor-images/:fileId/content` útra. MIÉRT NEM 302-es kép-út: egy `<img src>` nem tud `Authorization` fejlécet küldeni, a BFF pedig Bearer-tokent kér — a bájtok itt sem mennek át rajtunk (a precedens a ticket-csatolmány `downloadUrl`-je). A presigned URL SOHA nem kerül a mentett HTML-be és SOHA nem kerül logba. Jog-kapu: tickets.view VAGY messages.view VAGY product_updates.view (aki a tartalmat olvassa, a beágyazott képét is látja).',
    },
    invoiceStorno: {
        method: 'POST',
        path: '/invoices/:id/storno',
        headers: ClientIfMatchHeaders,
        body: InvoiceStornoBodySchema,
        responses: {
            200: InvoiceLifecycleResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Számla sztornózása (CSAK issued|overdue → storno; draft/paid/cancelled/storno → 409). Számlázz.hu generateReverseInvoice (kelt=ma, teljesítés=eredeti, opcionális megjegyzés) → SIKER esetén status=storno + storno_invoice_number + storno_comment mentése (busz, If-Match). Számlázz.hu-hiba → 502; nincs agent-kulcs → 503. Jog-kapu: invoices.manage.',
    },
    invoiceCancelProforma: {
        method: 'POST',
        path: '/invoices/:id/cancel-proforma',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: InvoiceLifecycleResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Díjbekérő érvénytelenítése (CSAK proforma típus + issued|overdue → cancelled). Számlázz.hu deleteProforma → SIKER esetén status=cancelled (busz, If-Match). Nem-proforma / rossz státusz → 409; Számlázz.hu-hiba → 502; nincs agent-kulcs → 503. Jog-kapu: invoices.manage.',
    },
    invoicePdf: {
        method: 'GET',
        path: '/invoices/:id/pdf',
        query: InvoicePdfQuerySchema,
        responses: {
            200: InvoicePdfSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Számla-PDF letöltése a Számlázz.hu-ról (letölthető státuszok: issued|paid|overdue|storno; `?which=storno` → a sztornó-bizonylat a storno_invoice_number-re, alapból az eredeti számla; draft vagy hiányzó bizonylatszám → 409). A PDF base64-ben tér vissza (a UI Blob-bá dekódolja) — SOHA nem tárolódik. Nincs agent-kulcs → 503; Számlázz.hu-hiba → 502. Jog-kapu: invoices.view.',
    },
    invoiceSyncStatus: {
        method: 'POST',
        path: '/invoices/sync-status',
        body: c.noBody(),
        responses: {
            200: InvoiceSyncResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            502: ErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Számla-státusz szinkronizáció (Számlázz.hu getInvoiceData) az issued/overdue számlákra: kifizetve → paid + payment_date; lejárt (due < ma, nem fizetett) → overdue; díjbekérőből autokassza-számla → related_invoice_number. Cache-lock a párhuzamos futás ellen (foglalt → 409). Nincs agent-kulcs → 503. Jog-kapu: invoices.manage.',
    },
    invoicesGenerate: {
        method: 'POST',
        path: '/invoices/generate',
        body: InvoiceGenerateBodySchema,
        responses: {
            200: InvoiceGenerateResultSchema,
            202: InvoiceGenerateStartedSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            503: ErrorSchema,
        },
        summary: 'Havi számla-GENERÁTOR (a legacy billing:generate-invoices 1:1, DRAFT-előállításig): esedékes recurring hozzárendelésekből (flat/tiered/base_tiered/commission árazás, prepaid/postpaid gördülő periódus, tört első hónap arányosítás) csoportonként (scope/profil/deviza/prefix/típus/kézbesítés/megállapodás/ÁFA) DRAFT-számlák a buszra. Idempotens (assignment+periódus ÁTFEDÉS-védelem). dryRun=true → SZINKRON becslés (200, nem ír, nem naplóz). ÉLES futás → 202 { runId }: a generálás a válasz UTÁN, HÁTTÉRBEN fut, a teljes fojtás-kivárási profillal (a 60 s-os proxy-timeout miatt a szinkron út `rate_limited` kihagyásokat termelt), a végéről a kezdeményező ÉRTESÍTÉST kap. Már fut egy kör → 409. Gépi kredenciál nélkül → 503. A valódi kihagyások (already_invoiced, zero és manually_removed nélkül — az első kettő nem probléma, a zár sorát a számla-szerkesztő írja) a kihagyás-naplóba (billing/invoice-generation-skips) íródnak runId-vel. Az --issue (kiállítás) NEM része. Jog-kapu: invoices.manage.',
    },
    invoiceGenerationSkips: {
        method: 'GET',
        path: '/invoices/generation-skips',
        query: InvoiceGenerationSkipListQuerySchema,
        responses: {
            200: InvoiceGenerationSkipListSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'A számla-generálás KIHAGYÁS-NAPLÓJA (billing/invoice-generation-skips, INTERNAL Odin-resource): mely hozzárendelés / tétel miért nem került számlára egy hónapban (ok-kód, futás-azonosító, indító, időbélyeg, részletek) — a cron és a kézi futás is ide ír. Alapból a NYITOTT kivételek (resolvedAt null); state=resolved csak a lezártak, state=all a teljes történet. Szűrés hónap (default: aktuális) / ok / ügyfél / szabad-szöveg; a nevek a BFF-en feloldva. Jog-kapu: invoices.view.',
    },
    invoiceGenerationSkipRelease: {
        method: 'POST',
        path: '/invoices/generation-skips/:id/release',
        body: z.object({}).optional(),
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A kézi tétel-törlés ZÁRÁNAK FELOLDÁSA (`manually_removed` napló-sor lezárása). A napi generálás a zárral jelölt [hozzárendelés, periódus] tételt nem állítja elő újra; a feloldás után a következő futás ismét generálja. CSAK `manually_removed` okú, NYITOTT soron értelmezett (más ok → 409): a többi kivételt maga a generálás zárja le, ha megszűnt. Jog-kapu: invoices.manage.',
    },
    serviceUsageList: {
        method: 'GET',
        path: '/service-usage',
        query: ServiceUsageListQuerySchema,
        responses: {
            200: z.object({
                rows: z.array(ServiceUsageRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A UsageDataChecker nyers használati (billing/service-usage, INTERNAL) sorai egy évre+hónapra (opcionális típus- és szabad-szöveg-szűrő), a külső rendszer ID-hoz best-effort feloldott fiók-nevekkel dúsítva. A mért érték forrás-típusonként MÁST jelent (darab · fő · jutalék-összeg), ezért a sor hordozza a jutalék DEVIZÁJÁT, illetve a nem-jutalékos sorokra a mennyiségből SZÁMOLT nettó összeget — ugyanazzal a motorral, mint a havi számla. Belső staff nézete a generátor ELŐTT. Jog-kapu: usage_data.view.',
    },
    serviceUsageFetch: {
        method: 'POST',
        path: '/service-usage/fetch',
        body: ServiceUsageFetchBodySchema,
        responses: {
            200: ServiceUsageFetchResultSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'HAVI USAGE-LEKÉRÉS a külső rendszerekből (a legacy `usage:fetch` 1:1): MailWizz kiküldés-darabszám (newsletter), HotelCMS/ZadirCMS promóció (loyalty_newsletter) és regisztrált felhasználó (rs_loyalty) — a Zadir→HotelCMS átállási logikával. Az eredmény az Odin `billing/service-usage` INTERNAL sorokba íródik (természetes kulcson upsert). A JUTALÉK-típusok forrása a HotelCMS commission-report (napi cron; a legacyben ilyen nem volt). Nem konfigurált forrás → `skipped`, nem hiba. A `failed` a nálunk elbukott írások száma (újrapróbálható). Jog-kapu: usage_data.manage.',
    },
    currencyRatesList: {
        method: 'GET',
        path: '/currency-rates',
        query: CurrencyRateListQuerySchema,
        responses: {
            200: z.object({ rates: z.array(CurrencyRateRowSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Idősoros árfolyamok (platform.currency_rates) megtekintése, opcionális base/quote szűrővel (a manuális havi árfolyam a hó 1-jén, source=manual). Az Odin-oldali read bármely hitelesített identity-nek megy (belső referencia). Jog-kapu: usage_data.view.',
    },
    currencyRateUpsert: {
        method: 'POST',
        path: '/currency-rates',
        body: CurrencyRateUpsertBodySchema,
        responses: {
            200: z.object({ upserted: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Manuális havi árfolyam felvitele/frissítése (EUR/USD → HUF, a hó 1-jére, source=manual). Odin-oldalon billing-írás (write:all:billing) vagy platform:admin — jog nélkül 403. Jog-kapu: usage_data.manage.',
    },
    currencyRateDelete: {
        method: 'DELETE',
        path: '/currency-rates/:base/:quote/:date',
        pathParams: CurrencyRateDeleteParamsSchema,
        body: z.undefined(),
        responses: {
            200: z.object({ deleted: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Manuális havi árfolyam TÖRLÉSE a természetes kulcson (base/quote/date). Idempotens: nem létező soron `deleted: 0` (nem 404). Odin-oldalon billing-írás vagy platform:admin — jog nélkül 403. Jog-kapu: usage_data.manage (mint a felvitelnél).',
    },
    billingProfiles: {
        method: 'GET',
        path: '/billing-profiles',
        query: BillingProfileListQuerySchema,
        responses: {
            200: z.object({
                profiles: z.array(BillingProfileRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            /**
             * ÁTMENETI upstream-zavar (429/5xx/timeout) — SZÉTVÁLASZTVA a 403-tól. A jog-hibára a felület „nincs
             * jogosultságod"-ot mond, erre „Újra"-t ajánl. A BFF korábban minden nem-200 upstream-státuszt 403-ra
             * képezett (mérve): egy Odin-restart alatt a lista „jog-hibának" látszott.
             */
            502: ErrorSchema,
        },
        summary: 'Számlázási profilok listája (billing, belső) a billing.billing-profiles busz-erőforrásból — ügyfél-név feloldva; szabad-szöveg + ügyfél-szűrő + csak-elsődleges + rendezés + offset-lapozás (BFF-oldal). Jog-kapu: billing_profiles.view.',
    },
    billingProfileClientRefs: {
        method: 'GET',
        path: '/billing-profiles/refs/clients',
        responses: {
            200: BillingProfileClientRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            // A controller ad 502-t upstream-zavarra (`upstream-read.ts`: minden nem-401/403 → 502);
            // a szerződés eddig nem hirdette, tehát a kliens tipizáltan nem tudott róla.
            502: ErrorSchema,
        },
        summary: 'A create-form ügyfél-választójának refs-adata (foundation /v1/admin/clients — id + név). Jog-kapu: billing_profiles.manage.',
    },
    billingProfile: {
        method: 'GET',
        path: '/billing-profiles/:id',
        responses: {
            200: BillingProfileRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            /** ÁTMENETI upstream-zavar — lásd a lista-végpont 502-jét. */
            502: ErrorSchema,
        },
        summary: 'Egy számlázási profil (detail — a szerkesztő elő-kitöltése + a read-only nézet forrása). Jog-kapu: billing_profiles.view.',
    },
    billingProfileCreate: {
        method: 'POST',
        path: '/billing-profiles',
        body: BillingProfileCreateBodySchema,
        responses: {
            201: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Számlázási profil létrehozása (billing [T] — tenant-cél a body clientId-jából, validálva). Az ügyfél ELSŐ profilja MINDIG elsődleges (az explicit isPrimary:false sem írja felül — ha van profil, pontosan egy elsődleges van); ha elsődleges lesz, a BFF előbb feloldja a régi elsődlegest (ensureSinglePrimary). Jog-kapu: billing_profiles.manage.',
    },
    billingProfileUpdate: {
        method: 'PUT',
        path: '/billing-profiles/:id',
        headers: ClientIfMatchHeaders,
        body: BillingProfileWriteBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Számlázási profil frissítése (teljes body, If-Match/OCC — hiányzó If-Match → 428, ütközés → 409). Ha elsődleges lesz, a BFF előbb feloldja a régi elsődlegest (ensureSinglePrimary); az elsődleges KÖZVETLEN lefokozása (isPrimary:false) → 422 (a váltás útja egy másik profil elsődlegessé tétele). Jog-kapu: billing_profiles.manage.',
    },
    billingProfileSetPrimary: {
        method: 'POST',
        path: '/billing-profiles/:id/set-primary',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Profil elsődlegessé tétele (dedikált action, If-Match/OCC): a BFF feloldja az ügyfél többi elsődlegesét, majd ezt állítja be. Jog-kapu: billing_profiles.manage.',
    },
    billingProfileDelete: {
        method: 'DELETE',
        path: '/billing-profiles/:id',
        body: c.noBody(),
        headers: ClientIfMatchHeaders,
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Számlázási profil törlése (soft-delete, OCC: `If-Match` KÖTELEZŐ — hiány → 428, ütközés → 409). KÉT GUARD, mindkettő 409: (1) elsődleges profil NEM törölhető; (2) felülíró profilként HASZNÁLT profil sem — a fiók-override-ok számát a törlés előtt megszámoljuk, és bizonytalan olvasatnál (`unknown`) is tiltunk, mert a csendes visszaesés az ügyfél elsődleges profiljára MÁS vevő-adatot tenne a számlára. Jog-kapu: billing_profiles.delete.',
    },
    paymentCards: {
        method: 'GET',
        path: '/payment-cards',
        query: PaymentCardListQuerySchema,
        responses: {
            200: z.object({
                cards: z.array(PaymentCardRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            /**
             * ÁTMENETI upstream-zavar (429/5xx/timeout). SZÉTVÁLASZTVA a 403-tól: a jog-hibára a felület „nincs
             * jogosultságod"-ot mond, erre viszont „Újra"-t ajánl — és a következő próbálkozás sikerül. A BFF
             * korábban MINDEN nem-200 upstream-státuszt 403-ra képezett (mérve, a 502-ág első
             * tesztjének írásakor): egy Odin-restart alatt az egész lista „jog-hibának" látszott.
             */
            502: ErrorSchema,
        },
        summary: 'Bankkártyák listája (billing, belső) a billing.payment-cards busz-erőforrásból — ügyfél-név feloldva; szabad-szöveg + ügyfél-szűrő + rendezés + offset-lapozás (BFF-oldal). A státusz (érvényes/lejár/lejárt) kliens-oldalon számított. Jog-kapu: payment_cards.view.',
    },
    paymentCardClientRefs: {
        method: 'GET',
        path: '/payment-cards/client-refs',
        responses: {
            200: PaymentCardClientRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            // A controller ad 502-t upstream-zavarra (`upstream-read.ts`: minden nem-401/403 → 502);
            // a szerződés eddig nem hirdette, tehát a kliens tipizáltan nem tudott róla.
            502: ErrorSchema,
        },
        summary: 'A create-form ügyfél-választójának refs-adata (foundation /v1/admin/clients — id + név). Jog-kapu: payment_cards.manage.',
    },
    paymentCard: {
        method: 'GET',
        path: '/payment-cards/:id',
        responses: {
            200: PaymentCardRowSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            /** ÁTMENETI upstream-zavar — lásd a lista-végpont 502-jét. */
            502: ErrorSchema,
        },
        summary: 'Egy bankkártya (detail — a szerkesztő elő-kitöltése + a read-only nézet forrása). Jog-kapu: payment_cards.view.',
    },
    paymentCardCreate: {
        method: 'POST',
        path: '/payment-cards',
        body: PaymentCardCreateBodySchema,
        responses: {
            201: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Bankkártya létrehozása (billing [T] — tenant-cél a body clientId-jából, validálva). Duplikáció-tiltás: ugyanazon ügyfélen belül (brand+last4+lejárat) ütközés → 409. Jog-kapu: payment_cards.manage.',
    },
    paymentCardUpdate: {
        method: 'PATCH',
        path: '/payment-cards/:id',
        headers: ClientIfMatchHeaders,
        body: PaymentCardWriteBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Bankkártya frissítése (teljes body, If-Match/OCC — hiányzó If-Match → 428, ütközés → 409). Duplikáció-tiltás: ugyanazon ügyfélen belül (brand+last4+lejárat, önmagát kivéve) ütközés → 409. Jog-kapu: payment_cards.manage.',
    },
    paymentCardDelete: {
        method: 'DELETE',
        path: '/payment-cards/:id',
        body: c.noBody(),
        headers: ClientIfMatchHeaders,
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Bankkártya törlése (soft-delete, OCC: `If-Match` KÖTELEZŐ — hiány → 428, ütközés → 409). HASZNÁLAT-GUARD: hozzárendelés által használt kártya NEM törölhető → 409 — bizonytalan olvasatnál is (fail-closed). Jog-kapu: payment_cards.delete.',
    },
    revenueForecast: {
        method: 'GET',
        path: '/revenue-forecast',
        query: RevenueForecastQuerySchema,
        responses: {
            200: RevenueForecastResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (a busz fojtott vagy hibázott) — a felület „Újra"-t ajánl, nem jog-hibát. */
            502: ErrorSchema,
        },
        summary: 'Bevétel-előrejelzés (READ-ONLY belső vezetői nézet). KÉT SZÁMOT ad minden hónapra: TERV (a `billing.service-plan-assignments` busz-erőforrásból — CSAK root; a `type` a halmazt NEM szűri, de a `tracking` sor MINDEN pénz-összegben 0 (`revenuePriceHUF`) — KIVÉVE az eseti díjat (`one_time`: kényszerítve tracking, de kézzel számlázott valódi bevétel); a darabszámokban és a részletező listákban viszont benne van) és TÉNY (`actual` — a kiszámlázott bizonylatokból: minden számla ÉS díjbekérő a semmis állapotúak kivételével; plusz az Atlason kívül számlázott eseti díjak a „Számlázás dátuma” hónapjában, ha arra a hónapra nincs rájuk Atlas-bizonylat-hányad). EREDMÉNYSZEMLÉLET: minden ismétlődő összeg a periódus-díj HAVI HÁNYADA (éves díj → tizenketted), a bizonylat esedékességétől függetlenül — a kártyák ÉS a chartok egyaránt; az eseti díj nem terül szét: TELJES áron a „Számlázás dátuma” hónapjában. Tartalom: MRR/PRR/NRR/ARR/AR kártyák · `mrrTrend` · `planTrend` + `actualTrend` (terv-tény összevetés; a tény NYITOTT hónapra `null`, nem 0; egy hónap akkor LEZÁRT, ha már véget ért, van rá adat, ÉS a rákövetkező hónapban kelt bizonylat — tehát a futó hónapra sosem mutatunk tényt) · szolgáltatás / top10-ügyfél / főkategória chartok. A motor fixpontos (Decimal-string), a deviza HUF. ÁRFOLYAM: a terv-oldalon MNB előző-hó-vég (fallback 1.0), a TÉNY-oldalon hónaponként a rögzített KÉZI árfolyam → annak híján az adott hónap MNB-ÁTLAGA → annak híján JELZETT 1:1 (`actual.rateSource`). Jog-kapu: finance.revenue.view (a PÉNZÜGY — bevétel/kiadás — lap nézet-joga; a kulcs ELŐDJE a `revenue_forecast.view` volt).',
    },
    revenueRows: {
        method: 'GET',
        path: '/revenue-rows',
        query: RevenueRowsQuerySchema,
        responses: {
            200: RevenueRowsResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (a busz fojtott vagy hibázott) — a felület „Újra"-t ajánl, nem jog-hibát. */
            502: ErrorSchema,
        },
        summary: 'A BEVÉTEL-LISTA egy hónapra (READ-ONLY, a Pénzügy lap „Bevételek" füle — a Kiadások lista párja). Egy sor = egy bevétel-tétel a hónapra, EREDMÉNYSZEMLÉLETBEN: a sor ahhoz a hónaphoz tartozik, amikor a szolgáltatás FUT, nem amikor a számla kiáll (a periódus-díj havi hányada; az utólag számlázott augusztusi szolgáltatás AUGUSZTUS sora; az eseti díj TELJES áron a „Számlázás dátuma” hónapjában). FORRÁS-SZABÁLY: a MÚLT hónapok sorai a kiállított bizonylatokból jönnek (`source: actual`; mellettük a horizont előtti periódusok áthozott hányada és az Atlason kívül számlázott eseti díjak), a jelen és a jövőbeli hónapoké a hozzárendelésekből (`source: plan`); egy még NEM LEZÁRT múlt hónap (nem futott le rá a kör) is TERV-sorokat kap (`closed: false`) — üres lista helyett. Szűrők: a kimutatáséval AZONOSAK (kereső, státusz, fő-/alkategória, díjtípus, szolgáltatás/csomag, ügyfél/fiók) + lapozás. Jog-kapu: finance.revenue.view.',
    },
    serviceAssignmentInvoiceGroups: {
        method: 'GET',
        path: '/service-assignments/invoice-groups',
        query: ServiceAssignmentInvoiceGroupsQuerySchema,
        responses: {
            200: ServiceAssignmentInvoiceGroupsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Az ügyfél / fiók adatlap „Számlánként" nézete: a hozzárendelések a leendő számlák szerint csoportosítva, a generátorral AZONOS csoport-szabállyal (címzett · profil · deviza · kibocsátó · bizonylattípus · kézbesítés · megállapodás · ÁFA), plusz ami nem kerül számlára, okkal. Pontosan egy kell: clientId vagy accountId (különben 422). Csak olvas. Jog-kapu: service_assignments.view.',
    },
    serviceAssignments: {
        method: 'GET',
        path: '/service-assignments',
        query: ServiceAssignmentListQuerySchema,
        responses: {
            200: z.object({
                assignments: z.array(ServiceAssignmentRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Szolgáltatás-hozzárendelések listája (billing [T]) a billing.service-plan-assignments busz-erőforrásból — CSAK root (parentId null); ügyfél/fiók + szolgáltatás/plan név feloldva, számított nettó alap-összeg (Decimal→string); szabad-szöveg + hatókör/ügyfél/fiók/típus/fizetési-megállapodás/árazási-modell/státusz/gyakoriság-szűrő + rendezés (alap: ügyfél-név → szolgáltatás-név) + offset-lapozás (BFF-oldal). Jog-kapu: service_assignments.view.',
    },
    serviceAssignmentAttention: {
        method: 'GET',
        path: '/service-assignments/attention',
        responses: {
            200: ServiceAssignmentAttentionSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'A hozzárendelés-lista FIGYELEM-panelje: a hamarosan (ASSIGNMENT_START_NOTICE_DAYS napon belül, default 5) induló FÜGGŐ (pending) hozzárendelések — ha addig nem kerülnek active-ra, a havi generátor kihagyja őket. Ugyanez a szabály hajtja a napi értesítő cront (service_assignment.pending_start_soon). A válasz hordozza az N-t (noticeDays). Jog-kapu: service_assignments.view.',
    },
    serviceAssignmentRefs: {
        method: 'GET',
        path: '/service-assignments/refs',
        /** `lang` — a személy-nevek sorrendje (hu: `Vezetéknév Keresztnév` · en: `Given Family`). A `supportTeamRefs` mintája. */
        query: z.object({ lang: z.enum(['hu', 'en']).optional() }),
        responses: {
            200: ServiceAssignmentRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'A hozzárendelés-form forrásai: katalógus szolgáltatás + plan + plan-ár (a snapshot/ár-forrás) + ügyfelek + fiókok + személyek (`type`-pal, a kapcsolattartó/szakértő szétválasztásához; a név a `?lang=` szerinti sorrendben). Jog-kapu: service_assignments.manage.',
    },
    serviceAssignmentActiveCheck: {
        method: 'GET',
        path: '/service-assignments/active-check',
        query: ServiceAssignmentActiveCheckQuerySchema,
        responses: {
            200: ServiceAssignmentActiveCheckSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Aktív-hozzárendelés ellenőrzés (nem-blokkoló figyelmeztetés): van-e már aktív, nem-lezárt hozzárendelés ugyanarra a szolgáltatásra ugyanannál az ügyfélnél/fióknál (a legacy checkForActiveAssignment 1:1; one_time → kihagyva). Jog-kapu: service_assignments.manage.',
    },
    serviceAssignment: {
        method: 'GET',
        path: '/service-assignments/:id',
        responses: {
            200: ServiceAssignmentDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Egy hozzárendelés (detail): a sor + a planSnapshot mezői + az ár-sorai + a „számlázott" jelző (szerkesztés-lock/törlés-tiltás forrása). Jog-kapu: service_assignments.view.',
    },
    serviceAssignmentCreate: {
        method: 'POST',
        path: '/service-assignments',
        body: ServiceAssignmentCreateBodySchema,
        responses: {
            201: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Hozzárendelés létrehozása (billing [T] — tenant-cél a body clientId-jából). A BFF a KATALÓGUS-planből építi a planSnapshot-ot (servicePlanId a katalógus-planre mutat), és — ha nincs ár-override — billing típusnál átmásolja a katalógus ár-sorait a service-plan-assignment-prices-ba (tracking sor csak kifejezett override-ra kap árat). Eseti (one_time) plannél a típus kényszerítve tracking, és a „Számlázás dátuma” kötelező (hiány → 422). Nem-atomi orchestráció → best-effort rollback + 502. Jog-kapu: service_assignments.manage.',
    },
    serviceAssignmentUpdate: {
        method: 'PUT',
        path: '/service-assignments/:id',
        headers: ClientIfMatchHeaders,
        body: ServiceAssignmentUpdateBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Hozzárendelés frissítése (partial, If-Match/OCC — hiányzó If-Match → 428, ütközés → 409). SZERVER-OLDALI szerkesztés-lock: számlázott assignmentnél a plan billingType-ja szűkíti a megengedett mezőket (one_time → csak note; recurring → status/quantity/quantityUnit/issuerKey/billingEndDate/contract*/note); nem-szerkeszthető mező → 422. Jog-kapu: service_assignments.manage.',
    },
    serviceAssignmentClose: {
        method: 'POST',
        path: '/service-assignments/:id/close',
        headers: ClientIfMatchHeaders,
        body: ServiceAssignmentCloseBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Hozzárendelés LEZÁRÁSA vagy FELOLDÁSA (If-Match/OCC). Dátummal: a billingEndDate + contractEndDate beállítása — CSAK type=billing + recurring plan, egyébként 422; a záró dátum nem lehet korábbi a kezdésnél. `billingEndDate: null`-lal: a két záró dátum TÖRLÉSE, ami minden root soron megengedett az ESETI díj kivételével (a nyilvántartás-típuson is — a generátor csak type=billing sorokat vesz munkába, tehát a feloldásnak nincs számlázási következménye). ESETI díj (one_time): sem lezárás, sem feloldás → 422 (a záró dátuma maga a számlázás napja). Jog-kapu: service_assignments.manage.',
    },
    serviceAssignmentDelete: {
        method: 'DELETE',
        path: '/service-assignments/:id',
        body: c.noBody(),
        headers: ClientIfMatchHeaders,
        responses: {
            204: c.noBody(),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            428: ErrorSchema,
            // A komponens-fa kaszkád-törlésének upstream-hibája (a gyerek-törlés busz-hibája — a szülő NEM törlődik).
            502: ErrorSchema,
        },
        summary: 'Hozzárendelés törlése (soft-delete, OCC: `If-Match` KÖTELEZŐ — hiány → 428, ütközés → 409). GUARD: KIÁLLÍTOTT bizonylaton szereplő hozzárendelés NEM törölhető → 409, bizonytalan olvasatnál is (a piszkozat és a semmis bizonylat nem blokkol; ismétlődő billing sor ilyenkor lezárható). Komplex sornál a komponens-gyerekek ELŐBB törlődnek (gyerek-hiba → 502, a szülő megmarad). Jog-kapu: service_assignments.delete.',
    },
    serviceCatalog: {
        method: 'GET',
        path: '/service-catalog',
        query: ServiceCatalogListQuerySchema,
        responses: {
            200: z.object({
                services: z.array(ServiceCatalogRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Szolgáltatás-katalógus (billing, belső) a billing.services SHARED busz-erőforrásból — i18n-név feloldva + katalógus-csomagok dúsítása (csomag-szám, distinct kategóriák, ár-tartomány domináns devizában, Decimal→string); keresés (szolgáltatás-név, csomag-nevek, árak) + szerkezet/fő-/alkategória/számlázási-típus/árazási-típus/státusz-szűrő + rendezés (name | planCount | priceRange) + offset-lapozás (BFF-oldal). Jog-kapu: service_catalog.view.',
    },
    serviceCatalogDetail: {
        method: 'GET',
        path: '/service-catalog/:id',
        responses: {
            200: ServiceCatalogDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Egy szolgáltatás TELJES részlete a háromszintű szerkesztőnek (service + plans[]+prices[], OCC-verziókkal) a billing SHARED busz-erőforrásokból. Jog-kapu: service_catalog.view. Csak nem-törölt sorok.',
    },
    serviceCatalogCreate: {
        method: 'POST',
        path: '/service-catalog',
        headers: ClientIdempotencyHeaders,
        body: ServiceCatalogWriteSchema,
        responses: {
            200: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Szolgáltatás létrehozása HÁROMSZINTŰEN (service→plans→prices; billing SHARED — write:all:billing). Jog-kapu: services.manage. Orchesztrált, best-effort rollback + 502. Struktúra/pricing/tier-validáció 422. Az Idempotency-Key fejlécet a contract elfogadja, de a billing/services · service-plans · service-plan-prices nem idempotenciás típus (`NO_IDEMPOTENCY_TYPES`) — a kulcs nem megy tovább az Odinnak.',
    },
    serviceCatalogUpdate: {
        method: 'PUT',
        path: '/service-catalog/:id',
        headers: ClientIfMatchHeaders,
        body: ServiceCatalogWriteSchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Szolgáltatás frissítése HÁROMSZINTŰEN, DIFF-alapon (service PUT If-Match; plan új→POST / létező→PUT If-Match / hiányzó→DELETE; ársorok teljes újraszinkronnal). ÉLŐ hozzárendelés által használt csomag kivétele → 409 (a fej-mentés ekkor már megtörtént, a csomag megmarad). Jog-kapu: services.manage. A package→simple konverzió TILOS (422).',
    },
    serviceDelete: {
        method: 'DELETE',
        path: '/service-catalog/:id',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Szolgáltatás soft-delete (billing SHARED). Jog-kapu: services.delete. HASZNÁLAT-GUARD: élő hozzárendelés által használt szolgáltatás NEM törölhető → 409 (bizonytalan/csonka olvasatnál is). A katalógus-csomagok/árak kaszkádja Odin-oldali (v1: a service-sor törlése).',
    },
    meetings: {
        method: 'GET',
        path: '/meetings',
        query: MeetingListQuerySchema,
        responses: {
            200: z.object({
                meetings: z.array(MeetingRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /**
                 * `true` → az upstream lapozás elérte a védő-plafont (10 000 sor) → a lista és a darabszám
                 * CSONKA. A felület jelzi: egy csendben elvágott lista rosszabb, mint egy látható figyelmeztetés.
                 */
                truncated: z.boolean(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Találkozó-lista (communication, belső) a communication.meetings busz-erőforrásból — szervező-név + résztvevő-szám feloldva; cím-keresés + dátum-tartomány + helyszín-szűrő (multi; az ismeretlen/üres helyszín „egyéb”) + offset-lapozás (BFF-oldal, startsAt szerint — alap DESC, `order=asc` megfordítja). Jog-kapu: meetings.view.',
    },
    meetingRefs: {
        method: 'GET',
        path: '/meetings/refs',
        responses: {
            200: z.object({
                /** Felvehető belső résztvevők (identity type=internal) — a sorrend/alak a KLIENSEN, nyelv szerint. */
                employees: z.array(PersonOptionSchema),
                /**
                 * Felvehető kapcsolattartók (identity type=partner) — a sorrend/alak a KLIENSEN, nyelv szerint. Az
                 * `accountNames` a kapcsolattartó (élő) fiókjainak neve ABC-rendben — a választó „Név (Hotel +N)" alakban
                 * mutatja, és mindegyikre lehet keresni. Ha a kötés vagy a fiók nem olvasható, üres.
                 */
                contacts: z.array(PersonOptionSchema.extend({ accountNames: z.array(z.string()) })),
                /**
                 * A munkatársak részleg → csapat CSOPORT-fája (egy kattintásos csoport-kijelöléshez). Ha a szervezeti
                 * felépítés nem olvasható a hívó jogával, ÜRES — a választó ilyenkor csoportok nélkül, simán működik.
                 */
                employeeGroups: z.array(MeetingEmployeeGroupSchema),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Találkozó-résztvevő picker opció-listák: belső munkatársak (identity type=internal) + kapcsolattartók (type=partner), névre rendezve, + a munkatársak részleg → csapat csoport-fája. Jog-kapu: meetings.view (csak a szerkesztő-form kéri).',
    },
    meetingParticipantConflicts: {
        method: 'GET',
        path: '/meetings/participant-conflicts',
        query: MeetingParticipantConflictQuerySchema,
        responses: {
            200: z.object({ conflicts: z.array(MeetingParticipantConflictSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Résztvevő-ütközés MENTÉS ELŐTT: a megadott belső munkatársak közül ki szerepel már egy MÁSIK, átfedő idősávú megbeszélésen (a lemondott — `declined` — részvétel nem ütközik). Figyelmeztetés, nem tiltás: a mentést a szerver nem blokkolja. Jog-kapu: meetings.manage ÉS meetings.view (a válasz a lista adatát adja).',
    },
    meetingRsvp: {
        method: 'PUT',
        path: '/meetings/:id/rsvp',
        body: MeetingRsvpBodySchema,
        responses: {
            200: z.object({
                id: z.string(),
                status: z.string(),
                respondedAt: z.string().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Saját RSVP-válasz (confirmed | declined) egy találkozóra. A cél a token sub-jához tartozó saját résztvevő-sor (self-scope, IDOR-mentes) — nem-résztvevő → 404. Jog-kapu: meetings.view.',
    },
    meetingCreate: {
        method: 'POST',
        path: '/meetings',
        body: MeetingWriteBodySchema,
        responses: {
            201: z.object({ id: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Találkozó létrehozása (communication INTERNAL — a busz-írás write:all:communication scope-ot kíván; az Idempotency-Key-t az internal busz elutasítja, a dupla-submitet a UI busy-állapota védi). Jog-kapu: meetings.manage. A résztvevőket (employeeIds/contactIds) a BFF a meeting-participants pivotra szinkronizálja (pending státusszal). Üzleti invariánsok (end>start, nem-múltbeli, online-link-exkluzivitás) + kereszt-ütközés (helyszín-átfedés / étkező tiltott sávja / Zoom-fiók) a BFF-en (422). Siker után (best-effort): meghívó a résztvevőknek (`meeting.invited`) + a létrehozónak visszaigazoló e-mail (`meeting.created`, sorozatnál egy). A résztvevő-szinkron részleges bukása → 502 (töltsd újra). ISMÉTLŐDÉS (`recurrence`): a BFF kibontja az alkalmakat, mindegyikre ellenőriz (helyszín / Zoom → 422), és a sorozatot + alkalmakat + résztvevőket EGY atomi Odin-kötegben írja (nincs részleges állapot); a válasz `id`-ja az első alkalomé.',
    },
    meetingUpdate: {
        method: 'PATCH',
        path: '/meetings/:id',
        headers: ClientIfMatchHeaders,
        query: MeetingSeriesScopeQuerySchema,
        body: MeetingWriteBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number(), affected: z.number().optional() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Találkozó frissítése (If-Match/OCC). Jog-kapu: meetings.manage — MÁS megbeszélésénél ezen felül meetings.modify_any (különben 403; a BFF a sor createdBy-ja ellen ellenőrzi). A résztvevőket (employeeIds/contactIds) a BFF a meeting-participants pivotra diffeli (create pending / soft-delete; a meglévő RSVP-státusz megőrződik). A résztvevő-szinkron részleges bukása → 502 (töltsd újra). Az invariánsok és a kereszt-ütközés (helyszín / étkező / Zoom) a BFF-en (422; a szerkesztett megbeszélés önmagával nem ütközik). Új résztvevő meghívót kap; időpont-változáskor a megmaradók átütemezés-értesítést. Sorozat-alkalomnál a `scope` (this | following | all): a több-alkalmas szerkesztés a mezőket, a napon belüli időpontot és a résztvevőket viszi át az érintett alkalmakra (a dátum-minta nem módosítható — 422), EGY atomi Odin-kötegben; `affected` = a módosított alkalmak száma.',
    },
    meetingDelete: {
        method: 'DELETE',
        path: '/meetings/:id',
        query: MeetingSeriesScopeQuerySchema,
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean(), affected: z.number().optional() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Találkozó soft-delete (communication INTERNAL). Jog-kapu: meetings.delete — MÁS megbeszélésénél ezen felül meetings.modify_any (különben 403; jog nélkül a tulajdon-olvasás fail-closed). Sorozat-alkalomnál a `scope` (this | following | all) dönti el, mely alkalmak törlődnek — a több-alkalmas törlés EGY atomi Odin-kötegben; a múltbeli alkalom változatlan marad. `affected` = a törölt alkalmak száma. Az izolációt az Odin scope adja; a résztvevők lemondás-értesítést kapnak (`meeting.cancelled`).',
    },
    messages: {
        method: 'GET',
        path: '/messages',
        query: MessageListQuerySchema,
        responses: {
            200: z.object({
                messages: z.array(MessageRowSchema),
                total: z.number(),
                nextOffset: z.number().nullable(),
                /**
                 * A KÉT IRÁNY darabszáma a jelenlegi kereső/típus-szűrő mellett (a fül-badge-ek forrása) — így a badge
                 * azt mutatja, mennyit látna a felhasználó fülváltás után.
                 */
                directions: z.object({ incoming: z.number(), outgoing: z.number() }),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Üzenet-lista (communication üzenőfal, belső) a communication.messages busz-erőforrásból — küldő-név feloldva + olvasás-szám + readByMe; irány (incoming = más küldte ÉS sent · outgoing = saját, bármely státusz; a tokenből) + kategória- + típus-szűrő + tárgy/tartalom-keresés + rendezés (sentAt|createdAt, alap DESC) + offset-lapozás (BFF-oldal). A saját archivált üzenetek alapból KIMARADNAK (`archived=only` → csak azok); `pinned=only` → csak a kitűzöttek, a kitűzés ideje szerint (a kezdőlap-kártya forrása). Jog-kapu: messages.view.',
    },
    messageRefs: {
        method: 'GET',
        path: '/messages/refs',
        responses: {
            200: MessageRefsSchema,
            401: ErrorSchema,
            403: ErrorSchema,
        },
        summary: 'Üzenet-címzett-picker adatok: kollégák (belső identity-k) + részlegek + csapatok (a részleg nevével) + ügyfélfiókok + kapcsolattartók + szolgáltatások — [{id, name}], a személyeknél név-részekkel. Jog-kapu: messages.manage (csak a compose-form kéri).',
    },
    messageCreate: {
        method: 'POST',
        path: '/messages',
        body: MessageComposeBodySchema,
        responses: {
            201: z.object({ id: z.string(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
            502: z.object({ message: z.string(), id: z.string().nullable() }),
        },
        summary: 'Üzenet-írás (küldés MOST vagy időzítés) — 3 lépcsős busz-orchestráció: draft-POST → címzett-pivot-POST-ok → záró státusz-PATCH (a félkész küldés sosem látszik sent-ként). Részleges hiba → 502 (pivot-hiba: best-effort rollback; záró-PATCH-hiba: a sor draft-ként marad, az id-vel folytatható). Jog-kapu: messages.manage. Az Idempotency-Key-t az internal busz elutasítja — a dupla-submitet a UI busy-state + pivot-unique + OCC védi. A sikeres küldés UTÁN in-app értesítés + e-mail megy a kibontott címzett-listára (fire-and-forget, a választ nem késlelteti; a kézbesítés a GÉPI service-identitással fut).',
    },
    messageSend: {
        method: 'POST',
        path: '/messages/:id/send',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string(), version: z.number(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: '„Küldés most": draft/scheduled → sent (OCC-claim, If-Match; 409 = közben elment/módosult; 422 = már sent). Jog-kapu: messages.manage + owner (csak a saját üzenet).',
    },
    messageSchedule: {
        method: 'POST',
        path: '/messages/:id/schedule',
        headers: ClientIfMatchHeaders,
        body: MessageScheduleBodySchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Időzítés / átidőzítés: draft/scheduled → scheduled (scheduledAt jövőbeli — múltbeli 422; OCC If-Match, 409 = közben elment). Jog-kapu: messages.manage + owner.',
    },
    messageUnschedule: {
        method: 'POST',
        path: '/messages/:id/unschedule',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string(), version: z.number(), status: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Időzítés visszavonása: scheduled → draft (scheduledAt → null; OCC If-Match — 409 = a dispatcher megelőzte, „már elküldve"). Jog-kapu: messages.manage + owner.',
    },
    messageUpdate: {
        method: 'PATCH',
        path: '/messages/:id',
        headers: ClientIfMatchHeaders,
        body: MessageContentSchema,
        responses: {
            200: z.object({ id: z.string(), version: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Üzenet-tartalom szerkesztése (5 mező; címzett NEM módosítható). Sent-nél ELŐBB revízió-snapshot (message-revisions, az ELŐZŐ állapot) — ha a snapshot hibázik, nincs szerkesztés (502); draft/scheduled revízió nélkül. If-Match/OCC (428/409). Jog-kapu: messages.manage + owner. A címzettek NEM kapnak értesítést.',
    },
    messageDetail: {
        method: 'GET',
        path: '/messages/:id',
        responses: {
            200: MessageDetailSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
            404: ErrorSchema,
        },
        summary: 'Üzenet-részletnézet (a lista nem hordoz body-t): SZANITÁLT HTML-törzs + meta + readByMe. Jog-kapu: messages.view.',
    },
    messageMarkRead: {
        method: 'POST',
        path: '/messages/:id/read',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Olvasottnak jelölés (upsert message-reads az AKTUÁLIS identity-re, readAt=now) — idempotens (létező sor → ok). Jog-kapu: messages.view.',
    },
    messagePin: {
        method: 'POST',
        path: '/messages/:id/pin',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string(), version: z.number(), pinnedAt: z.string().nullable() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Kitűzés: a (kiküldött, belső) üzenet a belső kezdőlapon jelenik meg (a legfrissebb kitűzött). Csak `sent` + `internal` üzenet tűzhető ki (különben 422). OCC If-Match. Jog-kapu: messages.manage + owner (mint a szerkesztés).',
    },
    messageUnpin: {
        method: 'POST',
        path: '/messages/:id/unpin',
        headers: ClientIfMatchHeaders,
        body: c.noBody(),
        responses: {
            200: z.object({ id: z.string(), version: z.number(), pinnedAt: z.string().nullable() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ValidationErrorSchema,
            428: ErrorSchema,
        },
        summary: 'Kitűzés levétele. OCC If-Match. Jog-kapu: messages.manage + owner.',
    },
    messageArchive: {
        method: 'POST',
        path: '/messages/:id/archive',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'OLVASÓ-OLDALI archiválás: az üzenet a hívó SAJÁT listájából eltűnik (a többi olvasónál megmarad — nem törlés); egyben olvasottá is teszi. A read-sor MINDIG a hívó identity-jére íródik (a token `sub`-ja — mást nem címezhet). Csak kiküldött (sent) üzenet archiválható (különben 404). Idempotens. Jog-kapu: messages.view.',
    },
    messageUnarchive: {
        method: 'POST',
        path: '/messages/:id/unarchive',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Archiválás visszavonása (a saját listába visszakerül; az olvasottság marad). Idempotens. Jog-kapu: messages.view.',
    },
    messageMarkAllRead: {
        method: 'POST',
        path: '/messages/read-all',
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean(), marked: z.number() }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: '„Mind olvasottnak jelöl" — read-sor a bejövő halmaz (sent üzenetek) olvasatlanjaira, CSAK a messages-scope-ra (a legacy minden-notification viselkedés NEM portolódik). Jog-kapu: messages.view.',
    },
    activityLog: {
        method: 'GET',
        path: '/activity-log',
        query: ActivityLogQuerySchema,
        responses: {
            200: z.object({
                events: z.array(ActivityEventSchema),
                nextCursor: z.string().nullable(),
            }),
            401: ErrorSchema,
            403: ErrorSchema,
            // A platform (Odin audit-végpont) hibája — SZÁNDÉKOSAN külön a 403-tól: korábban minden nem-200
            // upstream státusz 403-ra képződött, ezért egy Odin-kimaradás a felületen „Nincs jogosultság"-ként
            // jelent meg (újratöltés-gomb NÉLKÜL), és a felhasználó a saját jogára gyanakodott a platform helyett.
            502: ErrorSchema,
        },
        summary: 'Eseménynapló (platform.audit_log) — cursor-lapozott lista metaadatokkal (before/after NÉLKÜL); kereső + szereplő-, dátum-, kategória- és app-szűrő. Jog-kapu: activity_log.view.',
    },
    /**
     * LISTA-EXPORT AUDIT: a felület bejelenti a lefuttatott exportot (ki/mit/milyen szűrővel/hány sor) → az
     * Odin `POST /v1/audit/events`-én `export.list` audit-sor, `security` kategóriában. Fire-and-forget
     * jellegű: a válasz csak nyugta, a letöltés ettől független (már megtörtént a böngészőben).
     */
    exportAudit: {
        method: 'POST',
        path: '/export-audit',
        body: ExportAuditBodySchema,
        responses: {
            201: z.object({ recorded: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Lista-export audit-nyom (export.list) — az Odin audit-naplójába',
    },
    dashboard: {
        method: 'GET',
        path: '/dashboard',
        responses: {
            200: DashboardSchema,
            401: ErrorSchema,
        },
        summary: 'Áttekintő összefoglaló: az ügyfelek és a fiókok száma — belépett usernek; minden mező jog-szűrt (jog nélkül null).',
    },
    dashboardCelebrations: {
        method: 'GET',
        path: '/dashboard/celebrations',
        query: CelebrationsQuerySchema,
        responses: {
            200: CelebrationsResponseSchema,
            401: ErrorSchema,
            403: ErrorSchema,
            422: ValidationErrorSchema,
        },
        summary: 'Az „Ünnepeltek" csempe adata: a mai nap + a következő 7/14/30 nap születésnapjai (ÉV/ÉLETKOR NÉLKÜL), névnapjai, belépési évfordulói, az új belépők; a gyerekek születésnapja CSAK a hívó saját sorában. A kimaradást kérők kiesnek. Jog: celebrations.view. Gépi tokennel olvas; ha az nem elérhető, üres (fail-closed).',
    },
    adminApps: {
        method: 'GET',
        path: '/admin/apps',
        responses: {
            200: z.object({ apps: z.array(AdminAppSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Regisztrált Odin-appok listája a KÖZPONTI (multi-app) admin-konzol app-választójához (az Odin `GET /v1/admin/apps` proxyja). BFF-kapu: access_rights.view; ezen túl a jogot az Odin ítéli meg, és CSAK azt adja vissza, amit a hívó tényleg kezelhet: `platform:admin` → mind · `rbac:admin` → minden nem-platform app · `{ns}:admin` → azok, amelyek valamely namespace-ére admin-joga van. Egyik sem → 403, a UI a saját appjára esik vissza.',
    },
    adminRoles: {
        method: 'GET',
        path: '/admin/roles',
        query: AdminScopeQuerySchema,
        responses: {
            200: z.object({ roles: z.array(AdminRoleSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy app szerepei + jogaik (RBAC admin — az Odin admin API proxyja). `?app=` üres → a saját app (atlas). `?audience=internal|partner` → a felület (belső rendszer / partner) szerepeire szűr.',
    },
    adminPermissions: {
        method: 'GET',
        path: '/admin/permissions',
        query: AdminScopeQuerySchema,
        responses: {
            200: z.object({ permissions: z.array(AdminPermissionSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy app JOG-KATALÓGUSA GAZDAG metaadattal (kulcs + kétnyelvű címke + modul-csoport + sorrend) a KÖZPONTI konzol app-agnosztikus, lokalizált címkézéséhez — minden app a SAJÁT jog-metaadatát adja (az Odinból). `?app=` üres → a saját app (atlas). `?audience=internal|partner` → a felület jogaira szűr.',
    },
    adminMembers: {
        method: 'GET',
        path: '/admin/members',
        query: AdminAppQuerySchema,
        responses: {
            200: z.object({ members: z.array(AdminMemberSchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Egy app tagjai + szerepeik. `?app=` üres → a saját app (atlas). A munkaviszony-adat nélküli belső (gépi, pl. break-glass) fiókok KIMARADNAK (a munkaviszony-olvasás hibájánál fail-open). Jog: access_rights.view.',
    },
    assignRole: {
        method: 'POST',
        path: '/admin/members/:identityId/roles',
        query: AdminAppQuerySchema,
        body: z.object({ roleKey: z.string() }),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) - a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
        },
        summary: 'Szerep rendelése egy taghoz (a kiválasztott appban).',
    },
    updateRolePermissions: {
        method: 'POST',
        path: '/admin/roles/:roleKey/permissions',
        query: AdminAppQuerySchema,
        body: z.object({ permissions: z.array(z.string()) }),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) - a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
        },
        summary: 'Egy szerep KEZELT jogainak beállítása a kiválasztott appban: consumer-appnál csak a FEATURE-jogokat (pontos kulcs) cseréli, a scope-jait (kettőspontos — read:all/write/atlas:admin) megőrzi; platform-appnál (odin) a jog-katalógus kulcsai a kezeltek, a katalóguson kívülieket őrzi meg. Jog: access_rights.manage.',
    },
    syncMemberPermissions: {
        method: 'POST',
        path: '/admin/members/:identityId/permissions/sync',
        query: AdminAppQuerySchema,
        body: z.object({
            grantKeys: z.array(z.string()),
            denyKeys: z.array(z.string()),
        }),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ErrorSchema,
            // A handler `upstreamFail()`-t ad a nem várt upstream-státuszra (401/403 → 403, minden más → 502) —
            // a 502-t tehát a SZERZŐDÉSNEK is hirdetnie kell. Enélkül a kliens TÍPUS SZERINT nem ismeri az ágat,
            // és a felület egy valódi üzem-zavart nem tud megkülönböztetni: ez volt a hét másik admin-mutáción
            // már javított hiba, ez az egy maradt ki (a ts-rest típusa a hiányt átengedi, ezért csendben maradt).
            502: ErrorSchema,
        },
        summary: 'Egy tag PER-USER FELÜLÍRÁSAINAK szinkronizálása a kiválasztott appban: grant (tier fölötti többlet) + deny (szerep-jog elvétele).',
    },
    revokeRole: {
        method: 'POST',
        path: '/admin/members/:identityId/roles/:roleKey/revoke',
        query: AdminAppQuerySchema,
        body: z.object({}).optional(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) - a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Szerep visszavonása egy tagtól a kiválasztott appban (POST — a react-query mutation-hoz).',
    },
    // ── KÖZPONTI konzol: tag- és szerep-ÉLETCIKLUS (IdP-konzol-minta) ──
    adminIdentities: {
        method: 'GET',
        path: '/admin/identities',
        query: AdminIdentitiesQuerySchema,
        responses: {
            200: z.object({ identities: z.array(AdminIdentitySchema) }),
            401: ErrorSchema,
            403: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'GLOBÁLIS identity-katalógus a „➕ Tag hozzáadása apphoz" választóhoz (az Odin GET /v1/admin/identities?type= proxyja; app-tagságtól független). `?audience=partner` → külsős (partner) személyek, egyébként belső (internal). Jog: access_rights.view.',
    },
    addMember: {
        method: 'POST',
        path: '/admin/members',
        query: AdminAppQuerySchema,
        body: AddMemberBodySchema,
        responses: {
            200: z.object({ ok: z.boolean(), identityId: z.string() }),
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) - a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
            404: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Tag hozzáadása a kiválasztott apphoz: KIZÁRÓLAG MEGLÉVŐ globális személy (identityId) + opcionális kezdő szerep. Új személyt ez a végpont NEM hoz létre (a felvétel a Munkatársak / Kapcsolattartók vertikum dolga — ott dől el, belsős vagy külsős). Jog: access_rights.manage.',
    },
    removeMember: {
        method: 'POST',
        path: '/admin/members/:identityId/remove',
        query: AdminAppQuerySchema,
        body: z.object({}).optional(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) - a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
            422: ErrorSchema,
        },
        summary: 'Tag eltávolítása a kiválasztott APPBÓL (app-offboarding — a globális személyt NEM törli; az Odin DELETE .../members/{id} proxyja). Jog: access_rights.manage.',
    },
    /**
     * SZEREP TÖRLÉSE a katalógusból (soft). CSAK konzolos (`source: console`), NEM használt szerepre: a kódból
     * kezelt szerepet a boot-sync visszahozná (422), a használatban lévő törlése pedig a hozzáféréseket hagyná
     * szerep nélkül (409). Jog: access_rights.manage.
     */
    deleteRole: {
        method: 'DELETE',
        path: '/admin/roles/:roleKey',
        query: AdminAppQuerySchema,
        body: c.noBody(),
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            404: ErrorSchema,
            409: ErrorSchema,
            422: ErrorSchema,
            502: ErrorSchema,
        },
        summary: 'Szerep törlése a katalógusból (soft) — csak KONZOLOS és nem használt szerepre (kódból kezelt → 422, használatban → 409). Jog: access_rights.manage.',
    },
    createRole: {
        method: 'POST',
        path: '/admin/roles',
        query: AdminAppQuerySchema,
        body: CreateRoleBodySchema,
        responses: {
            200: z.object({ ok: z.boolean() }),
            401: ErrorSchema,
            403: ErrorSchema,
            422: ErrorSchema,
            /** ÁTMENETI upstream-hiba (fojtás, kiesés) — a felület újrapróbálást ajánl, nem jog-hibát. */
            502: ErrorSchema,
        },
        summary: 'ÚJ (üres jogú) szerep létrehozása a kiválasztott appban; a jogokat utána a szerep-szerkesztő állítja. Jog: access_rights.manage.',
    },
});
//# sourceMappingURL=index.js.map